// Imports
import ___CSS_LOADER_API_SOURCEMAP_IMPORT___ from "../node_modules/css-loader/dist/runtime/sourceMaps.js";
import ___CSS_LOADER_API_IMPORT___ from "../node_modules/css-loader/dist/runtime/api.js";
var ___CSS_LOADER_EXPORT___ = ___CSS_LOADER_API_IMPORT___(___CSS_LOADER_API_SOURCEMAP_IMPORT___);
// Module
___CSS_LOADER_EXPORT___.push([module.id, `/* ==========================================================================
   LANDING PAGE DESIGN SYSTEM
   Scoped to .landing-page so the internal CRM styles stay untouched.
   ========================================================================== */

/* Tokens live on :root so fixed elements rendered outside .landing-page
   (navigation, modals) can use them as well. */
:root {
    /* Palette */
    --lp-ink: #0b1224;
    --lp-ink-2: #121b36;
    --lp-ink-3: #1b2648;
    --lp-text: #1e293b;
    --lp-text-soft: #475569;
    --lp-muted: #64748b;
    --lp-line: rgba(15, 23, 42, 0.08);
    --lp-brand: #3b82f6;
    --lp-brand-2: #6366f1;
    --lp-brand-soft: #eaf2ff;
    --lp-accent: #22d3ee;
    --lp-success: #10b981;
    --lp-warning: #f59e0b;
    --lp-danger: #ef4444;
    --lp-grad: linear-gradient(135deg, #5ca9fb 0%, #6372ff 100%);
    --lp-grad-text: linear-gradient(90deg, #7cc0ff 0%, #8b93ff 50%, #67e8f9 100%);
    --lp-bg: #f6f8fc;
    --lp-surface: #ffffff;

    /* Shape & depth */
    --lp-radius: 20px;
    --lp-radius-sm: 12px;
    --lp-shadow: 0 10px 30px -12px rgba(15, 23, 42, 0.18);
    --lp-shadow-lg: 0 30px 60px -24px rgba(15, 23, 42, 0.32);
    --lp-ring: 0 0 0 4px rgba(99, 114, 255, 0.18);

    /* Type */
    --lp-font: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    --lp-font-display: "Manrope", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}

.landing-page {
    font-family: var(--lp-font);
    color: var(--lp-text);
    font-size: 16px;
    line-height: 1.65;
    background: var(--lp-surface);
    overflow-x: clip;
}

.landing-page *,
.landing-page *::before,
.landing-page *::after {
    box-sizing: border-box;
}

.landing-page h1,
.landing-page h2,
.landing-page h3,
.landing-page h4 {
    font-family: var(--lp-font-display);
    color: var(--lp-ink);
    text-transform: none;
    letter-spacing: -0.02em;
    margin: 0;
}

.landing-page p {
    font-size: inherit;
    margin: 0;
}

.landing-page label {
    float: none;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
}

/* Font Awesome icons inherit the badge styling; never carry template sizing */
.landing-page i[class*="fa"] {
    width: auto;
    height: auto;
    padding: 0;
    margin: 0;
    background: none;
    border-radius: 0;
    box-shadow: none;
    font-size: inherit;
    line-height: 1;
}

/* Headings keep their own spacing even against the generic reset above */
.landing-page .lp-title { margin-bottom: 16px; }
.landing-page .lp-about__sub { margin: 32px 0 16px; }
.landing-page .lp-card__title { margin: 0; }

.landing-page a {
    color: var(--lp-brand);
    text-decoration: none;
    transition: color 0.2s ease;
}

.landing-page a:hover {
    color: var(--lp-brand-2);
}

.landing-page img {
    max-width: 100%;
}

.landing-page ::selection {
    background: rgba(99, 114, 255, 0.25);
}

/* --------------------------------------------------------------------------
   Layout primitives
   -------------------------------------------------------------------------- */

.lp-container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 24px;
}

.lp-container--narrow {
    max-width: 920px;
}

.lp-section {
    position: relative;
    padding: clamp(72px, 9vw, 120px) 0;
}

.lp-section--soft {
    background: var(--lp-bg);
}

.lp-section--dark {
    background:
        radial-gradient(1200px 600px at 110% -10%, rgba(99, 114, 255, 0.35), transparent 60%),
        radial-gradient(900px 500px at -10% 110%, rgba(34, 211, 238, 0.22), transparent 60%),
        linear-gradient(160deg, var(--lp-ink) 0%, var(--lp-ink-2) 55%, var(--lp-ink-3) 100%);
    color: rgba(255, 255, 255, 0.82);
}

.lp-section--dark h2,
.lp-section--dark h3,
.lp-section--dark h4 {
    color: #fff;
}

.lp-section--dark a {
    color: #9cc8ff;
}

.lp-section--dark a:hover {
    color: #fff;
}

/* Subtle dotted texture used in light sections */
.lp-section--dots::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(15, 23, 42, 0.08) 1px, transparent 1px);
    background-size: 22px 22px;
    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), transparent 55%);
    -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), transparent 55%);
    pointer-events: none;
}

.lp-section > .lp-container {
    position: relative;
    z-index: 1;
}

.lp-grid {
    display: grid;
    gap: 24px;
}

.lp-grid--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.lp-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.lp-grid--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }

@media (max-width: 1024px) {
    .lp-grid--4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .lp-grid--3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 640px) {
    .lp-grid--2,
    .lp-grid--3,
    .lp-grid--4 { grid-template-columns: minmax(0, 1fr); }
}

/* --------------------------------------------------------------------------
   Section heading
   -------------------------------------------------------------------------- */

.lp-heading {
    max-width: 780px;
    margin-bottom: clamp(40px, 5vw, 64px);
}

.lp-heading--center {
    margin-left: auto;
    margin-right: auto;
    text-align: center;
}

.lp-eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 999px;
    background: var(--lp-brand-soft);
    color: var(--lp-brand-2);
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    margin-bottom: 18px;
}

.lp-eyebrow::before {
    content: "";
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: var(--lp-grad);
}

.lp-section--dark .lp-eyebrow {
    background: rgba(255, 255, 255, 0.1);
    color: #bcd9ff;
    border: 1px solid rgba(255, 255, 255, 0.14);
}

.lp-title {
    font-size: clamp(30px, 4.2vw, 48px);
    font-weight: 800;
    line-height: 1.1;
    margin-bottom: 16px;
}

.lp-title em {
    font-style: normal;
    background: var(--lp-grad);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
}

.lp-lead {
    font-size: clamp(16px, 1.4vw, 19px);
    line-height: 1.7;
    color: var(--lp-text-soft);
}

.lp-section--dark .lp-lead {
    color: rgba(255, 255, 255, 0.78);
}

/* --------------------------------------------------------------------------
   Cards, badges, chips
   -------------------------------------------------------------------------- */

.lp-card {
    position: relative;
    background: var(--lp-surface);
    border: 1px solid var(--lp-line);
    border-radius: var(--lp-radius);
    padding: 28px;
    box-shadow: var(--lp-shadow);
    transition: transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.35s ease, border-color 0.35s ease;
    display: flex;
    flex-direction: column;
    gap: 12px;
    overflow: hidden;
}

.lp-card:hover {
    transform: translateY(-6px);
    box-shadow: var(--lp-shadow-lg);
    border-color: rgba(99, 114, 255, 0.35);
}

.lp-card--static:hover {
    transform: none;
    box-shadow: var(--lp-shadow);
    border-color: var(--lp-line);
}

/* Gradient top bar revealed on hover */
.lp-card--bar::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: var(--lp-grad);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.lp-card--bar:hover::before {
    transform: scaleX(1);
}

.lp-card--glass {
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    box-shadow: none;
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
}

.lp-card--glass:hover {
    background: rgba(255, 255, 255, 0.09);
    border-color: rgba(156, 200, 255, 0.45);
    box-shadow: 0 30px 60px -30px rgba(92, 169, 251, 0.45);
}

.lp-card__title {
    font-size: 20px;
    font-weight: 800;
    line-height: 1.3;
}

.lp-card__text {
    color: var(--lp-text-soft);
    font-size: 15.5px;
    line-height: 1.7;
}

.lp-section--dark .lp-card__text {
    color: rgba(255, 255, 255, 0.72);
}

.lp-icon-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 56px;
    height: 56px;
    border-radius: 16px;
    background: var(--lp-grad);
    color: #fff;
    font-size: 22px;
    box-shadow: 0 12px 24px -10px rgba(99, 114, 255, 0.7);
    flex-shrink: 0;
    transition: transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.lp-card:hover .lp-icon-badge {
    transform: rotate(-6deg) scale(1.06);
}

.lp-icon-badge--soft {
    background: var(--lp-brand-soft);
    color: var(--lp-brand-2);
    box-shadow: none;
}

.lp-chip {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 14px;
    border-radius: 999px;
    background: var(--lp-brand-soft);
    border: 1px solid rgba(99, 114, 255, 0.18);
    color: var(--lp-brand-2);
    font-weight: 700;
    font-size: 14px;
    transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
}

a.lp-chip:hover {
    background: var(--lp-brand-2);
    color: #fff;
    transform: translateY(-2px);
}

.lp-tag {
    display: inline-block;
    padding: 4px 10px;
    border-radius: 8px;
    background: var(--lp-brand-soft);
    color: var(--lp-brand-2);
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

/* --------------------------------------------------------------------------
   Buttons
   -------------------------------------------------------------------------- */

.lp-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 15px 28px;
    border-radius: 999px;
    font-family: var(--lp-font);
    font-size: 16px;
    font-weight: 800;
    line-height: 1.2;
    text-align: center;
    text-decoration: none;
    border: 1px solid transparent;
    cursor: pointer;
    transition: transform 0.25s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease;
    overflow: hidden;
    max-width: 100%;
}

.lp-btn:focus-visible {
    outline: none;
    box-shadow: var(--lp-ring);
}

.lp-btn svg {
    transition: transform 0.25s ease;
}

.lp-btn:hover svg {
    transform: translateX(3px);
}

.lp-btn--primary {
    background: var(--lp-grad);
    color: #fff;
    box-shadow: 0 14px 30px -10px rgba(99, 114, 255, 0.75);
}

.lp-btn--primary::after {
    content: "";
    position: absolute;
    top: 0;
    left: -80%;
    width: 50%;
    height: 100%;
    background: linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.35) 50%, transparent 100%);
    transform: skewX(-20deg);
    transition: left 0.6s ease;
}

.lp-btn--primary:hover {
    color: #fff;
    transform: translateY(-2px);
    box-shadow: 0 20px 40px -12px rgba(99, 114, 255, 0.85);
}

.lp-btn--primary:hover::after {
    left: 130%;
}

.lp-btn--ghost {
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.25);
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
}

.lp-btn--ghost:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.5);
    color: #fff;
    transform: translateY(-2px);
}

.lp-btn--outline {
    background: #fff;
    color: var(--lp-brand-2);
    border-color: rgba(99, 114, 255, 0.4);
}

.lp-btn--outline:hover {
    background: var(--lp-brand-soft);
    color: var(--lp-brand-2);
    transform: translateY(-2px);
}

.lp-btn--lg {
    padding: 19px 36px;
    font-size: 18px;
}

.lp-btn--block {
    width: 100%;
}

.lp-btn[disabled] {
    opacity: 0.6;
    cursor: wait;
}

/* --------------------------------------------------------------------------
   Scroll reveal
   -------------------------------------------------------------------------- */

.landing-page [data-reveal] {
    opacity: 0;
    transform: translateY(26px);
    transition: opacity 0.8s cubic-bezier(0.2, 0.7, 0.2, 1), transform 0.8s cubic-bezier(0.2, 0.7, 0.2, 1);
    transition-delay: var(--reveal-delay, 0ms);
    will-change: opacity, transform;
}

.landing-page [data-reveal="left"] { transform: translateX(-32px); }
.landing-page [data-reveal="right"] { transform: translateX(32px); }
.landing-page [data-reveal="scale"] { transform: scale(0.94); }

.landing-page [data-reveal].is-visible {
    opacity: 1;
    transform: none;
}

/* --------------------------------------------------------------------------
   Navigation
   -------------------------------------------------------------------------- */

.lp-nav {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    z-index: 2000;
    height: 76px;
    display: flex;
    align-items: center;
    background: transparent;
    border-bottom: 1px solid transparent;
    transition: background 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease, height 0.35s ease;
}

.lp-nav--solid {
    height: 68px;
    background: rgba(255, 255, 255, 0.86);
    backdrop-filter: blur(16px) saturate(160%);
    -webkit-backdrop-filter: blur(16px) saturate(160%);
    border-bottom-color: rgba(15, 23, 42, 0.06);
    box-shadow: 0 10px 30px -20px rgba(15, 23, 42, 0.35);
}

.lp-nav__inner {
    width: 100%;
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    height: 100%;
}

.lp-nav__brand {
    display: flex;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    flex-shrink: 0;
    color: #fff;
    transition: color 0.35s ease;
}

.lp-nav--solid .lp-nav__brand {
    color: var(--lp-ink);
}

.lp-nav__logo {
    height: 38px;
    width: 38px;
    border-radius: 11px;
    box-shadow: 0 6px 16px -6px rgba(15, 23, 42, 0.5);
}

.lp-nav__name {
    font-family: var(--lp-font-display);
    font-size: 20px;
    font-weight: 800;
    letter-spacing: -0.02em;
}

.lp-nav__links {
    display: flex;
    align-items: center;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 2px;
}

.lp-nav__link {
    position: relative;
    display: inline-block;
    padding: 10px 8px;
    font-size: 14px;
    font-weight: 700;
    color: rgba(255, 255, 255, 0.82);
    border-radius: 10px;
    white-space: nowrap;
    transition: color 0.2s ease, background 0.2s ease;
}

.lp-nav__link::after {
    content: "";
    position: absolute;
    left: 10px;
    right: 10px;
    bottom: 5px;
    height: 2px;
    border-radius: 2px;
    background: var(--lp-grad);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.25s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.lp-nav__link:hover,
.lp-nav__link.is-active {
    color: #fff;
}

.lp-nav__link:hover::after,
.lp-nav__link.is-active::after {
    transform: scaleX(1);
}

.lp-nav--solid .lp-nav__link {
    color: var(--lp-text-soft);
}

.lp-nav--solid .lp-nav__link:hover,
.lp-nav--solid .lp-nav__link.is-active {
    color: var(--lp-ink);
}

.lp-nav__cta {
    padding: 11px 20px;
    font-size: 14px;
    margin-left: 10px;
    white-space: nowrap;
}

/* Login: compact toggle in the bar, form opens in a popover */
.lp-nav__login {
    position: relative;
    margin-left: 6px;
}

.lp-nav__login-toggle {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.22);
    background: rgba(255, 255, 255, 0.08);
    color: #fff;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s ease, color 0.35s ease, border-color 0.35s ease;
}

.lp-nav__login-toggle:hover,
.lp-nav__login-toggle.is-open {
    background: rgba(255, 255, 255, 0.18);
}

.lp-nav--solid .lp-nav__login-toggle {
    border-color: rgba(15, 23, 42, 0.12);
    background: #f8fafc;
    color: var(--lp-ink);
}

.lp-nav--solid .lp-nav__login-toggle:hover,
.lp-nav--solid .lp-nav__login-toggle.is-open {
    background: var(--lp-brand-soft);
    color: var(--lp-brand-2);
}

.lp-nav__popover {
    position: absolute;
    top: calc(100% + 12px);
    right: 0;
    width: 280px;
    padding: 18px;
    background: #fff;
    border-radius: 18px;
    border: 1px solid #f1f5f9;
    box-shadow: 0 24px 50px -16px rgba(15, 23, 42, 0.35);
    animation: lp-pop 0.2s cubic-bezier(0.2, 0.7, 0.2, 1);
    z-index: 2100;
}

.lp-nav__popover-title {
    font-family: var(--lp-font-display);
    font-size: 15px;
    font-weight: 800;
    color: var(--lp-ink);
    margin-bottom: 12px;
}

.lp-nav__popover .login-form-dynamic {
    flex-direction: column;
    gap: 10px;
}

.lp-nav__popover .login-input {
    width: 100%;
    padding: 12px 14px;
    font-size: 14px;
    border-radius: 12px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.lp-nav__popover .login-input:focus {
    outline: none;
    border-color: var(--lp-brand);
    box-shadow: var(--lp-ring);
}

.lp-nav__popover .login-submit-btn {
    width: 100%;
    padding: 12px;
    font-size: 14px;
    border-radius: 12px;
    background: var(--lp-grad);
}

.lp-nav__popover .login-error-msg {
    position: static;
    font-size: 12px;
}

.lp-nav__user-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    font-size: 14px;
    font-weight: 700;
    color: var(--lp-text-soft);
    background: transparent;
    border: none;
    border-radius: 10px;
    cursor: pointer;
    font-family: inherit;
    transition: background 0.2s ease, color 0.2s ease;
}

.lp-nav__user-btn:hover {
    background: var(--lp-brand-soft);
    color: var(--lp-brand-2);
}

.lp-nav__user-btn--accent {
    color: var(--lp-brand);
}

.lp-nav__profile {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 12px 6px 6px;
    background: #f8fafc;
    border-radius: 999px;
    border: 1px solid #e2e8f0;
    cursor: pointer;
    -webkit-user-select: none;
            user-select: none;
    transition: box-shadow 0.2s ease;
}

.lp-nav__profile:hover {
    box-shadow: var(--lp-ring);
}

.lp-nav__dropdown {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    width: 250px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 20px 40px -12px rgba(15, 23, 42, 0.3);
    border: 1px solid #f1f5f9;
    padding: 8px;
    z-index: 2100;
    animation: lp-pop 0.2s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.lp-nav__burger {
    display: none;
    align-items: center;
    justify-content: center;
    width: 46px;
    height: 46px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    cursor: pointer;
    transition: background 0.2s ease, color 0.35s ease;
    -webkit-tap-highlight-color: transparent;
}

.lp-nav--solid .lp-nav__burger {
    background: var(--lp-ink);
    border-color: var(--lp-ink);
    color: #fff;
}

@media (max-width: 1200px) {
    .lp-nav__links { display: none; }
    .lp-nav__burger { display: inline-flex; }
}

/* --------------------------------------------------------------------------
   Mobile slide-in menu
   -------------------------------------------------------------------------- */

.lp-mobile__overlay {
    position: fixed;
    inset: 0;
    background: rgba(11, 18, 36, 0.7);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    z-index: 2200;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.35s ease, visibility 0.35s ease;
    touch-action: none;
}

.lp-mobile__overlay.is-open {
    opacity: 1;
    visibility: visible;
}

.lp-mobile {
    position: fixed;
    top: 0;
    right: 0;
    width: 88%;
    max-width: 360px;
    height: 100dvh;
    background: #fff;
    z-index: 2250;
    display: flex;
    flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);
    box-shadow: -20px 0 60px rgba(11, 18, 36, 0.3);
    overflow-y: auto;
    font-family: var(--lp-font);
}

.lp-mobile.is-open {
    transform: translateX(0);
}

.lp-mobile__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--lp-line);
}

.lp-mobile__brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-family: var(--lp-font-display);
    font-weight: 800;
    font-size: 18px;
    color: var(--lp-ink);
}

.lp-mobile__brand img {
    width: 34px;
    height: 34px;
    border-radius: 10px;
}

.lp-mobile__close {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    border: 1px solid var(--lp-line);
    background: #f8fafc;
    color: var(--lp-ink);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.lp-mobile__body {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    flex: 1 1;
}

.lp-mobile__label {
    display: block;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--lp-brand-2);
}

.lp-mobile__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 6px;
}

.lp-mobile__link {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 700;
    color: var(--lp-ink);
    background: #f8fafc;
    border: 1px solid transparent;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.lp-mobile__link .lp-mobile__arrow {
    margin-left: auto;
    opacity: 0.35;
    transition: transform 0.2s ease, opacity 0.2s ease;
}

.lp-mobile__link:hover,
.lp-mobile__link:focus-visible {
    background: var(--lp-brand-soft);
    color: var(--lp-brand-2);
    border-color: rgba(99, 114, 255, 0.25);
}

.lp-mobile__link:hover .lp-mobile__arrow {
    opacity: 1;
    transform: translateX(3px);
}

.lp-mobile__link.is-active {
    background: var(--lp-grad);
    color: #fff;
    box-shadow: 0 12px 24px -12px rgba(99, 114, 255, 0.8);
}

.lp-mobile__link.is-active .lp-mobile__arrow {
    opacity: 1;
}

.lp-mobile__link--accent {
    color: var(--lp-brand);
}

.lp-mobile__account {
    margin-top: auto;
}

.lp-mobile__login {
    padding: 18px;
    border-radius: 18px;
    background: var(--lp-bg);
    border: 1px solid var(--lp-line);
    display: grid;
    gap: 12px;
}

.lp-mobile__login .login-form-dynamic {
    flex-direction: column;
}

.lp-mobile__login .login-input {
    width: 100%;
    padding: 12px 14px;
    font-size: 15px;
    border-radius: 12px;
}

.lp-mobile__login .login-submit-btn {
    padding: 12px;
    border-radius: 12px;
    background: var(--lp-ink);
    font-size: 15px;
}

.lp-mobile__login .login-error-msg {
    position: static;
    font-size: 12px;
}

.lp-mobile__user {
    padding: 20px;
    border-radius: 18px;
    background: var(--lp-bg);
    border: 1px solid var(--lp-line);
    text-align: center;
}

.lp-mobile__user--admin {
    background: #fffbeb;
    border-color: #fde68a;
}

.lp-mobile__user-role {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--lp-muted);
    margin-bottom: 8px;
}

.lp-mobile__user--admin .lp-mobile__user-role {
    color: #d97706;
}

.lp-mobile__user-name {
    font-weight: 800;
    color: var(--lp-ink);
    word-break: break-all;
    margin: 0 0 16px;
}

.lp-mobile__logout {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 13px;
    border-radius: 12px;
    border: none;
    background: #e11d48;
    color: #fff;
    font-family: inherit;
    font-weight: 800;
    cursor: pointer;
}

/* Scroll progress bar */
.lp-progress {
    position: fixed;
    top: 0;
    left: 0;
    height: 3px;
    width: 100%;
    z-index: 2001;
    background: transparent;
    pointer-events: none;
}

.lp-progress__bar {
    height: 100%;
    width: 100%;
    background: var(--lp-grad);
    transform-origin: left;
    transform: scaleX(0);
}

/* Back to top */
.lp-top {
    position: fixed;
    right: 22px;
    bottom: 22px;
    z-index: 1500;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    border: none;
    background: var(--lp-ink);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 16px 30px -12px rgba(15, 23, 42, 0.6);
    cursor: pointer;
    opacity: 0;
    transform: translateY(16px) scale(0.9);
    pointer-events: none;
    transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.2, 0.7, 0.2, 1), background 0.2s ease;
}

.lp-top.is-visible {
    opacity: 1;
    transform: none;
    pointer-events: auto;
}

.lp-top:hover {
    background: var(--lp-brand-2);
}

/* --------------------------------------------------------------------------
   Hero
   -------------------------------------------------------------------------- */

.lp-hero {
    position: relative;
    min-height: 100dvh;
    display: flex;
    align-items: center;
    padding: 140px 0 130px;
    color: #fff;
    background:
        radial-gradient(900px 500px at 15% 10%, rgba(99, 114, 255, 0.35), transparent 60%),
        radial-gradient(800px 500px at 90% 85%, rgba(34, 211, 238, 0.22), transparent 60%),
        linear-gradient(160deg, #0b1224 0%, #0f1a36 50%, #141f45 100%);
    overflow: hidden;
    isolation: isolate;
}

/* Grid pattern */
.lp-hero__grid {
    position: absolute;
    inset: 0;
    background-image:
        linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
    background-size: 64px 64px;
    mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.9), transparent 75%);
    -webkit-mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.9), transparent 75%);
    z-index: -1;
    animation: lp-grid-drift 40s linear infinite;
}

/* Aurora blobs */
.lp-hero__blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(70px);
    opacity: 0.55;
    z-index: -1;
    animation: lp-float 18s ease-in-out infinite;
}

.lp-hero__blob--1 {
    width: 520px;
    height: 520px;
    top: -140px;
    left: -120px;
    background: #5ca9fb;
}

.lp-hero__blob--2 {
    width: 460px;
    height: 460px;
    bottom: -160px;
    right: -80px;
    background: #6372ff;
    animation-delay: -6s;
}

.lp-hero__blob--3 {
    width: 320px;
    height: 320px;
    top: 40%;
    left: 55%;
    background: #22d3ee;
    opacity: 0.25;
    animation-delay: -12s;
}

.lp-hero__inner {
    display: grid;
    grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);
    gap: 48px;
    align-items: center;
}

.lp-hero__eyebrow {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 16px 8px 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.16);
    color: #cfe3ff;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    margin-bottom: 26px;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
}

.lp-hero__pulse {
    position: relative;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--lp-accent);
    flex-shrink: 0;
}

.lp-hero__pulse::after {
    content: "";
    position: absolute;
    inset: -4px;
    border-radius: 50%;
    border: 2px solid var(--lp-accent);
    animation: lp-pulse 1.8s ease-out infinite;
}

.landing-page .lp-hero__title {
    font-size: clamp(34px, 4.4vw, 58px);
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: -0.03em;
    color: #fff;
    margin-bottom: 24px;
    overflow-wrap: anywhere;
}

.lp-hero__title em {
    font-style: normal;
    background: var(--lp-grad-text);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
}

.lp-hero__text {
    font-size: clamp(17px, 1.5vw, 20px);
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.8);
    max-width: 620px;
    margin-bottom: 30px;
}

.lp-hero__benefits {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    list-style: none;
    padding: 0;
    margin: 0 0 36px;
}

.lp-hero__benefit {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 9px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.16);
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    backdrop-filter: blur(8px);
    -webkit-backdrop-filter: blur(8px);
}

.lp-hero__benefit svg {
    color: var(--lp-accent);
    flex-shrink: 0;
}

.lp-hero__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
}

.lp-hero__visual {
    position: relative;
    display: flex;
    justify-content: center;
    animation: lp-rise 1.1s cubic-bezier(0.2, 0.7, 0.2, 1) 0.3s both;
}

.lp-hero__visual svg {
    width: 100%;
    max-width: 520px;
    height: auto;
    filter: drop-shadow(0 30px 50px rgba(0, 0, 0, 0.45));
}

.lp-hero__scroll {
    position: absolute;
    left: 50%;
    bottom: 26px;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    color: rgba(255, 255, 255, 0.55);
    font-size: 11px;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    text-decoration: none;
}

.lp-hero__scroll:hover {
    color: #fff;
}

.lp-hero__scroll span {
    width: 1px;
    height: 44px;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.6), transparent);
    animation: lp-scroll-line 2s ease-in-out infinite;
}

/* Hero stagger animations on initial load */
.lp-hero__copy > * {
    animation: lp-rise 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) both;
}

.lp-hero__copy > :nth-child(1) { animation-delay: 0.05s; }
.lp-hero__copy > :nth-child(2) { animation-delay: 0.15s; }
.lp-hero__copy > :nth-child(3) { animation-delay: 0.25s; }
.lp-hero__copy > :nth-child(4) { animation-delay: 0.35s; }
.lp-hero__copy > :nth-child(5) { animation-delay: 0.45s; }

/* Integration diagram animation */
.lp-net__link {
    stroke: rgba(255, 255, 255, 0.22);
    stroke-width: 1.5;
    fill: none;
}

.lp-net__flow {
    stroke: url(#lp-net-grad);
    stroke-width: 2.5;
    fill: none;
    stroke-dasharray: 14 180;
    stroke-linecap: round;
    animation: lp-dash 3.2s linear infinite;
}

.lp-net__node {
    animation: lp-node-float 6s ease-in-out infinite;
}

.lp-net__node:nth-of-type(2) { animation-delay: -1s; }
.lp-net__node:nth-of-type(3) { animation-delay: -2s; }
.lp-net__node:nth-of-type(4) { animation-delay: -3s; }
.lp-net__node:nth-of-type(5) { animation-delay: -4s; }
.lp-net__node:nth-of-type(6) { animation-delay: -5s; }

.lp-net__ring {
    fill: none;
    stroke: rgba(124, 192, 255, 0.5);
    stroke-width: 1.5;
    transform-origin: 260px 230px;
    animation: lp-ring 4s ease-out infinite;
}

.lp-net__ring--2 { animation-delay: -2s; }

.lp-net__label {
    font-family: var(--lp-font);
    font-size: 12px;
    font-weight: 700;
    fill: #fff;
    text-anchor: middle;
}

.lp-net__sub {
    font-family: var(--lp-font);
    font-size: 9.5px;
    font-weight: 500;
    fill: rgba(255, 255, 255, 0.6);
    text-anchor: middle;
}

@media (max-width: 1024px) {
    .lp-hero__inner {
        grid-template-columns: minmax(0, 1fr);
        text-align: center;
    }

    .lp-hero__text {
        margin-left: auto;
        margin-right: auto;
    }

    .lp-hero__benefits,
    .lp-hero__actions {
        justify-content: center;
    }

    .lp-hero__visual {
        order: -1;
    }

    .lp-hero__visual svg {
        max-width: 360px;
    }

    .lp-hero {
        padding-top: 120px;
    }
}

@media (max-width: 640px) {
    .lp-hero__visual {
        display: none;
    }

    .lp-hero__actions .lp-btn {
        width: 100%;
    }

    .lp-hero__benefit {
        white-space: normal;
        text-align: left;
    }

    .lp-hero__blob {
        filter: blur(50px);
    }
}

/* --------------------------------------------------------------------------
   Features
   -------------------------------------------------------------------------- */

.lp-feature {
    gap: 16px;
}

.lp-feature__index {
    position: absolute;
    top: 18px;
    right: 22px;
    font-family: var(--lp-font-display);
    font-size: 44px;
    font-weight: 800;
    line-height: 1;
    color: rgba(15, 23, 42, 0.05);
    transition: color 0.3s ease;
}

.lp-feature:hover .lp-feature__index {
    color: rgba(99, 114, 255, 0.16);
}

/* Browser-style frame for showcase images */
.lp-frame {
    margin: clamp(48px, 6vw, 72px) auto 0;
    max-width: 880px;
    border-radius: 18px;
    background: #0f172a;
    box-shadow: var(--lp-shadow-lg);
    overflow: hidden;
    border: 1px solid rgba(15, 23, 42, 0.12);
}

.lp-frame__bar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #111a33;
    border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.lp-frame__dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ff5f57;
}

.lp-frame__dot:nth-child(2) { background: #febc2e; }
.lp-frame__dot:nth-child(3) { background: #28c840; }

.lp-frame__url {
    margin-left: 12px;
    flex: 1 1;
    height: 22px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.06);
    color: rgba(255, 255, 255, 0.5);
    font-size: 11px;
    display: flex;
    align-items: center;
    padding: 0 10px;
    font-family: monospace;
}

.lp-frame img {
    display: block;
    width: 100%;
    height: auto;
    transition: transform 0.6s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.lp-frame a:hover img {
    transform: scale(1.02);
}

.lp-actions-center {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 32px;
}

/* --------------------------------------------------------------------------
   Form controls
   -------------------------------------------------------------------------- */

.lp-field {
    display: block;
    margin-bottom: 16px;
}

.lp-field__label {
    display: block;
    font-size: 13px;
    font-weight: 700;
    color: var(--lp-text-soft);
    margin-bottom: 8px;
    letter-spacing: 0.02em;
}

.lp-input,
.lp-textarea {
    width: 100%;
    padding: 13px 16px;
    border-radius: 12px;
    border: 1px solid #dbe2ee;
    background: #fff;
    font-family: var(--lp-font);
    font-size: 15px;
    color: var(--lp-text);
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.lp-textarea {
    resize: vertical;
    min-height: 160px;
    line-height: 1.6;
}

.lp-input:focus,
.lp-textarea:focus {
    outline: none;
    border-color: var(--lp-brand);
    box-shadow: var(--lp-ring);
}

.lp-input--dark {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.16);
    color: #fff;
}

.lp-input--dark:focus {
    border-color: #7cc0ff;
    box-shadow: 0 0 0 4px rgba(124, 192, 255, 0.2);
}

.lp-input--dark::placeholder {
    color: rgba(255, 255, 255, 0.35);
}

/* --------------------------------------------------------------------------
   About
   -------------------------------------------------------------------------- */

.lp-about {
    display: grid;
    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
    gap: clamp(32px, 5vw, 72px);
    align-items: center;
}

@media (max-width: 960px) {
    .lp-about { grid-template-columns: minmax(0, 1fr); }
}

.lp-about__media {
    position: relative;
    padding: 20px;
}

.lp-about__media::before {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: 32px;
    background: var(--lp-grad);
    opacity: 0.12;
    transform: rotate(-3deg);
}

.lp-about__media::after {
    content: "";
    position: absolute;
    inset: 10px;
    border-radius: 28px;
    border: 1px dashed rgba(99, 114, 255, 0.35);
    transform: rotate(2deg);
}

.lp-about__img {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    border-radius: 24px;
    box-shadow: var(--lp-shadow-lg);
    background: #fff;
}

.lp-about__badge {
    position: absolute;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: #fff;
    border-radius: 16px;
    box-shadow: var(--lp-shadow-lg);
    border: 1px solid var(--lp-line);
    animation: lp-float-sm 7s ease-in-out infinite;
}

.lp-about__badge--1 { bottom: 34px; left: -12px; }
.lp-about__badge--2 { top: 30px; right: -12px; animation-delay: -3s; }

.lp-about__badge strong {
    display: block;
    font-family: var(--lp-font-display);
    font-size: 16px;
    color: var(--lp-ink);
    line-height: 1.2;
}

.lp-about__badge span {
    font-size: 12px;
    color: var(--lp-muted);
}

@media (max-width: 560px) {
    .lp-about__badge { position: static; margin-top: 12px; animation: none; }
}

.lp-about__text p {
    font-size: 17px;
    line-height: 1.75;
    color: var(--lp-text-soft);
}

.lp-about__sub {
    font-size: 22px;
    font-weight: 800;
    margin: 32px 0 16px;
}

.lp-checks {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px 22px;
    list-style: none;
    margin: 0;
    padding: 0;
}

@media (max-width: 640px) {
    .lp-checks { grid-template-columns: minmax(0, 1fr); }
}

.lp-check {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    font-size: 15.5px;
    line-height: 1.6;
    color: var(--lp-text);
    padding: 10px 12px;
    border-radius: 12px;
    transition: background 0.2s ease;
}

.lp-check:hover {
    background: var(--lp-brand-soft);
}

.lp-check__icon {
    width: 24px;
    height: 24px;
    border-radius: 8px;
    background: var(--lp-grad);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    margin-top: 2px;
}

/* --------------------------------------------------------------------------
   Services
   -------------------------------------------------------------------------- */

.lp-service {
    gap: 14px;
}

.lp-service__icon {
    width: 58px;
    height: 58px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #9cc8ff;
    font-size: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;
}

.lp-service:hover .lp-service__icon {
    background: var(--lp-grad);
    color: #fff;
    transform: rotate(-6deg) scale(1.06);
}

.lp-split {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 32px;
    align-items: center;
    margin-top: clamp(48px, 6vw, 80px);
    padding: clamp(28px, 4vw, 48px);
    border-radius: 28px;
    background: rgba(255, 255, 255, 0.04);
    border: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 900px) {
    .lp-split { grid-template-columns: minmax(0, 1fr); }
}

.lp-split__full {
    grid-column: 1 / -1;
}

.lp-split h3 {
    font-size: clamp(22px, 2.4vw, 28px);
    font-weight: 800;
    margin-bottom: 14px;
}

.lp-split h4 {
    font-size: 19px;
    font-weight: 800;
    color: #9cc8ff;
    margin-bottom: 12px;
}

.lp-split p {
    color: rgba(255, 255, 255, 0.74);
    line-height: 1.75;
    font-size: 16px;
}

.lp-zoom {
    position: relative;
    display: block;
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid rgba(255, 255, 255, 0.12);
    box-shadow: 0 30px 60px -24px rgba(0, 0, 0, 0.7);
    cursor: zoom-in;
    background: none;
    padding: 0;
    width: 100%;
}

.lp-zoom img {
    display: block;
    width: 100%;
    transition: transform 0.6s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.lp-zoom:hover img {
    transform: scale(1.04);
}

.lp-zoom::after {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(to top, rgba(11, 18, 36, 0.55), transparent 50%);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.lp-zoom:hover::after {
    opacity: 1;
}

.lp-zoom__hint {
    position: absolute;
    right: 12px;
    bottom: 12px;
    z-index: 1;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 8px;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    backdrop-filter: blur(6px);
    opacity: 0;
    transform: translateY(6px);
    transition: opacity 0.3s ease, transform 0.3s ease;
}

.lp-zoom:hover .lp-zoom__hint {
    opacity: 1;
    transform: none;
}

/* --------------------------------------------------------------------------
   Why us
   -------------------------------------------------------------------------- */

.lp-why {
    gap: 16px;
    padding-top: 36px;
}

.lp-why__num {
    position: absolute;
    top: -6px;
    right: 18px;
    font-family: var(--lp-font-display);
    font-size: 96px;
    font-weight: 800;
    line-height: 1;
    color: transparent;
    -webkit-text-stroke: 1px rgba(99, 114, 255, 0.22);
    pointer-events: none;
    transition: -webkit-text-stroke-color 0.3s ease;
}

.lp-why:hover .lp-why__num {
    -webkit-text-stroke: 1px rgba(99, 114, 255, 0.55);
}

.lp-why__link {
    margin-top: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 800;
    color: var(--lp-brand-2);
}

.lp-why__link svg {
    transition: transform 0.2s ease;
}

.lp-why__link:hover svg {
    transform: translateX(4px);
}

/* --------------------------------------------------------------------------
   Process steps & pricing
   -------------------------------------------------------------------------- */

.lp-steps {
    position: relative;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 24px;
    margin-bottom: clamp(56px, 7vw, 88px);
}

.lp-steps::before {
    content: "";
    position: absolute;
    left: 8%;
    right: 8%;
    top: 30px;
    height: 2px;
    background: linear-gradient(90deg, rgba(92, 169, 251, 0.2), rgba(99, 114, 255, 0.6), rgba(92, 169, 251, 0.2));
}

@media (max-width: 1024px) {
    .lp-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .lp-steps::before { display: none; }
}

@media (max-width: 560px) {
    .lp-steps { grid-template-columns: minmax(0, 1fr); }
}

.lp-step {
    position: relative;
    text-align: left;
}

.lp-step__num {
    position: relative;
    z-index: 1;
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: var(--lp-grad);
    color: #fff;
    font-family: var(--lp-font-display);
    font-size: 22px;
    font-weight: 800;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 0 8px #fff, 0 14px 28px -10px rgba(99, 114, 255, 0.8);
    margin-bottom: 20px;
}

.lp-section--soft .lp-step__num {
    box-shadow: 0 0 0 8px var(--lp-bg), 0 14px 28px -10px rgba(99, 114, 255, 0.8);
}

.lp-step h3 {
    font-size: 19px;
    font-weight: 800;
    margin-bottom: 8px;
}

.lp-step p {
    color: var(--lp-text-soft);
    font-size: 15px;
    line-height: 1.7;
}

.lp-price {
    max-width: 920px;
    margin: 0 auto;
}

.lp-price h3 {
    text-align: center;
    font-size: clamp(24px, 2.6vw, 30px);
    font-weight: 800;
    margin-bottom: 22px;
}

.lp-price__table {
    background: #fff;
    border-radius: var(--lp-radius);
    border: 1px solid var(--lp-line);
    box-shadow: var(--lp-shadow);
    overflow: hidden;
}

.lp-price__row {
    display: grid;
    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);
    gap: 16px;
    align-items: center;
    padding: 20px 26px;
    border-bottom: 1px solid var(--lp-line);
    font-size: 16px;
    transition: background 0.2s ease;
}

.lp-price__row:last-child {
    border-bottom: none;
}

.lp-price__row:hover {
    background: var(--lp-bg);
}

.lp-price__row--highlight {
    background: linear-gradient(90deg, rgba(92, 169, 251, 0.12), rgba(99, 114, 255, 0.08));
}

.lp-price__row--highlight:hover {
    background: linear-gradient(90deg, rgba(92, 169, 251, 0.18), rgba(99, 114, 255, 0.12));
}

.lp-price__service {
    display: flex;
    align-items: center;
    gap: 12px;
    font-weight: 600;
    color: var(--lp-text);
}

.lp-price__service svg {
    color: var(--lp-brand-2);
    flex-shrink: 0;
}

.lp-price__value {
    font-family: var(--lp-font-display);
    font-weight: 800;
    color: var(--lp-ink);
    text-align: right;
}

.lp-price__row--highlight .lp-price__value {
    background: var(--lp-grad);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

@media (max-width: 560px) {
    .lp-price__row { grid-template-columns: minmax(0, 1fr); gap: 6px; }
    .lp-price__value { text-align: left; }
}

.lp-price__note {
    margin-top: 16px;
    font-size: 14px;
    color: var(--lp-muted);
    line-height: 1.7;
}

.lp-price__delivery {
    margin-top: 40px;
    padding: 26px 28px;
    border-radius: var(--lp-radius);
    background: var(--lp-bg);
    border: 1px solid var(--lp-line);
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 18px;
    align-items: flex-start;
}

@media (max-width: 560px) {
    .lp-price__delivery { grid-template-columns: minmax(0, 1fr); }
}

.lp-price__delivery h3 {
    text-align: left;
    font-size: 20px;
    margin-bottom: 8px;
}

.lp-price__delivery p {
    color: var(--lp-text-soft);
    line-height: 1.75;
    font-size: 15.5px;
}

/* --------------------------------------------------------------------------
   Portfolio / gallery
   -------------------------------------------------------------------------- */

.lp-portfolio__intro {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 20px 40px;
    margin-bottom: clamp(40px, 5vw, 56px);
}

@media (max-width: 860px) {
    .lp-portfolio__intro { grid-template-columns: minmax(0, 1fr); }
}

.lp-portfolio__intro p {
    font-size: 16.5px;
    line-height: 1.75;
    color: var(--lp-text-soft);
    padding-left: 18px;
    border-left: 3px solid rgba(99, 114, 255, 0.25);
}

.lp-portfolio__intro strong {
    color: var(--lp-ink);
}

.lp-project {
    gap: 14px;
}

.lp-project__mark {
    width: 56px;
    height: 56px;
    border-radius: 16px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: var(--lp-font-display);
    font-size: 24px;
    font-weight: 800;
    color: #fff;
    background: var(--lp-grad);
    box-shadow: 0 12px 24px -10px rgba(99, 114, 255, 0.7);
}

.lp-project__label {
    font-size: 13px;
    color: var(--lp-muted);
}

.lp-project__link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-weight: 800;
    font-size: 15px;
    word-break: break-all;
    margin-top: auto;
}

.lp-project__link svg {
    flex-shrink: 0;
    transition: transform 0.2s ease;
}

.lp-project__link:hover svg {
    transform: translate(2px, -2px);
}

/* --------------------------------------------------------------------------
   Testimonials
   -------------------------------------------------------------------------- */

.lp-quote {
    gap: 18px;
}

.lp-quote__mark {
    position: absolute;
    top: 16px;
    right: 22px;
    color: rgba(99, 114, 255, 0.14);
    transition: color 0.3s ease, transform 0.3s ease;
}

.lp-quote:hover .lp-quote__mark {
    color: rgba(99, 114, 255, 0.35);
    transform: rotate(-8deg);
}

.lp-quote__stars {
    display: flex;
    gap: 2px;
    color: #f59e0b;
}

.lp-quote__text {
    font-size: 16.5px;
    line-height: 1.75;
    color: var(--lp-text);
    flex: 1 1;
}

.lp-quote__author {
    display: flex;
    align-items: center;
    gap: 14px;
    padding-top: 16px;
    border-top: 1px solid var(--lp-line);
}

.lp-avatar {
    width: 50px;
    height: 50px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
    background: var(--lp-grad);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: var(--lp-font-display);
    font-weight: 800;
    font-size: 17px;
    box-shadow: 0 0 0 3px #fff, 0 0 0 4px rgba(99, 114, 255, 0.35);
}

.lp-quote__name {
    font-weight: 800;
    color: var(--lp-ink);
    font-size: 15px;
}

/* --------------------------------------------------------------------------
   Team
   -------------------------------------------------------------------------- */

.lp-team-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 24px;
    max-width: 1000px;
    margin: 0 auto;
}

@media (max-width: 860px) {
    .lp-team-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 560px) {
    .lp-team-grid { grid-template-columns: minmax(0, 1fr); }
}

.lp-member {
    align-items: center;
    text-align: center;
    padding: 32px 24px 28px;
    gap: 6px;
}

.lp-member__ring {
    position: relative;
    width: 150px;
    height: 150px;
    border-radius: 50%;
    padding: 5px;
    background: var(--lp-grad);
    margin-bottom: 14px;
    transition: transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.lp-member__ring::before {
    content: "";
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    border: 1px dashed rgba(99, 114, 255, 0.4);
    animation: lp-spin 24s linear infinite;
}

.lp-member:hover .lp-member__ring {
    transform: scale(1.05);
}

.lp-member__img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    border: 4px solid #fff;
    display: block;
}

.lp-member__name {
    font-size: 20px;
    font-weight: 800;
}

.lp-member__role {
    color: var(--lp-brand-2);
    font-weight: 700;
    font-size: 14px;
    letter-spacing: 0.02em;
}

/* --------------------------------------------------------------------------
   Articles
   -------------------------------------------------------------------------- */

.lp-article {
    gap: 12px;
}

.lp-article__meta {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    font-size: 13px;
    color: var(--lp-muted);
}

.lp-article__title {
    font-size: 19px;
    line-height: 1.35;
    font-weight: 800;
}

.lp-article__title a {
    color: var(--lp-ink);
}

.lp-article__title a:hover {
    color: var(--lp-brand-2);
}

.lp-article__read {
    margin-top: auto;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-weight: 800;
    color: var(--lp-brand-2);
}

.lp-article__read svg {
    transition: transform 0.2s ease;
}

.lp-article__read:hover svg {
    transform: translateX(4px);
}

.lp-article__links {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 12px;
    margin-top: 36px;
}

/* --------------------------------------------------------------------------
   Contact
   -------------------------------------------------------------------------- */

.lp-contact {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 24px;
    align-items: stretch;
}

@media (max-width: 900px) {
    .lp-contact { grid-template-columns: minmax(0, 1fr); }
}

.lp-contact__card {
    color: var(--lp-text);
    gap: 0;
}

.lp-contact__card h3 {
    font-size: 22px;
    font-weight: 800;
    margin-bottom: 20px;
    color: var(--lp-ink);
}

.lp-contact__list {
    list-style: none;
    padding: 0;
    margin: 0 0 20px;
    display: grid;
    gap: 14px;
}

.lp-contact__item {
    display: flex;
    gap: 14px;
    align-items: flex-start;
}

.lp-contact__item .lp-icon-badge {
    width: 42px;
    height: 42px;
    border-radius: 12px;
    font-size: 16px;
}

.lp-contact__item strong {
    display: block;
    font-size: 12px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--lp-muted);
    margin-bottom: 2px;
}

.lp-contact__item > div {
    min-width: 0;
}

.lp-contact__item span,
.lp-contact__item a {
    font-size: 16px;
    font-weight: 600;
    color: var(--lp-text);
    overflow-wrap: anywhere;
}

.lp-contact__item a:hover {
    color: var(--lp-brand-2);
}

.lp-contact__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 20px;
}

.lp-contact__support {
    margin-top: 22px;
    padding-top: 20px;
    border-top: 1px solid var(--lp-line);
}

.lp-contact__support h4 {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 800;
    margin-bottom: 12px;
    color: var(--lp-ink);
}

.lp-contact__support dl {
    display: grid;
    grid-template-columns: auto minmax(0, 1fr);
    gap: 6px 14px;
    margin: 0;
    font-size: 14.5px;
}

.lp-contact__support dt {
    font-weight: 700;
    color: var(--lp-text);
}

.lp-contact__support dd {
    margin: 0;
    color: var(--lp-text-soft);
}

.lp-contact__map {
    padding: 0;
    min-height: 420px;
}

.lp-contact__map iframe {
    width: 100%;
    height: 100%;
    min-height: 420px;
    border: 0;
    display: block;
    filter: saturate(0.9);
}

/* --------------------------------------------------------------------------
   FAQ (native details accordion)
   -------------------------------------------------------------------------- */

.lp-faq {
    max-width: 860px;
    margin: 0 auto;
    display: grid;
    gap: 12px;
}

.lp-faq__item {
    background: #fff;
    border: 1px solid var(--lp-line);
    border-radius: 16px;
    box-shadow: var(--lp-shadow);
    overflow: hidden;
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
}

.lp-faq__item[open] {
    border-color: rgba(99, 114, 255, 0.4);
    box-shadow: var(--lp-shadow-lg);
}

.lp-faq__q {
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 24px;
    cursor: pointer;
    font-family: var(--lp-font-display);
    font-size: 17px;
    font-weight: 800;
    color: var(--lp-ink);
    -webkit-user-select: none;
            user-select: none;
    transition: color 0.2s ease;
}

.lp-faq__q::-webkit-details-marker {
    display: none;
}

.lp-faq__q:hover {
    color: var(--lp-brand-2);
}

.lp-faq__icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 10px;
    background: var(--lp-brand-soft);
    color: var(--lp-brand-2);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s cubic-bezier(0.2, 0.7, 0.2, 1), background 0.3s ease, color 0.3s ease;
}

.lp-faq__item[open] .lp-faq__icon {
    transform: rotate(180deg);
    background: var(--lp-grad);
    color: #fff;
}

.lp-faq__a {
    padding: 0 24px 22px;
    color: var(--lp-text-soft);
    font-size: 15.5px;
    line-height: 1.75;
    animation: lp-fade-down 0.35s cubic-bezier(0.2, 0.7, 0.2, 1);
}

/* --------------------------------------------------------------------------
   Footer
   -------------------------------------------------------------------------- */

.lp-footer {
    background: var(--lp-ink);
    color: rgba(255, 255, 255, 0.7);
    padding: 64px 0 28px;
    font-size: 14.5px;
}

.lp-footer__grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1.1fr 1fr;
    gap: 32px;
    padding-bottom: 36px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 960px) {
    .lp-footer__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}

@media (max-width: 520px) {
    .lp-footer__grid { grid-template-columns: minmax(0, 1fr); }
}

.lp-footer__brand {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #fff;
    font-family: var(--lp-font-display);
    font-size: 18px;
    font-weight: 800;
    margin-bottom: 14px;
}

.lp-footer__brand img {
    width: 36px;
    height: 36px;
    border-radius: 10px;
}

.lp-footer h4 {
    font-size: 12.5px;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: #fff;
    margin-bottom: 14px;
}

.lp-footer p {
    line-height: 1.8;
}

.lp-footer ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: grid;
    gap: 8px;
}

.lp-footer a {
    color: rgba(255, 255, 255, 0.72);
    transition: color 0.2s ease, padding-left 0.2s ease;
}

.lp-footer a:hover {
    color: #fff;
}

.lp-footer ul a:hover {
    padding-left: 4px;
}

.lp-footer__bottom {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 10px;
    padding-top: 24px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.5);
}

/* --------------------------------------------------------------------------
   Lightbox
   -------------------------------------------------------------------------- */

.lp-lightbox {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(5, 10, 24, 0.88);
    backdrop-filter: blur(10px);
    -webkit-backdrop-filter: blur(10px);
    cursor: zoom-out;
    animation: lp-fade 0.25s ease;
}

.lp-lightbox img {
    max-width: 92vw;
    max-height: 90vh;
    border-radius: 16px;
    box-shadow: 0 40px 80px rgba(0, 0, 0, 0.6);
    object-fit: contain;
    animation: lp-pop 0.35s cubic-bezier(0.2, 0.7, 0.2, 1);
}

.lp-lightbox__close {
    position: absolute;
    top: 20px;
    right: 20px;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

/* --------------------------------------------------------------------------
   Products (automation ecosystem)
   -------------------------------------------------------------------------- */

.lp-product {
    gap: 14px;
}

.lp-product--partner {
    border-style: dashed;
}

.lp-product__head {
    display: flex;
    align-items: flex-start;
    gap: 14px;
}

.lp-product__head .lp-icon-badge {
    flex: 0 0 auto;
}

.lp-product__role {
    color: var(--lp-muted);
    font-size: 14px;
    font-weight: 600;
    margin-top: 4px;
    line-height: 1.4;
}

.lp-product__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.lp-tag--partner {
    background: #fff4e5;
    color: #c2410c;
}

.lp-product__list {
    list-style: disc;
    margin: 0;
    padding-left: 18px;
    display: grid;
    gap: 6px;
    color: var(--lp-text-soft);
    font-size: 14.5px;
    line-height: 1.6;
}

.lp-product__toggle {
    align-self: flex-start;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 0;
    border: none;
    background: none;
    color: var(--lp-brand-2);
    font-family: inherit;
    font-size: 14px;
    font-weight: 800;
    cursor: pointer;
}

.lp-product__toggle svg {
    transition: transform 0.2s ease;
}

.lp-product__toggle.is-open svg {
    transform: rotate(180deg);
}

.lp-product__usecase {
    background: var(--lp-bg);
    border: 1px solid var(--lp-line);
    border-radius: var(--lp-radius-sm);
    padding: 16px;
    animation: lp-fade 0.3s ease;
}

.lp-product__usecase-dept {
    display: block;
    font-size: 11.5px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    font-weight: 800;
    color: var(--lp-muted);
    margin-bottom: 6px;
}

.lp-product__usecase h4 {
    font-size: 16px;
    font-weight: 800;
    margin-bottom: 8px;
}

.lp-product__usecase p {
    font-size: 14px;
    color: var(--lp-text-soft);
    line-height: 1.65;
}

.lp-product__metrics {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin: 14px 0 0;
}

.lp-product__metrics div {
    background: #fff;
    border: 1px solid var(--lp-line);
    border-radius: 10px;
    padding: 10px 8px;
    text-align: center;
}

.lp-product__metrics dt {
    font-family: var(--lp-font-display);
    font-weight: 800;
    font-size: 14px;
    color: var(--lp-ink);
}

.lp-product__metrics dd {
    margin: 2px 0 0;
    font-size: 11.5px;
    color: var(--lp-muted);
    line-height: 1.4;
}

@media (max-width: 480px) {
    .lp-product__metrics { grid-template-columns: minmax(0, 1fr); }
}

.lp-product__links {
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid var(--lp-line);
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 13.5px;
    font-weight: 700;
}

.lp-product__links a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.lp-product__flow {
    margin-top: clamp(40px, 5vw, 64px);
    background: var(--lp-surface);
    border: 1px solid var(--lp-line);
    border-radius: var(--lp-radius);
    padding: clamp(20px, 3vw, 32px);
    box-shadow: var(--lp-shadow);
}

.lp-product__flow ol {
    margin: 14px 0 0;
    padding-left: 20px;
    display: grid;
    gap: 8px;
    color: var(--lp-text-soft);
    font-size: 15px;
    line-height: 1.6;
}

.lp-product__flow strong {
    color: var(--lp-ink);
}

/* --------------------------------------------------------------------------
   Inquiry wizard (deterministic inquiry assistant)
   -------------------------------------------------------------------------- */

.lp-wizard {
    max-width: 1040px;
    margin: 0 auto;
    border-radius: 28px;
    background: rgba(5, 10, 24, 0.55);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: clamp(24px, 4vw, 44px);
}

.lp-wizard__steps {
    list-style: none;
    margin: 0 0 28px;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}

.lp-wizard__step {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 6px 14px 6px 6px;
    border-radius: 999px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    color: rgba(255, 255, 255, 0.55);
    font-size: 13.5px;
    font-weight: 700;
}

.lp-wizard__step-num {
    width: 26px;
    height: 26px;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.08);
    font-size: 12px;
    font-weight: 800;
}

.lp-wizard__step.is-active {
    color: #fff;
    border-color: rgba(124, 192, 255, 0.5);
    background: rgba(124, 192, 255, 0.08);
}

.lp-wizard__step.is-active .lp-wizard__step-num {
    background: var(--lp-grad);
    color: #fff;
}

.lp-wizard__step.is-done {
    color: rgba(255, 255, 255, 0.8);
}

.lp-wizard__step.is-done .lp-wizard__step-num {
    background: var(--lp-success);
    color: #fff;
}

.lp-wizard__panel h3 {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: clamp(20px, 2.2vw, 26px);
    font-weight: 800;
    margin-bottom: 8px;
}

.lp-wizard__hint {
    color: rgba(255, 255, 255, 0.6);
    font-size: 14.5px;
    line-height: 1.6;
    margin-bottom: 20px;
}

.lp-wizard__intents {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

@media (max-width: 760px) {
    .lp-wizard__intents { grid-template-columns: minmax(0, 1fr); }
}

.lp-intent {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 18px 18px 18px 52px;
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.04);
    color: #fff;
    text-align: left;
    font-family: inherit;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
}

.lp-intent:hover {
    border-color: rgba(124, 192, 255, 0.5);
    transform: translateY(-2px);
}

.lp-intent.is-checked {
    border-color: #7cc0ff;
    background: rgba(124, 192, 255, 0.12);
    box-shadow: 0 0 0 4px rgba(124, 192, 255, 0.15);
}

.lp-intent__check {
    position: absolute;
    left: 18px;
    top: 20px;
    width: 22px;
    height: 22px;
    border-radius: 7px;
    border: 1px solid rgba(255, 255, 255, 0.3);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: transparent;
    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;
}

.lp-intent.is-checked .lp-intent__check {
    background: var(--lp-grad);
    border-color: transparent;
    color: #fff;
}

.lp-intent__title {
    font-family: var(--lp-font-display);
    font-weight: 800;
    font-size: 16px;
}

.lp-intent__text {
    font-size: 13.5px;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.55;
}

.lp-intent__examples {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 4px;
}

.lp-intent__examples span {
    font-size: 11.5px;
    font-weight: 700;
    padding: 3px 8px;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.7);
}

.lp-wizard__question {
    margin-bottom: 22px;
}

.lp-wizard__label {
    display: block;
    font-size: 12px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.55);
    font-weight: 800;
    margin-bottom: 10px;
}

.lp-wizard__choices {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
}

@media (max-width: 760px) {
    .lp-wizard__choices { grid-template-columns: minmax(0, 1fr); }
}

.lp-choice {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
    padding: 14px 16px;
    border-radius: 14px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.04);
    color: #fff;
    text-align: left;
    font-family: inherit;
    cursor: pointer;
    transition: border-color 0.2s ease, background 0.2s ease;
}

.lp-choice:hover {
    border-color: rgba(124, 192, 255, 0.5);
}

.lp-choice.is-checked {
    border-color: #7cc0ff;
    background: rgba(124, 192, 255, 0.12);
}

.lp-choice__icon {
    color: #9cc8ff;
}

.lp-choice__label {
    font-weight: 800;
    font-size: 15px;
}

.lp-choice__hint {
    font-size: 12.5px;
    color: rgba(255, 255, 255, 0.55);
}

.lp-wizard .lp-field {
    margin-bottom: 0;
}

.lp-wizard textarea.lp-input--dark {
    min-height: 0;
    background: #0a1022;
    border-color: rgba(255, 255, 255, 0.16);
    color: #fff;
    color-scheme: dark;
}

.lp-wizard__results {
    display: grid;
    gap: 14px;
    margin-bottom: 28px;
}

.lp-reco {
    border-radius: 16px;
    border: 1px solid rgba(255, 255, 255, 0.12);
    background: rgba(255, 255, 255, 0.04);
    padding: 20px 22px;
}

.lp-reco h4 {
    font-size: 17px;
    font-weight: 800;
    margin-bottom: 8px;
}

.lp-reco p {
    color: rgba(255, 255, 255, 0.72);
    font-size: 14.5px;
    line-height: 1.65;
}

.lp-reco--ai {
    border-color: rgba(103, 232, 249, 0.35);
    background: rgba(103, 232, 249, 0.06);
}

.lp-reco__products {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 12px;
}

.lp-chip--dark {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 13px;
    padding: 6px 12px;
}

a.lp-chip--dark:hover {
    background: var(--lp-brand);
    color: #fff;
}

.lp-reco__links {
    display: flex;
    flex-wrap: wrap;
    gap: 14px;
    margin-top: 12px;
    font-size: 13.5px;
    font-weight: 700;
}

.lp-reco__links a {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.lp-wizard__summary {
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    padding-top: 24px;
}

.lp-wizard__summary .lp-wizard__hint {
    margin: 12px 0 16px;
}

.lp-wizard__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.lp-wizard__error {
    color: #ff9b9b;
    font-weight: 700;
    font-size: 14px;
    margin-top: 16px;
}

.lp-wizard__nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 28px;
    padding-top: 20px;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
}


/* --------------------------------------------------------------------------
   Keyframes
   -------------------------------------------------------------------------- */

@keyframes lp-rise {
    from { opacity: 0; transform: translateY(28px); }
    to { opacity: 1; transform: none; }
}

@keyframes lp-pop {
    from { opacity: 0; transform: translateY(8px) scale(0.98); }
    to { opacity: 1; transform: none; }
}

@keyframes lp-fade {
    from { opacity: 0; }
    to { opacity: 1; }
}

@keyframes lp-fade-down {
    from { opacity: 0; transform: translateY(-6px); }
    to { opacity: 1; transform: none; }
}

@keyframes lp-float {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(40px, -30px) scale(1.08); }
    66% { transform: translate(-30px, 30px) scale(0.95); }
}

@keyframes lp-float-sm {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
}

@keyframes lp-grid-drift {
    from { background-position: 0 0, 0 0; }
    to { background-position: 0 64px, 64px 0; }
}

@keyframes lp-pulse {
    0% { transform: scale(0.6); opacity: 1; }
    100% { transform: scale(2.2); opacity: 0; }
}

@keyframes lp-scroll-line {
    0% { transform: scaleY(0); transform-origin: top; }
    50% { transform: scaleY(1); transform-origin: top; }
    51% { transform-origin: bottom; }
    100% { transform: scaleY(0); transform-origin: bottom; }
}

@keyframes lp-dash {
    from { stroke-dashoffset: 194; }
    to { stroke-dashoffset: 0; }
}

@keyframes lp-node-float {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-6px); }
}

@keyframes lp-ring {
    0% { transform: scale(0.4); opacity: 0.9; }
    100% { transform: scale(2.4); opacity: 0; }
}

@keyframes lp-spin {
    to { transform: rotate(360deg); }
}

@keyframes lp-blink {
    to { visibility: hidden; }
}

/* --------------------------------------------------------------------------
   Reduced motion
   -------------------------------------------------------------------------- */

@media (prefers-reduced-motion: reduce) {
    .landing-page *,
    .landing-page *::before,
    .landing-page *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
    }

    .landing-page [data-reveal] {
        opacity: 1;
        transform: none;
    }
}
`, "",{"version":3,"sources":["webpack://./src/landing.css"],"names":[],"mappings":"AAAA;;;+EAG+E;;AAE/E;+CAC+C;AAC/C;IACI,YAAY;IACZ,iBAAiB;IACjB,mBAAmB;IACnB,mBAAmB;IACnB,kBAAkB;IAClB,uBAAuB;IACvB,mBAAmB;IACnB,iCAAiC;IACjC,mBAAmB;IACnB,qBAAqB;IACrB,wBAAwB;IACxB,oBAAoB;IACpB,qBAAqB;IACrB,qBAAqB;IACrB,oBAAoB;IACpB,4DAA4D;IAC5D,6EAA6E;IAC7E,gBAAgB;IAChB,qBAAqB;;IAErB,kBAAkB;IAClB,iBAAiB;IACjB,oBAAoB;IACpB,qDAAqD;IACrD,wDAAwD;IACxD,6CAA6C;;IAE7C,SAAS;IACT,qFAAqF;IACrF,gGAAgG;AACpG;;AAEA;IACI,2BAA2B;IAC3B,qBAAqB;IACrB,eAAe;IACf,iBAAiB;IACjB,6BAA6B;IAC7B,gBAAgB;AACpB;;AAEA;;;IAGI,sBAAsB;AAC1B;;AAEA;;;;IAII,mCAAmC;IACnC,oBAAoB;IACpB,oBAAoB;IACpB,uBAAuB;IACvB,SAAS;AACb;;AAEA;IACI,kBAAkB;IAClB,SAAS;AACb;;AAEA;IACI,WAAW;IACX,oBAAoB;IACpB,kBAAkB;IAClB,oBAAoB;AACxB;;AAEA,8EAA8E;AAC9E;IACI,WAAW;IACX,YAAY;IACZ,UAAU;IACV,SAAS;IACT,gBAAgB;IAChB,gBAAgB;IAChB,gBAAgB;IAChB,kBAAkB;IAClB,cAAc;AAClB;;AAEA,yEAAyE;AACzE,0BAA0B,mBAAmB,EAAE;AAC/C,+BAA+B,mBAAmB,EAAE;AACpD,gCAAgC,SAAS,EAAE;;AAE3C;IACI,sBAAsB;IACtB,qBAAqB;IACrB,2BAA2B;AAC/B;;AAEA;IACI,wBAAwB;AAC5B;;AAEA;IACI,eAAe;AACnB;;AAEA;IACI,oCAAoC;AACxC;;AAEA;;+EAE+E;;AAE/E;IACI,WAAW;IACX,iBAAiB;IACjB,cAAc;IACd,eAAe;AACnB;;AAEA;IACI,gBAAgB;AACpB;;AAEA;IACI,kBAAkB;IAClB,kCAAkC;AACtC;;AAEA;IACI,wBAAwB;AAC5B;;AAEA;IACI;;;4FAGwF;IACxF,gCAAgC;AACpC;;AAEA;;;IAGI,WAAW;AACf;;AAEA;IACI,cAAc;AAClB;;AAEA;IACI,WAAW;AACf;;AAEA,iDAAiD;AACjD;IACI,WAAW;IACX,kBAAkB;IAClB,QAAQ;IACR,8EAA8E;IAC9E,0BAA0B;IAC1B,4EAA4E;IAC5E,oFAAoF;IACpF,oBAAoB;AACxB;;AAEA;IACI,kBAAkB;IAClB,UAAU;AACd;;AAEA;IACI,aAAa;IACb,SAAS;AACb;;AAEA,cAAc,gDAAgD,EAAE;AAChE,cAAc,gDAAgD,EAAE;AAChE,cAAc,gDAAgD,EAAE;;AAEhE;IACI,cAAc,gDAAgD,EAAE;IAChE,cAAc,gDAAgD,EAAE;AACpE;;AAEA;IACI;;kBAEc,qCAAqC,EAAE;AACzD;;AAEA;;+EAE+E;;AAE/E;IACI,gBAAgB;IAChB,qCAAqC;AACzC;;AAEA;IACI,iBAAiB;IACjB,kBAAkB;IAClB,kBAAkB;AACtB;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,iBAAiB;IACjB,oBAAoB;IACpB,gCAAgC;IAChC,wBAAwB;IACxB,eAAe;IACf,gBAAgB;IAChB,sBAAsB;IACtB,yBAAyB;IACzB,mBAAmB;AACvB;;AAEA;IACI,WAAW;IACX,UAAU;IACV,WAAW;IACX,kBAAkB;IAClB,0BAA0B;AAC9B;;AAEA;IACI,oCAAoC;IACpC,cAAc;IACd,2CAA2C;AAC/C;;AAEA;IACI,mCAAmC;IACnC,gBAAgB;IAChB,gBAAgB;IAChB,mBAAmB;AACvB;;AAEA;IACI,kBAAkB;IAClB,0BAA0B;IAC1B,6BAA6B;IAC7B,qBAAqB;IACrB,kBAAkB;AACtB;;AAEA;IACI,mCAAmC;IACnC,gBAAgB;IAChB,0BAA0B;AAC9B;;AAEA;IACI,gCAAgC;AACpC;;AAEA;;+EAE+E;;AAE/E;IACI,kBAAkB;IAClB,6BAA6B;IAC7B,gCAAgC;IAChC,+BAA+B;IAC/B,aAAa;IACb,4BAA4B;IAC5B,0GAA0G;IAC1G,aAAa;IACb,sBAAsB;IACtB,SAAS;IACT,gBAAgB;AACpB;;AAEA;IACI,2BAA2B;IAC3B,+BAA+B;IAC/B,sCAAsC;AAC1C;;AAEA;IACI,eAAe;IACf,4BAA4B;IAC5B,4BAA4B;AAChC;;AAEA,uCAAuC;AACvC;IACI,WAAW;IACX,kBAAkB;IAClB,MAAM;IACN,OAAO;IACP,QAAQ;IACR,WAAW;IACX,0BAA0B;IAC1B,oBAAoB;IACpB,sBAAsB;IACtB,yDAAyD;AAC7D;;AAEA;IACI,oBAAoB;AACxB;;AAEA;IACI,qCAAqC;IACrC,uCAAuC;IACvC,gBAAgB;IAChB,2BAA2B;IAC3B,mCAAmC;AACvC;;AAEA;IACI,qCAAqC;IACrC,uCAAuC;IACvC,sDAAsD;AAC1D;;AAEA;IACI,eAAe;IACf,gBAAgB;IAChB,gBAAgB;AACpB;;AAEA;IACI,0BAA0B;IAC1B,iBAAiB;IACjB,gBAAgB;AACpB;;AAEA;IACI,gCAAgC;AACpC;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,WAAW;IACX,YAAY;IACZ,mBAAmB;IACnB,0BAA0B;IAC1B,WAAW;IACX,eAAe;IACf,qDAAqD;IACrD,cAAc;IACd,0DAA0D;AAC9D;;AAEA;IACI,oCAAoC;AACxC;;AAEA;IACI,gCAAgC;IAChC,wBAAwB;IACxB,gBAAgB;AACpB;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,iBAAiB;IACjB,oBAAoB;IACpB,gCAAgC;IAChC,0CAA0C;IAC1C,wBAAwB;IACxB,gBAAgB;IAChB,eAAe;IACf,sEAAsE;AAC1E;;AAEA;IACI,6BAA6B;IAC7B,WAAW;IACX,2BAA2B;AAC/B;;AAEA;IACI,qBAAqB;IACrB,iBAAiB;IACjB,kBAAkB;IAClB,gCAAgC;IAChC,wBAAwB;IACxB,eAAe;IACf,gBAAgB;IAChB,sBAAsB;IACtB,yBAAyB;AAC7B;;AAEA;;+EAE+E;;AAE/E;IACI,kBAAkB;IAClB,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,SAAS;IACT,kBAAkB;IAClB,oBAAoB;IACpB,2BAA2B;IAC3B,eAAe;IACf,gBAAgB;IAChB,gBAAgB;IAChB,kBAAkB;IAClB,qBAAqB;IACrB,6BAA6B;IAC7B,eAAe;IACf,0HAA0H;IAC1H,gBAAgB;IAChB,eAAe;AACnB;;AAEA;IACI,aAAa;IACb,0BAA0B;AAC9B;;AAEA;IACI,gCAAgC;AACpC;;AAEA;IACI,0BAA0B;AAC9B;;AAEA;IACI,0BAA0B;IAC1B,WAAW;IACX,sDAAsD;AAC1D;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,MAAM;IACN,UAAU;IACV,UAAU;IACV,YAAY;IACZ,oGAAoG;IACpG,wBAAwB;IACxB,0BAA0B;AAC9B;;AAEA;IACI,WAAW;IACX,2BAA2B;IAC3B,sDAAsD;AAC1D;;AAEA;IACI,UAAU;AACd;;AAEA;IACI,qCAAqC;IACrC,WAAW;IACX,uCAAuC;IACvC,0BAA0B;IAC1B,kCAAkC;AACtC;;AAEA;IACI,qCAAqC;IACrC,sCAAsC;IACtC,WAAW;IACX,2BAA2B;AAC/B;;AAEA;IACI,gBAAgB;IAChB,wBAAwB;IACxB,qCAAqC;AACzC;;AAEA;IACI,gCAAgC;IAChC,wBAAwB;IACxB,2BAA2B;AAC/B;;AAEA;IACI,kBAAkB;IAClB,eAAe;AACnB;;AAEA;IACI,WAAW;AACf;;AAEA;IACI,YAAY;IACZ,YAAY;AAChB;;AAEA;;+EAE+E;;AAE/E;IACI,UAAU;IACV,2BAA2B;IAC3B,sGAAsG;IACtG,0CAA0C;IAC1C,+BAA+B;AACnC;;AAEA,qCAAqC,4BAA4B,EAAE;AACnE,sCAAsC,2BAA2B,EAAE;AACnE,sCAAsC,sBAAsB,EAAE;;AAE9D;IACI,UAAU;IACV,eAAe;AACnB;;AAEA;;+EAE+E;;AAE/E;IACI,eAAe;IACf,MAAM;IACN,OAAO;IACP,WAAW;IACX,aAAa;IACb,YAAY;IACZ,aAAa;IACb,mBAAmB;IACnB,uBAAuB;IACvB,oCAAoC;IACpC,oGAAoG;AACxG;;AAEA;IACI,YAAY;IACZ,qCAAqC;IACrC,0CAA0C;IAC1C,kDAAkD;IAClD,2CAA2C;IAC3C,oDAAoD;AACxD;;AAEA;IACI,WAAW;IACX,iBAAiB;IACjB,cAAc;IACd,eAAe;IACf,aAAa;IACb,mBAAmB;IACnB,8BAA8B;IAC9B,SAAS;IACT,YAAY;AAChB;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,SAAS;IACT,eAAe;IACf,cAAc;IACd,WAAW;IACX,4BAA4B;AAChC;;AAEA;IACI,oBAAoB;AACxB;;AAEA;IACI,YAAY;IACZ,WAAW;IACX,mBAAmB;IACnB,iDAAiD;AACrD;;AAEA;IACI,mCAAmC;IACnC,eAAe;IACf,gBAAgB;IAChB,uBAAuB;AAC3B;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,gBAAgB;IAChB,SAAS;IACT,UAAU;IACV,QAAQ;AACZ;;AAEA;IACI,kBAAkB;IAClB,qBAAqB;IACrB,iBAAiB;IACjB,eAAe;IACf,gBAAgB;IAChB,gCAAgC;IAChC,mBAAmB;IACnB,mBAAmB;IACnB,iDAAiD;AACrD;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,UAAU;IACV,WAAW;IACX,WAAW;IACX,WAAW;IACX,kBAAkB;IAClB,0BAA0B;IAC1B,oBAAoB;IACpB,sBAAsB;IACtB,0DAA0D;AAC9D;;AAEA;;IAEI,WAAW;AACf;;AAEA;;IAEI,oBAAoB;AACxB;;AAEA;IACI,0BAA0B;AAC9B;;AAEA;;IAEI,oBAAoB;AACxB;;AAEA;IACI,kBAAkB;IAClB,eAAe;IACf,iBAAiB;IACjB,mBAAmB;AACvB;;AAEA,8DAA8D;AAC9D;IACI,kBAAkB;IAClB,gBAAgB;AACpB;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,kBAAkB;IAClB,oBAAoB;IACpB,2CAA2C;IAC3C,qCAAqC;IACrC,WAAW;IACX,oBAAoB;IACpB,eAAe;IACf,gBAAgB;IAChB,eAAe;IACf,2EAA2E;AAC/E;;AAEA;;IAEI,qCAAqC;AACzC;;AAEA;IACI,oCAAoC;IACpC,mBAAmB;IACnB,oBAAoB;AACxB;;AAEA;;IAEI,gCAAgC;IAChC,wBAAwB;AAC5B;;AAEA;IACI,kBAAkB;IAClB,sBAAsB;IACtB,QAAQ;IACR,YAAY;IACZ,aAAa;IACb,gBAAgB;IAChB,mBAAmB;IACnB,yBAAyB;IACzB,oDAAoD;IACpD,qDAAqD;IACrD,aAAa;AACjB;;AAEA;IACI,mCAAmC;IACnC,eAAe;IACf,gBAAgB;IAChB,oBAAoB;IACpB,mBAAmB;AACvB;;AAEA;IACI,sBAAsB;IACtB,SAAS;AACb;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,eAAe;IACf,mBAAmB;IACnB,wDAAwD;AAC5D;;AAEA;IACI,aAAa;IACb,6BAA6B;IAC7B,0BAA0B;AAC9B;;AAEA;IACI,WAAW;IACX,aAAa;IACb,eAAe;IACf,mBAAmB;IACnB,0BAA0B;AAC9B;;AAEA;IACI,gBAAgB;IAChB,eAAe;AACnB;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,kBAAkB;IAClB,eAAe;IACf,gBAAgB;IAChB,0BAA0B;IAC1B,uBAAuB;IACvB,YAAY;IACZ,mBAAmB;IACnB,eAAe;IACf,oBAAoB;IACpB,iDAAiD;AACrD;;AAEA;IACI,gCAAgC;IAChC,wBAAwB;AAC5B;;AAEA;IACI,sBAAsB;AAC1B;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,SAAS;IACT,yBAAyB;IACzB,mBAAmB;IACnB,oBAAoB;IACpB,yBAAyB;IACzB,eAAe;IACf,yBAAiB;YAAjB,iBAAiB;IACjB,gCAAgC;AACpC;;AAEA;IACI,0BAA0B;AAC9B;;AAEA;IACI,kBAAkB;IAClB,sBAAsB;IACtB,QAAQ;IACR,YAAY;IACZ,gBAAgB;IAChB,mBAAmB;IACnB,mDAAmD;IACnD,yBAAyB;IACzB,YAAY;IACZ,aAAa;IACb,qDAAqD;AACzD;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,uBAAuB;IACvB,WAAW;IACX,YAAY;IACZ,mBAAmB;IACnB,2CAA2C;IAC3C,oCAAoC;IACpC,WAAW;IACX,eAAe;IACf,kDAAkD;IAClD,wCAAwC;AAC5C;;AAEA;IACI,yBAAyB;IACzB,2BAA2B;IAC3B,WAAW;AACf;;AAEA;IACI,iBAAiB,aAAa,EAAE;IAChC,kBAAkB,oBAAoB,EAAE;AAC5C;;AAEA;;+EAE+E;;AAE/E;IACI,eAAe;IACf,QAAQ;IACR,iCAAiC;IACjC,2BAA2B;IAC3B,mCAAmC;IACnC,aAAa;IACb,UAAU;IACV,kBAAkB;IAClB,qDAAqD;IACrD,kBAAkB;AACtB;;AAEA;IACI,UAAU;IACV,mBAAmB;AACvB;;AAEA;IACI,eAAe;IACf,MAAM;IACN,QAAQ;IACR,UAAU;IACV,gBAAgB;IAChB,cAAc;IACd,gBAAgB;IAChB,aAAa;IACb,aAAa;IACb,sBAAsB;IACtB,2BAA2B;IAC3B,yDAAyD;IACzD,8CAA8C;IAC9C,gBAAgB;IAChB,2BAA2B;AAC/B;;AAEA;IACI,wBAAwB;AAC5B;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,8BAA8B;IAC9B,kBAAkB;IAClB,uCAAuC;AAC3C;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,SAAS;IACT,mCAAmC;IACnC,gBAAgB;IAChB,eAAe;IACf,oBAAoB;AACxB;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,mBAAmB;AACvB;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,mBAAmB;IACnB,gCAAgC;IAChC,mBAAmB;IACnB,oBAAoB;IACpB,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,eAAe;AACnB;;AAEA;IACI,aAAa;IACb,aAAa;IACb,sBAAsB;IACtB,SAAS;IACT,SAAO;AACX;;AAEA;IACI,cAAc;IACd,eAAe;IACf,gBAAgB;IAChB,sBAAsB;IACtB,yBAAyB;IACzB,wBAAwB;AAC5B;;AAEA;IACI,gBAAgB;IAChB,SAAS;IACT,UAAU;IACV,aAAa;IACb,QAAQ;AACZ;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,SAAS;IACT,kBAAkB;IAClB,mBAAmB;IACnB,eAAe;IACf,gBAAgB;IAChB,oBAAoB;IACpB,mBAAmB;IACnB,6BAA6B;IAC7B,yEAAyE;AAC7E;;AAEA;IACI,iBAAiB;IACjB,aAAa;IACb,kDAAkD;AACtD;;AAEA;;IAEI,gCAAgC;IAChC,wBAAwB;IACxB,sCAAsC;AAC1C;;AAEA;IACI,UAAU;IACV,0BAA0B;AAC9B;;AAEA;IACI,0BAA0B;IAC1B,WAAW;IACX,qDAAqD;AACzD;;AAEA;IACI,UAAU;AACd;;AAEA;IACI,sBAAsB;AAC1B;;AAEA;IACI,gBAAgB;AACpB;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,wBAAwB;IACxB,gCAAgC;IAChC,aAAa;IACb,SAAS;AACb;;AAEA;IACI,sBAAsB;AAC1B;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,eAAe;IACf,mBAAmB;AACvB;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,yBAAyB;IACzB,eAAe;AACnB;;AAEA;IACI,gBAAgB;IAChB,eAAe;AACnB;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,wBAAwB;IACxB,gCAAgC;IAChC,kBAAkB;AACtB;;AAEA;IACI,mBAAmB;IACnB,qBAAqB;AACzB;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,eAAe;IACf,gBAAgB;IAChB,sBAAsB;IACtB,yBAAyB;IACzB,sBAAsB;IACtB,kBAAkB;AACtB;;AAEA;IACI,cAAc;AAClB;;AAEA;IACI,gBAAgB;IAChB,oBAAoB;IACpB,qBAAqB;IACrB,gBAAgB;AACpB;;AAEA;IACI,WAAW;IACX,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,QAAQ;IACR,aAAa;IACb,mBAAmB;IACnB,YAAY;IACZ,mBAAmB;IACnB,WAAW;IACX,oBAAoB;IACpB,gBAAgB;IAChB,eAAe;AACnB;;AAEA,wBAAwB;AACxB;IACI,eAAe;IACf,MAAM;IACN,OAAO;IACP,WAAW;IACX,WAAW;IACX,aAAa;IACb,uBAAuB;IACvB,oBAAoB;AACxB;;AAEA;IACI,YAAY;IACZ,WAAW;IACX,0BAA0B;IAC1B,sBAAsB;IACtB,oBAAoB;AACxB;;AAEA,gBAAgB;AAChB;IACI,eAAe;IACf,WAAW;IACX,YAAY;IACZ,aAAa;IACb,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,YAAY;IACZ,yBAAyB;IACzB,WAAW;IACX,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,mDAAmD;IACnD,eAAe;IACf,UAAU;IACV,sCAAsC;IACtC,oBAAoB;IACpB,kGAAkG;AACtG;;AAEA;IACI,UAAU;IACV,eAAe;IACf,oBAAoB;AACxB;;AAEA;IACI,6BAA6B;AACjC;;AAEA;;+EAE+E;;AAE/E;IACI,kBAAkB;IAClB,kBAAkB;IAClB,aAAa;IACb,mBAAmB;IACnB,sBAAsB;IACtB,WAAW;IACX;;;sEAGkE;IAClE,gBAAgB;IAChB,kBAAkB;AACtB;;AAEA,iBAAiB;AACjB;IACI,kBAAkB;IAClB,QAAQ;IACR;;8EAE0E;IAC1E,0BAA0B;IAC1B,mFAAmF;IACnF,2FAA2F;IAC3F,WAAW;IACX,4CAA4C;AAChD;;AAEA,iBAAiB;AACjB;IACI,kBAAkB;IAClB,kBAAkB;IAClB,kBAAkB;IAClB,aAAa;IACb,WAAW;IACX,4CAA4C;AAChD;;AAEA;IACI,YAAY;IACZ,aAAa;IACb,WAAW;IACX,YAAY;IACZ,mBAAmB;AACvB;;AAEA;IACI,YAAY;IACZ,aAAa;IACb,cAAc;IACd,YAAY;IACZ,mBAAmB;IACnB,oBAAoB;AACxB;;AAEA;IACI,YAAY;IACZ,aAAa;IACb,QAAQ;IACR,SAAS;IACT,mBAAmB;IACnB,aAAa;IACb,qBAAqB;AACzB;;AAEA;IACI,aAAa;IACb,0DAA0D;IAC1D,SAAS;IACT,mBAAmB;AACvB;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,SAAS;IACT,0BAA0B;IAC1B,oBAAoB;IACpB,qCAAqC;IACrC,2CAA2C;IAC3C,cAAc;IACd,eAAe;IACf,gBAAgB;IAChB,sBAAsB;IACtB,mBAAmB;IACnB,0BAA0B;IAC1B,kCAAkC;AACtC;;AAEA;IACI,kBAAkB;IAClB,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,4BAA4B;IAC5B,cAAc;AAClB;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,WAAW;IACX,kBAAkB;IAClB,kCAAkC;IAClC,0CAA0C;AAC9C;;AAEA;IACI,mCAAmC;IACnC,gBAAgB;IAChB,iBAAiB;IACjB,uBAAuB;IACvB,WAAW;IACX,mBAAmB;IACnB,uBAAuB;AAC3B;;AAEA;IACI,kBAAkB;IAClB,+BAA+B;IAC/B,6BAA6B;IAC7B,qBAAqB;IACrB,kBAAkB;AACtB;;AAEA;IACI,mCAAmC;IACnC,gBAAgB;IAChB,+BAA+B;IAC/B,gBAAgB;IAChB,mBAAmB;AACvB;;AAEA;IACI,aAAa;IACb,eAAe;IACf,SAAS;IACT,gBAAgB;IAChB,UAAU;IACV,gBAAgB;AACpB;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,iBAAiB;IACjB,oBAAoB;IACpB,qCAAqC;IACrC,2CAA2C;IAC3C,WAAW;IACX,eAAe;IACf,gBAAgB;IAChB,0BAA0B;IAC1B,kCAAkC;AACtC;;AAEA;IACI,uBAAuB;IACvB,cAAc;AAClB;;AAEA;IACI,aAAa;IACb,eAAe;IACf,SAAS;AACb;;AAEA;IACI,kBAAkB;IAClB,aAAa;IACb,uBAAuB;IACvB,gEAAgE;AACpE;;AAEA;IACI,WAAW;IACX,gBAAgB;IAChB,YAAY;IACZ,oDAAoD;AACxD;;AAEA;IACI,kBAAkB;IAClB,SAAS;IACT,YAAY;IACZ,2BAA2B;IAC3B,aAAa;IACb,sBAAsB;IACtB,mBAAmB;IACnB,QAAQ;IACR,gCAAgC;IAChC,eAAe;IACf,qBAAqB;IACrB,yBAAyB;IACzB,qBAAqB;AACzB;;AAEA;IACI,WAAW;AACf;;AAEA;IACI,UAAU;IACV,YAAY;IACZ,6EAA6E;IAC7E,iDAAiD;AACrD;;AAEA,4CAA4C;AAC5C;IACI,2DAA2D;AAC/D;;AAEA,iCAAiC,sBAAsB,EAAE;AACzD,iCAAiC,sBAAsB,EAAE;AACzD,iCAAiC,sBAAsB,EAAE;AACzD,iCAAiC,sBAAsB,EAAE;AACzD,iCAAiC,sBAAsB,EAAE;;AAEzD,kCAAkC;AAClC;IACI,iCAAiC;IACjC,iBAAiB;IACjB,UAAU;AACd;;AAEA;IACI,yBAAyB;IACzB,iBAAiB;IACjB,UAAU;IACV,wBAAwB;IACxB,qBAAqB;IACrB,uCAAuC;AAC3C;;AAEA;IACI,gDAAgD;AACpD;;AAEA,+BAA+B,oBAAoB,EAAE;AACrD,+BAA+B,oBAAoB,EAAE;AACrD,+BAA+B,oBAAoB,EAAE;AACrD,+BAA+B,oBAAoB,EAAE;AACrD,+BAA+B,oBAAoB,EAAE;;AAErD;IACI,UAAU;IACV,gCAAgC;IAChC,iBAAiB;IACjB,6BAA6B;IAC7B,uCAAuC;AAC3C;;AAEA,mBAAmB,oBAAoB,EAAE;;AAEzC;IACI,2BAA2B;IAC3B,eAAe;IACf,gBAAgB;IAChB,UAAU;IACV,mBAAmB;AACvB;;AAEA;IACI,2BAA2B;IAC3B,gBAAgB;IAChB,gBAAgB;IAChB,8BAA8B;IAC9B,mBAAmB;AACvB;;AAEA;IACI;QACI,qCAAqC;QACrC,kBAAkB;IACtB;;IAEA;QACI,iBAAiB;QACjB,kBAAkB;IACtB;;IAEA;;QAEI,uBAAuB;IAC3B;;IAEA;QACI,SAAS;IACb;;IAEA;QACI,gBAAgB;IACpB;;IAEA;QACI,kBAAkB;IACtB;AACJ;;AAEA;IACI;QACI,aAAa;IACjB;;IAEA;QACI,WAAW;IACf;;IAEA;QACI,mBAAmB;QACnB,gBAAgB;IACpB;;IAEA;QACI,kBAAkB;IACtB;AACJ;;AAEA;;+EAE+E;;AAE/E;IACI,SAAS;AACb;;AAEA;IACI,kBAAkB;IAClB,SAAS;IACT,WAAW;IACX,mCAAmC;IACnC,eAAe;IACf,gBAAgB;IAChB,cAAc;IACd,6BAA6B;IAC7B,2BAA2B;AAC/B;;AAEA;IACI,+BAA+B;AACnC;;AAEA,4CAA4C;AAC5C;IACI,qCAAqC;IACrC,gBAAgB;IAChB,mBAAmB;IACnB,mBAAmB;IACnB,+BAA+B;IAC/B,gBAAgB;IAChB,wCAAwC;AAC5C;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,QAAQ;IACR,kBAAkB;IAClB,mBAAmB;IACnB,kDAAkD;AACtD;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,mBAAmB;AACvB;;AAEA,8BAA8B,mBAAmB,EAAE;AACnD,8BAA8B,mBAAmB,EAAE;;AAEnD;IACI,iBAAiB;IACjB,SAAO;IACP,YAAY;IACZ,kBAAkB;IAClB,qCAAqC;IACrC,+BAA+B;IAC/B,eAAe;IACf,aAAa;IACb,mBAAmB;IACnB,eAAe;IACf,sBAAsB;AAC1B;;AAEA;IACI,cAAc;IACd,WAAW;IACX,YAAY;IACZ,yDAAyD;AAC7D;;AAEA;IACI,sBAAsB;AAC1B;;AAEA;IACI,aAAa;IACb,uBAAuB;IACvB,eAAe;IACf,SAAS;IACT,gBAAgB;AACpB;;AAEA;;+EAE+E;;AAE/E;IACI,cAAc;IACd,mBAAmB;AACvB;;AAEA;IACI,cAAc;IACd,eAAe;IACf,gBAAgB;IAChB,0BAA0B;IAC1B,kBAAkB;IAClB,sBAAsB;AAC1B;;AAEA;;IAEI,WAAW;IACX,kBAAkB;IAClB,mBAAmB;IACnB,yBAAyB;IACzB,gBAAgB;IAChB,2BAA2B;IAC3B,eAAe;IACf,qBAAqB;IACrB,wDAAwD;AAC5D;;AAEA;IACI,gBAAgB;IAChB,iBAAiB;IACjB,gBAAgB;AACpB;;AAEA;;IAEI,aAAa;IACb,6BAA6B;IAC7B,0BAA0B;AAC9B;;AAEA;IACI,qCAAqC;IACrC,uCAAuC;IACvC,WAAW;AACf;;AAEA;IACI,qBAAqB;IACrB,8CAA8C;AAClD;;AAEA;IACI,gCAAgC;AACpC;;AAEA;;+EAE+E;;AAE/E;IACI,aAAa;IACb,oDAAoD;IACpD,2BAA2B;IAC3B,mBAAmB;AACvB;;AAEA;IACI,YAAY,qCAAqC,EAAE;AACvD;;AAEA;IACI,kBAAkB;IAClB,aAAa;AACjB;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,QAAQ;IACR,mBAAmB;IACnB,0BAA0B;IAC1B,aAAa;IACb,wBAAwB;AAC5B;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,WAAW;IACX,mBAAmB;IACnB,2CAA2C;IAC3C,uBAAuB;AAC3B;;AAEA;IACI,kBAAkB;IAClB,UAAU;IACV,cAAc;IACd,WAAW;IACX,mBAAmB;IACnB,+BAA+B;IAC/B,gBAAgB;AACpB;;AAEA;IACI,kBAAkB;IAClB,UAAU;IACV,aAAa;IACb,mBAAmB;IACnB,SAAS;IACT,kBAAkB;IAClB,gBAAgB;IAChB,mBAAmB;IACnB,+BAA+B;IAC/B,gCAAgC;IAChC,8CAA8C;AAClD;;AAEA,sBAAsB,YAAY,EAAE,WAAW,EAAE;AACjD,sBAAsB,SAAS,EAAE,YAAY,EAAE,oBAAoB,EAAE;;AAErE;IACI,cAAc;IACd,mCAAmC;IACnC,eAAe;IACf,oBAAoB;IACpB,gBAAgB;AACpB;;AAEA;IACI,eAAe;IACf,sBAAsB;AAC1B;;AAEA;IACI,mBAAmB,gBAAgB,EAAE,gBAAgB,EAAE,eAAe,EAAE;AAC5E;;AAEA;IACI,eAAe;IACf,iBAAiB;IACjB,0BAA0B;AAC9B;;AAEA;IACI,eAAe;IACf,gBAAgB;IAChB,mBAAmB;AACvB;;AAEA;IACI,aAAa;IACb,gDAAgD;IAChD,cAAc;IACd,gBAAgB;IAChB,SAAS;IACT,UAAU;AACd;;AAEA;IACI,aAAa,qCAAqC,EAAE;AACxD;;AAEA;IACI,aAAa;IACb,SAAS;IACT,uBAAuB;IACvB,iBAAiB;IACjB,gBAAgB;IAChB,qBAAqB;IACrB,kBAAkB;IAClB,mBAAmB;IACnB,gCAAgC;AACpC;;AAEA;IACI,gCAAgC;AACpC;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,0BAA0B;IAC1B,WAAW;IACX,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,cAAc;IACd,eAAe;AACnB;;AAEA;;+EAE+E;;AAE/E;IACI,SAAS;AACb;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,mBAAmB;IACnB,qCAAqC;IACrC,2CAA2C;IAC3C,cAAc;IACd,eAAe;IACf,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,sEAAsE;AAC1E;;AAEA;IACI,0BAA0B;IAC1B,WAAW;IACX,oCAAoC;AACxC;;AAEA;IACI,aAAa;IACb,gDAAgD;IAChD,SAAS;IACT,mBAAmB;IACnB,kCAAkC;IAClC,+BAA+B;IAC/B,mBAAmB;IACnB,qCAAqC;IACrC,0CAA0C;AAC9C;;AAEA;IACI,YAAY,qCAAqC,EAAE;AACvD;;AAEA;IACI,mBAAmB;AACvB;;AAEA;IACI,mCAAmC;IACnC,gBAAgB;IAChB,mBAAmB;AACvB;;AAEA;IACI,eAAe;IACf,gBAAgB;IAChB,cAAc;IACd,mBAAmB;AACvB;;AAEA;IACI,gCAAgC;IAChC,iBAAiB;IACjB,eAAe;AACnB;;AAEA;IACI,kBAAkB;IAClB,cAAc;IACd,mBAAmB;IACnB,gBAAgB;IAChB,2CAA2C;IAC3C,gDAAgD;IAChD,eAAe;IACf,gBAAgB;IAChB,UAAU;IACV,WAAW;AACf;;AAEA;IACI,cAAc;IACd,WAAW;IACX,yDAAyD;AAC7D;;AAEA;IACI,sBAAsB;AAC1B;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,QAAQ;IACR,4EAA4E;IAC5E,UAAU;IACV,6BAA6B;AACjC;;AAEA;IACI,UAAU;AACd;;AAEA;IACI,kBAAkB;IAClB,WAAW;IACX,YAAY;IACZ,UAAU;IACV,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,iBAAiB;IACjB,kBAAkB;IAClB,qCAAqC;IACrC,WAAW;IACX,eAAe;IACf,gBAAgB;IAChB,0BAA0B;IAC1B,UAAU;IACV,0BAA0B;IAC1B,kDAAkD;AACtD;;AAEA;IACI,UAAU;IACV,eAAe;AACnB;;AAEA;;+EAE+E;;AAE/E;IACI,SAAS;IACT,iBAAiB;AACrB;;AAEA;IACI,kBAAkB;IAClB,SAAS;IACT,WAAW;IACX,mCAAmC;IACnC,eAAe;IACf,gBAAgB;IAChB,cAAc;IACd,kBAAkB;IAClB,iDAAiD;IACjD,oBAAoB;IACpB,+CAA+C;AACnD;;AAEA;IACI,iDAAiD;AACrD;;AAEA;IACI,gBAAgB;IAChB,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,gBAAgB;IAChB,wBAAwB;AAC5B;;AAEA;IACI,+BAA+B;AACnC;;AAEA;IACI,0BAA0B;AAC9B;;AAEA;;+EAE+E;;AAE/E;IACI,kBAAkB;IAClB,aAAa;IACb,gDAAgD;IAChD,SAAS;IACT,qCAAqC;AACzC;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,QAAQ;IACR,SAAS;IACT,SAAS;IACT,WAAW;IACX,6GAA6G;AACjH;;AAEA;IACI,YAAY,gDAAgD,EAAE;IAC9D,oBAAoB,aAAa,EAAE;AACvC;;AAEA;IACI,YAAY,qCAAqC,EAAE;AACvD;;AAEA;IACI,kBAAkB;IAClB,gBAAgB;AACpB;;AAEA;IACI,kBAAkB;IAClB,UAAU;IACV,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,0BAA0B;IAC1B,WAAW;IACX,mCAAmC;IACnC,eAAe;IACf,gBAAgB;IAChB,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,qEAAqE;IACrE,mBAAmB;AACvB;;AAEA;IACI,6EAA6E;AACjF;;AAEA;IACI,eAAe;IACf,gBAAgB;IAChB,kBAAkB;AACtB;;AAEA;IACI,0BAA0B;IAC1B,eAAe;IACf,gBAAgB;AACpB;;AAEA;IACI,gBAAgB;IAChB,cAAc;AAClB;;AAEA;IACI,kBAAkB;IAClB,mCAAmC;IACnC,gBAAgB;IAChB,mBAAmB;AACvB;;AAEA;IACI,gBAAgB;IAChB,+BAA+B;IAC/B,gCAAgC;IAChC,4BAA4B;IAC5B,gBAAgB;AACpB;;AAEA;IACI,aAAa;IACb,sDAAsD;IACtD,SAAS;IACT,mBAAmB;IACnB,kBAAkB;IAClB,uCAAuC;IACvC,eAAe;IACf,gCAAgC;AACpC;;AAEA;IACI,mBAAmB;AACvB;;AAEA;IACI,wBAAwB;AAC5B;;AAEA;IACI,sFAAsF;AAC1F;;AAEA;IACI,sFAAsF;AAC1F;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,SAAS;IACT,gBAAgB;IAChB,qBAAqB;AACzB;;AAEA;IACI,wBAAwB;IACxB,cAAc;AAClB;;AAEA;IACI,mCAAmC;IACnC,gBAAgB;IAChB,oBAAoB;IACpB,iBAAiB;AACrB;;AAEA;IACI,0BAA0B;IAC1B,6BAA6B;IAC7B,qBAAqB;IACrB,kBAAkB;IAClB,yBAAyB;IACzB,sBAAsB;AAC1B;;AAEA;IACI,iBAAiB,qCAAqC,EAAE,QAAQ,EAAE;IAClE,mBAAmB,gBAAgB,EAAE;AACzC;;AAEA;IACI,gBAAgB;IAChB,eAAe;IACf,sBAAsB;IACtB,gBAAgB;AACpB;;AAEA;IACI,gBAAgB;IAChB,kBAAkB;IAClB,+BAA+B;IAC/B,wBAAwB;IACxB,gCAAgC;IAChC,aAAa;IACb,0CAA0C;IAC1C,SAAS;IACT,uBAAuB;AAC3B;;AAEA;IACI,sBAAsB,qCAAqC,EAAE;AACjE;;AAEA;IACI,gBAAgB;IAChB,eAAe;IACf,kBAAkB;AACtB;;AAEA;IACI,0BAA0B;IAC1B,iBAAiB;IACjB,iBAAiB;AACrB;;AAEA;;+EAE+E;;AAE/E;IACI,aAAa;IACb,gDAAgD;IAChD,cAAc;IACd,qCAAqC;AACzC;;AAEA;IACI,uBAAuB,qCAAqC,EAAE;AAClE;;AAEA;IACI,iBAAiB;IACjB,iBAAiB;IACjB,0BAA0B;IAC1B,kBAAkB;IAClB,+CAA+C;AACnD;;AAEA;IACI,oBAAoB;AACxB;;AAEA;IACI,SAAS;AACb;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,mBAAmB;IACnB,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,mCAAmC;IACnC,eAAe;IACf,gBAAgB;IAChB,WAAW;IACX,0BAA0B;IAC1B,qDAAqD;AACzD;;AAEA;IACI,eAAe;IACf,sBAAsB;AAC1B;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,gBAAgB;IAChB,eAAe;IACf,qBAAqB;IACrB,gBAAgB;AACpB;;AAEA;IACI,cAAc;IACd,+BAA+B;AACnC;;AAEA;IACI,+BAA+B;AACnC;;AAEA;;+EAE+E;;AAE/E;IACI,SAAS;AACb;;AAEA;IACI,kBAAkB;IAClB,SAAS;IACT,WAAW;IACX,+BAA+B;IAC/B,gDAAgD;AACpD;;AAEA;IACI,+BAA+B;IAC/B,wBAAwB;AAC5B;;AAEA;IACI,aAAa;IACb,QAAQ;IACR,cAAc;AAClB;;AAEA;IACI,iBAAiB;IACjB,iBAAiB;IACjB,qBAAqB;IACrB,SAAO;AACX;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,SAAS;IACT,iBAAiB;IACjB,oCAAoC;AACxC;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,iBAAiB;IACjB,cAAc;IACd,0BAA0B;IAC1B,WAAW;IACX,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,mCAAmC;IACnC,gBAAgB;IAChB,eAAe;IACf,8DAA8D;AAClE;;AAEA;IACI,gBAAgB;IAChB,oBAAoB;IACpB,eAAe;AACnB;;AAEA;;+EAE+E;;AAE/E;IACI,aAAa;IACb,gDAAgD;IAChD,SAAS;IACT,iBAAiB;IACjB,cAAc;AAClB;;AAEA;IACI,gBAAgB,gDAAgD,EAAE;AACtE;;AAEA;IACI,gBAAgB,qCAAqC,EAAE;AAC3D;;AAEA;IACI,mBAAmB;IACnB,kBAAkB;IAClB,uBAAuB;IACvB,QAAQ;AACZ;;AAEA;IACI,kBAAkB;IAClB,YAAY;IACZ,aAAa;IACb,kBAAkB;IAClB,YAAY;IACZ,0BAA0B;IAC1B,mBAAmB;IACnB,yDAAyD;AAC7D;;AAEA;IACI,WAAW;IACX,kBAAkB;IAClB,WAAW;IACX,kBAAkB;IAClB,0CAA0C;IAC1C,sCAAsC;AAC1C;;AAEA;IACI,sBAAsB;AAC1B;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,iBAAiB;IACjB,sBAAsB;IACtB,cAAc;AAClB;;AAEA;IACI,eAAe;IACf,gBAAgB;AACpB;;AAEA;IACI,wBAAwB;IACxB,gBAAgB;IAChB,eAAe;IACf,sBAAsB;AAC1B;;AAEA;;+EAE+E;;AAE/E;IACI,SAAS;AACb;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,SAAS;IACT,eAAe;IACf,eAAe;IACf,sBAAsB;AAC1B;;AAEA;IACI,eAAe;IACf,iBAAiB;IACjB,gBAAgB;AACpB;;AAEA;IACI,oBAAoB;AACxB;;AAEA;IACI,wBAAwB;AAC5B;;AAEA;IACI,gBAAgB;IAChB,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,gBAAgB;IAChB,wBAAwB;AAC5B;;AAEA;IACI,+BAA+B;AACnC;;AAEA;IACI,0BAA0B;AAC9B;;AAEA;IACI,aAAa;IACb,eAAe;IACf,uBAAuB;IACvB,SAAS;IACT,gBAAgB;AACpB;;AAEA;;+EAE+E;;AAE/E;IACI,aAAa;IACb,oDAAoD;IACpD,SAAS;IACT,oBAAoB;AACxB;;AAEA;IACI,cAAc,qCAAqC,EAAE;AACzD;;AAEA;IACI,qBAAqB;IACrB,MAAM;AACV;;AAEA;IACI,eAAe;IACf,gBAAgB;IAChB,mBAAmB;IACnB,oBAAoB;AACxB;;AAEA;IACI,gBAAgB;IAChB,UAAU;IACV,gBAAgB;IAChB,aAAa;IACb,SAAS;AACb;;AAEA;IACI,aAAa;IACb,SAAS;IACT,uBAAuB;AAC3B;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,mBAAmB;IACnB,eAAe;AACnB;;AAEA;IACI,cAAc;IACd,eAAe;IACf,sBAAsB;IACtB,yBAAyB;IACzB,sBAAsB;IACtB,kBAAkB;AACtB;;AAEA;IACI,YAAY;AAChB;;AAEA;;IAEI,eAAe;IACf,gBAAgB;IAChB,qBAAqB;IACrB,uBAAuB;AAC3B;;AAEA;IACI,wBAAwB;AAC5B;;AAEA;IACI,aAAa;IACb,eAAe;IACf,SAAS;IACT,mBAAmB;AACvB;;AAEA;IACI,gBAAgB;IAChB,iBAAiB;IACjB,oCAAoC;AACxC;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,QAAQ;IACR,eAAe;IACf,gBAAgB;IAChB,mBAAmB;IACnB,oBAAoB;AACxB;;AAEA;IACI,aAAa;IACb,0CAA0C;IAC1C,aAAa;IACb,SAAS;IACT,iBAAiB;AACrB;;AAEA;IACI,gBAAgB;IAChB,qBAAqB;AACzB;;AAEA;IACI,SAAS;IACT,0BAA0B;AAC9B;;AAEA;IACI,UAAU;IACV,iBAAiB;AACrB;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,iBAAiB;IACjB,SAAS;IACT,cAAc;IACd,qBAAqB;AACzB;;AAEA;;+EAE+E;;AAE/E;IACI,gBAAgB;IAChB,cAAc;IACd,aAAa;IACb,SAAS;AACb;;AAEA;IACI,gBAAgB;IAChB,gCAAgC;IAChC,mBAAmB;IACnB,4BAA4B;IAC5B,gBAAgB;IAChB,0DAA0D;AAC9D;;AAEA;IACI,qCAAqC;IACrC,+BAA+B;AACnC;;AAEA;IACI,gBAAgB;IAChB,aAAa;IACb,mBAAmB;IACnB,8BAA8B;IAC9B,SAAS;IACT,kBAAkB;IAClB,eAAe;IACf,mCAAmC;IACnC,eAAe;IACf,gBAAgB;IAChB,oBAAoB;IACpB,yBAAiB;YAAjB,iBAAiB;IACjB,2BAA2B;AAC/B;;AAEA;IACI,aAAa;AACjB;;AAEA;IACI,wBAAwB;AAC5B;;AAEA;IACI,cAAc;IACd,WAAW;IACX,YAAY;IACZ,mBAAmB;IACnB,gCAAgC;IAChC,wBAAwB;IACxB,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,gGAAgG;AACpG;;AAEA;IACI,yBAAyB;IACzB,0BAA0B;IAC1B,WAAW;AACf;;AAEA;IACI,oBAAoB;IACpB,0BAA0B;IAC1B,iBAAiB;IACjB,iBAAiB;IACjB,4DAA4D;AAChE;;AAEA;;+EAE+E;;AAE/E;IACI,yBAAyB;IACzB,+BAA+B;IAC/B,oBAAoB;IACpB,iBAAiB;AACrB;;AAEA;IACI,aAAa;IACb,0CAA0C;IAC1C,SAAS;IACT,oBAAoB;IACpB,iDAAiD;AACrD;;AAEA;IACI,mBAAmB,gDAAgD,EAAE;AACzE;;AAEA;IACI,mBAAmB,qCAAqC,EAAE;AAC9D;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,SAAS;IACT,WAAW;IACX,mCAAmC;IACnC,eAAe;IACf,gBAAgB;IAChB,mBAAmB;AACvB;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,mBAAmB;AACvB;;AAEA;IACI,iBAAiB;IACjB,sBAAsB;IACtB,yBAAyB;IACzB,WAAW;IACX,mBAAmB;AACvB;;AAEA;IACI,gBAAgB;AACpB;;AAEA;IACI,gBAAgB;IAChB,SAAS;IACT,UAAU;IACV,aAAa;IACb,QAAQ;AACZ;;AAEA;IACI,gCAAgC;IAChC,mDAAmD;AACvD;;AAEA;IACI,WAAW;AACf;;AAEA;IACI,iBAAiB;AACrB;;AAEA;IACI,aAAa;IACb,eAAe;IACf,8BAA8B;IAC9B,SAAS;IACT,iBAAiB;IACjB,eAAe;IACf,+BAA+B;AACnC;;AAEA;;+EAE+E;;AAE/E;IACI,eAAe;IACf,QAAQ;IACR,aAAa;IACb,aAAa;IACb,mBAAmB;IACnB,uBAAuB;IACvB,aAAa;IACb,iCAAiC;IACjC,2BAA2B;IAC3B,mCAAmC;IACnC,gBAAgB;IAChB,6BAA6B;AACjC;;AAEA;IACI,eAAe;IACf,gBAAgB;IAChB,mBAAmB;IACnB,0CAA0C;IAC1C,mBAAmB;IACnB,sDAAsD;AAC1D;;AAEA;IACI,kBAAkB;IAClB,SAAS;IACT,WAAW;IACX,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,0CAA0C;IAC1C,oCAAoC;IACpC,WAAW;IACX,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,eAAe;AACnB;;AAEA;;+EAE+E;;AAE/E;IACI,SAAS;AACb;;AAEA;IACI,oBAAoB;AACxB;;AAEA;IACI,aAAa;IACb,uBAAuB;IACvB,SAAS;AACb;;AAEA;IACI,cAAc;AAClB;;AAEA;IACI,sBAAsB;IACtB,eAAe;IACf,gBAAgB;IAChB,eAAe;IACf,gBAAgB;AACpB;;AAEA;IACI,aAAa;IACb,eAAe;IACf,QAAQ;AACZ;;AAEA;IACI,mBAAmB;IACnB,cAAc;AAClB;;AAEA;IACI,gBAAgB;IAChB,SAAS;IACT,kBAAkB;IAClB,aAAa;IACb,QAAQ;IACR,0BAA0B;IAC1B,iBAAiB;IACjB,gBAAgB;AACpB;;AAEA;IACI,sBAAsB;IACtB,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;IACR,UAAU;IACV,YAAY;IACZ,gBAAgB;IAChB,wBAAwB;IACxB,oBAAoB;IACpB,eAAe;IACf,gBAAgB;IAChB,eAAe;AACnB;;AAEA;IACI,+BAA+B;AACnC;;AAEA;IACI,yBAAyB;AAC7B;;AAEA;IACI,wBAAwB;IACxB,gCAAgC;IAChC,kCAAkC;IAClC,aAAa;IACb,4BAA4B;AAChC;;AAEA;IACI,cAAc;IACd,iBAAiB;IACjB,qBAAqB;IACrB,yBAAyB;IACzB,gBAAgB;IAChB,sBAAsB;IACtB,kBAAkB;AACtB;;AAEA;IACI,eAAe;IACf,gBAAgB;IAChB,kBAAkB;AACtB;;AAEA;IACI,eAAe;IACf,0BAA0B;IAC1B,iBAAiB;AACrB;;AAEA;IACI,aAAa;IACb,gDAAgD;IAChD,SAAS;IACT,gBAAgB;AACpB;;AAEA;IACI,gBAAgB;IAChB,gCAAgC;IAChC,mBAAmB;IACnB,iBAAiB;IACjB,kBAAkB;AACtB;;AAEA;IACI,mCAAmC;IACnC,gBAAgB;IAChB,eAAe;IACf,oBAAoB;AACxB;;AAEA;IACI,eAAe;IACf,iBAAiB;IACjB,sBAAsB;IACtB,gBAAgB;AACpB;;AAEA;IACI,uBAAuB,qCAAqC,EAAE;AAClE;;AAEA;IACI,gBAAgB;IAChB,iBAAiB;IACjB,oCAAoC;IACpC,aAAa;IACb,sBAAsB;IACtB,QAAQ;IACR,iBAAiB;IACjB,gBAAgB;AACpB;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;AACZ;;AAEA;IACI,kCAAkC;IAClC,6BAA6B;IAC7B,gCAAgC;IAChC,+BAA+B;IAC/B,+BAA+B;IAC/B,4BAA4B;AAChC;;AAEA;IACI,gBAAgB;IAChB,kBAAkB;IAClB,aAAa;IACb,QAAQ;IACR,0BAA0B;IAC1B,eAAe;IACf,gBAAgB;AACpB;;AAEA;IACI,oBAAoB;AACxB;;AAEA;;+EAE+E;;AAE/E;IACI,iBAAiB;IACjB,cAAc;IACd,mBAAmB;IACnB,iCAAiC;IACjC,0CAA0C;IAC1C,+BAA+B;AACnC;;AAEA;IACI,gBAAgB;IAChB,gBAAgB;IAChB,UAAU;IACV,aAAa;IACb,eAAe;IACf,SAAS;AACb;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,SAAS;IACT,yBAAyB;IACzB,oBAAoB;IACpB,2CAA2C;IAC3C,gCAAgC;IAChC,iBAAiB;IACjB,gBAAgB;AACpB;;AAEA;IACI,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,qCAAqC;IACrC,eAAe;IACf,gBAAgB;AACpB;;AAEA;IACI,WAAW;IACX,sCAAsC;IACtC,qCAAqC;AACzC;;AAEA;IACI,0BAA0B;IAC1B,WAAW;AACf;;AAEA;IACI,+BAA+B;AACnC;;AAEA;IACI,6BAA6B;IAC7B,WAAW;AACf;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,SAAS;IACT,mCAAmC;IACnC,gBAAgB;IAChB,kBAAkB;AACtB;;AAEA;IACI,+BAA+B;IAC/B,iBAAiB;IACjB,gBAAgB;IAChB,mBAAmB;AACvB;;AAEA;IACI,aAAa;IACb,gDAAgD;IAChD,SAAS;AACb;;AAEA;IACI,sBAAsB,qCAAqC,EAAE;AACjE;;AAEA;IACI,kBAAkB;IAClB,aAAa;IACb,sBAAsB;IACtB,uBAAuB;IACvB,QAAQ;IACR,4BAA4B;IAC5B,mBAAmB;IACnB,2CAA2C;IAC3C,qCAAqC;IACrC,WAAW;IACX,gBAAgB;IAChB,oBAAoB;IACpB,eAAe;IACf,mGAAmG;AACvG;;AAEA;IACI,sCAAsC;IACtC,2BAA2B;AAC/B;;AAEA;IACI,qBAAqB;IACrB,qCAAqC;IACrC,+CAA+C;AACnD;;AAEA;IACI,kBAAkB;IAClB,UAAU;IACV,SAAS;IACT,WAAW;IACX,YAAY;IACZ,kBAAkB;IAClB,0CAA0C;IAC1C,oBAAoB;IACpB,mBAAmB;IACnB,uBAAuB;IACvB,kBAAkB;IAClB,yEAAyE;AAC7E;;AAEA;IACI,0BAA0B;IAC1B,yBAAyB;IACzB,WAAW;AACf;;AAEA;IACI,mCAAmC;IACnC,gBAAgB;IAChB,eAAe;AACnB;;AAEA;IACI,iBAAiB;IACjB,gCAAgC;IAChC,iBAAiB;AACrB;;AAEA;IACI,aAAa;IACb,eAAe;IACf,QAAQ;IACR,eAAe;AACnB;;AAEA;IACI,iBAAiB;IACjB,gBAAgB;IAChB,gBAAgB;IAChB,kBAAkB;IAClB,qCAAqC;IACrC,+BAA+B;AACnC;;AAEA;IACI,mBAAmB;AACvB;;AAEA;IACI,cAAc;IACd,eAAe;IACf,qBAAqB;IACrB,yBAAyB;IACzB,gCAAgC;IAChC,gBAAgB;IAChB,mBAAmB;AACvB;;AAEA;IACI,aAAa;IACb,gDAAgD;IAChD,SAAS;AACb;;AAEA;IACI,sBAAsB,qCAAqC,EAAE;AACjE;;AAEA;IACI,aAAa;IACb,sBAAsB;IACtB,uBAAuB;IACvB,QAAQ;IACR,kBAAkB;IAClB,mBAAmB;IACnB,2CAA2C;IAC3C,qCAAqC;IACrC,WAAW;IACX,gBAAgB;IAChB,oBAAoB;IACpB,eAAe;IACf,wDAAwD;AAC5D;;AAEA;IACI,sCAAsC;AAC1C;;AAEA;IACI,qBAAqB;IACrB,qCAAqC;AACzC;;AAEA;IACI,cAAc;AAClB;;AAEA;IACI,gBAAgB;IAChB,eAAe;AACnB;;AAEA;IACI,iBAAiB;IACjB,gCAAgC;AACpC;;AAEA;IACI,gBAAgB;AACpB;;AAEA;IACI,aAAa;IACb,mBAAmB;IACnB,uCAAuC;IACvC,WAAW;IACX,kBAAkB;AACtB;;AAEA;IACI,aAAa;IACb,SAAS;IACT,mBAAmB;AACvB;;AAEA;IACI,mBAAmB;IACnB,2CAA2C;IAC3C,qCAAqC;IACrC,kBAAkB;AACtB;;AAEA;IACI,eAAe;IACf,gBAAgB;IAChB,kBAAkB;AACtB;;AAEA;IACI,gCAAgC;IAChC,iBAAiB;IACjB,iBAAiB;AACrB;;AAEA;IACI,uCAAuC;IACvC,qCAAqC;AACzC;;AAEA;IACI,aAAa;IACb,eAAe;IACf,QAAQ;IACR,gBAAgB;AACpB;;AAEA;IACI,qCAAqC;IACrC,uCAAuC;IACvC,WAAW;IACX,eAAe;IACf,iBAAiB;AACrB;;AAEA;IACI,2BAA2B;IAC3B,WAAW;AACf;;AAEA;IACI,aAAa;IACb,eAAe;IACf,SAAS;IACT,gBAAgB;IAChB,iBAAiB;IACjB,gBAAgB;AACpB;;AAEA;IACI,oBAAoB;IACpB,mBAAmB;IACnB,QAAQ;AACZ;;AAEA;IACI,8CAA8C;IAC9C,iBAAiB;AACrB;;AAEA;IACI,mBAAmB;AACvB;;AAEA;IACI,aAAa;IACb,eAAe;IACf,SAAS;AACb;;AAEA;IACI,cAAc;IACd,gBAAgB;IAChB,eAAe;IACf,gBAAgB;AACpB;;AAEA;IACI,aAAa;IACb,8BAA8B;IAC9B,mBAAmB;IACnB,SAAS;IACT,gBAAgB;IAChB,iBAAiB;IACjB,8CAA8C;AAClD;;;AAGA;;+EAE+E;;AAE/E;IACI,OAAO,UAAU,EAAE,2BAA2B,EAAE;IAChD,KAAK,UAAU,EAAE,eAAe,EAAE;AACtC;;AAEA;IACI,OAAO,UAAU,EAAE,sCAAsC,EAAE;IAC3D,KAAK,UAAU,EAAE,eAAe,EAAE;AACtC;;AAEA;IACI,OAAO,UAAU,EAAE;IACnB,KAAK,UAAU,EAAE;AACrB;;AAEA;IACI,OAAO,UAAU,EAAE,2BAA2B,EAAE;IAChD,KAAK,UAAU,EAAE,eAAe,EAAE;AACtC;;AAEA;IACI,WAAW,mCAAmC,EAAE;IAChD,MAAM,6CAA6C,EAAE;IACrD,MAAM,6CAA6C,EAAE;AACzD;;AAEA;IACI,WAAW,wBAAwB,EAAE;IACrC,MAAM,2BAA2B,EAAE;AACvC;;AAEA;IACI,OAAO,6BAA6B,EAAE;IACtC,KAAK,mCAAmC,EAAE;AAC9C;;AAEA;IACI,KAAK,qBAAqB,EAAE,UAAU,EAAE;IACxC,OAAO,qBAAqB,EAAE,UAAU,EAAE;AAC9C;;AAEA;IACI,KAAK,oBAAoB,EAAE,qBAAqB,EAAE;IAClD,MAAM,oBAAoB,EAAE,qBAAqB,EAAE;IACnD,MAAM,wBAAwB,EAAE;IAChC,OAAO,oBAAoB,EAAE,wBAAwB,EAAE;AAC3D;;AAEA;IACI,OAAO,sBAAsB,EAAE;IAC/B,KAAK,oBAAoB,EAAE;AAC/B;;AAEA;IACI,WAAW,wBAAwB,EAAE;IACrC,MAAM,2BAA2B,EAAE;AACvC;;AAEA;IACI,KAAK,qBAAqB,EAAE,YAAY,EAAE;IAC1C,OAAO,qBAAqB,EAAE,UAAU,EAAE;AAC9C;;AAEA;IACI,KAAK,yBAAyB,EAAE;AACpC;;AAEA;IACI,KAAK,kBAAkB,EAAE;AAC7B;;AAEA;;+EAE+E;;AAE/E;IACI;;;QAGI,qCAAqC;QACrC,uCAAuC;QACvC,sCAAsC;IAC1C;;IAEA;QACI,UAAU;QACV,eAAe;IACnB;AACJ","sourcesContent":["/* ==========================================================================\n   LANDING PAGE DESIGN SYSTEM\n   Scoped to .landing-page so the internal CRM styles stay untouched.\n   ========================================================================== */\n\n/* Tokens live on :root so fixed elements rendered outside .landing-page\n   (navigation, modals) can use them as well. */\n:root {\n    /* Palette */\n    --lp-ink: #0b1224;\n    --lp-ink-2: #121b36;\n    --lp-ink-3: #1b2648;\n    --lp-text: #1e293b;\n    --lp-text-soft: #475569;\n    --lp-muted: #64748b;\n    --lp-line: rgba(15, 23, 42, 0.08);\n    --lp-brand: #3b82f6;\n    --lp-brand-2: #6366f1;\n    --lp-brand-soft: #eaf2ff;\n    --lp-accent: #22d3ee;\n    --lp-success: #10b981;\n    --lp-warning: #f59e0b;\n    --lp-danger: #ef4444;\n    --lp-grad: linear-gradient(135deg, #5ca9fb 0%, #6372ff 100%);\n    --lp-grad-text: linear-gradient(90deg, #7cc0ff 0%, #8b93ff 50%, #67e8f9 100%);\n    --lp-bg: #f6f8fc;\n    --lp-surface: #ffffff;\n\n    /* Shape & depth */\n    --lp-radius: 20px;\n    --lp-radius-sm: 12px;\n    --lp-shadow: 0 10px 30px -12px rgba(15, 23, 42, 0.18);\n    --lp-shadow-lg: 0 30px 60px -24px rgba(15, 23, 42, 0.32);\n    --lp-ring: 0 0 0 4px rgba(99, 114, 255, 0.18);\n\n    /* Type */\n    --lp-font: \"Inter\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", Roboto, sans-serif;\n    --lp-font-display: \"Manrope\", \"Inter\", -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif;\n}\n\n.landing-page {\n    font-family: var(--lp-font);\n    color: var(--lp-text);\n    font-size: 16px;\n    line-height: 1.65;\n    background: var(--lp-surface);\n    overflow-x: clip;\n}\n\n.landing-page *,\n.landing-page *::before,\n.landing-page *::after {\n    box-sizing: border-box;\n}\n\n.landing-page h1,\n.landing-page h2,\n.landing-page h3,\n.landing-page h4 {\n    font-family: var(--lp-font-display);\n    color: var(--lp-ink);\n    text-transform: none;\n    letter-spacing: -0.02em;\n    margin: 0;\n}\n\n.landing-page p {\n    font-size: inherit;\n    margin: 0;\n}\n\n.landing-page label {\n    float: none;\n    font-family: inherit;\n    font-size: inherit;\n    font-weight: inherit;\n}\n\n/* Font Awesome icons inherit the badge styling; never carry template sizing */\n.landing-page i[class*=\"fa\"] {\n    width: auto;\n    height: auto;\n    padding: 0;\n    margin: 0;\n    background: none;\n    border-radius: 0;\n    box-shadow: none;\n    font-size: inherit;\n    line-height: 1;\n}\n\n/* Headings keep their own spacing even against the generic reset above */\n.landing-page .lp-title { margin-bottom: 16px; }\n.landing-page .lp-about__sub { margin: 32px 0 16px; }\n.landing-page .lp-card__title { margin: 0; }\n\n.landing-page a {\n    color: var(--lp-brand);\n    text-decoration: none;\n    transition: color 0.2s ease;\n}\n\n.landing-page a:hover {\n    color: var(--lp-brand-2);\n}\n\n.landing-page img {\n    max-width: 100%;\n}\n\n.landing-page ::selection {\n    background: rgba(99, 114, 255, 0.25);\n}\n\n/* --------------------------------------------------------------------------\n   Layout primitives\n   -------------------------------------------------------------------------- */\n\n.lp-container {\n    width: 100%;\n    max-width: 1200px;\n    margin: 0 auto;\n    padding: 0 24px;\n}\n\n.lp-container--narrow {\n    max-width: 920px;\n}\n\n.lp-section {\n    position: relative;\n    padding: clamp(72px, 9vw, 120px) 0;\n}\n\n.lp-section--soft {\n    background: var(--lp-bg);\n}\n\n.lp-section--dark {\n    background:\n        radial-gradient(1200px 600px at 110% -10%, rgba(99, 114, 255, 0.35), transparent 60%),\n        radial-gradient(900px 500px at -10% 110%, rgba(34, 211, 238, 0.22), transparent 60%),\n        linear-gradient(160deg, var(--lp-ink) 0%, var(--lp-ink-2) 55%, var(--lp-ink-3) 100%);\n    color: rgba(255, 255, 255, 0.82);\n}\n\n.lp-section--dark h2,\n.lp-section--dark h3,\n.lp-section--dark h4 {\n    color: #fff;\n}\n\n.lp-section--dark a {\n    color: #9cc8ff;\n}\n\n.lp-section--dark a:hover {\n    color: #fff;\n}\n\n/* Subtle dotted texture used in light sections */\n.lp-section--dots::before {\n    content: \"\";\n    position: absolute;\n    inset: 0;\n    background-image: radial-gradient(rgba(15, 23, 42, 0.08) 1px, transparent 1px);\n    background-size: 22px 22px;\n    mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), transparent 55%);\n    -webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), transparent 55%);\n    pointer-events: none;\n}\n\n.lp-section > .lp-container {\n    position: relative;\n    z-index: 1;\n}\n\n.lp-grid {\n    display: grid;\n    gap: 24px;\n}\n\n.lp-grid--2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }\n.lp-grid--3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }\n.lp-grid--4 { grid-template-columns: repeat(4, minmax(0, 1fr)); }\n\n@media (max-width: 1024px) {\n    .lp-grid--4 { grid-template-columns: repeat(2, minmax(0, 1fr)); }\n    .lp-grid--3 { grid-template-columns: repeat(2, minmax(0, 1fr)); }\n}\n\n@media (max-width: 640px) {\n    .lp-grid--2,\n    .lp-grid--3,\n    .lp-grid--4 { grid-template-columns: minmax(0, 1fr); }\n}\n\n/* --------------------------------------------------------------------------\n   Section heading\n   -------------------------------------------------------------------------- */\n\n.lp-heading {\n    max-width: 780px;\n    margin-bottom: clamp(40px, 5vw, 64px);\n}\n\n.lp-heading--center {\n    margin-left: auto;\n    margin-right: auto;\n    text-align: center;\n}\n\n.lp-eyebrow {\n    display: inline-flex;\n    align-items: center;\n    gap: 8px;\n    padding: 6px 14px;\n    border-radius: 999px;\n    background: var(--lp-brand-soft);\n    color: var(--lp-brand-2);\n    font-size: 12px;\n    font-weight: 800;\n    letter-spacing: 0.14em;\n    text-transform: uppercase;\n    margin-bottom: 18px;\n}\n\n.lp-eyebrow::before {\n    content: \"\";\n    width: 7px;\n    height: 7px;\n    border-radius: 50%;\n    background: var(--lp-grad);\n}\n\n.lp-section--dark .lp-eyebrow {\n    background: rgba(255, 255, 255, 0.1);\n    color: #bcd9ff;\n    border: 1px solid rgba(255, 255, 255, 0.14);\n}\n\n.lp-title {\n    font-size: clamp(30px, 4.2vw, 48px);\n    font-weight: 800;\n    line-height: 1.1;\n    margin-bottom: 16px;\n}\n\n.lp-title em {\n    font-style: normal;\n    background: var(--lp-grad);\n    -webkit-background-clip: text;\n    background-clip: text;\n    color: transparent;\n}\n\n.lp-lead {\n    font-size: clamp(16px, 1.4vw, 19px);\n    line-height: 1.7;\n    color: var(--lp-text-soft);\n}\n\n.lp-section--dark .lp-lead {\n    color: rgba(255, 255, 255, 0.78);\n}\n\n/* --------------------------------------------------------------------------\n   Cards, badges, chips\n   -------------------------------------------------------------------------- */\n\n.lp-card {\n    position: relative;\n    background: var(--lp-surface);\n    border: 1px solid var(--lp-line);\n    border-radius: var(--lp-radius);\n    padding: 28px;\n    box-shadow: var(--lp-shadow);\n    transition: transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.35s ease, border-color 0.35s ease;\n    display: flex;\n    flex-direction: column;\n    gap: 12px;\n    overflow: hidden;\n}\n\n.lp-card:hover {\n    transform: translateY(-6px);\n    box-shadow: var(--lp-shadow-lg);\n    border-color: rgba(99, 114, 255, 0.35);\n}\n\n.lp-card--static:hover {\n    transform: none;\n    box-shadow: var(--lp-shadow);\n    border-color: var(--lp-line);\n}\n\n/* Gradient top bar revealed on hover */\n.lp-card--bar::before {\n    content: \"\";\n    position: absolute;\n    top: 0;\n    left: 0;\n    right: 0;\n    height: 4px;\n    background: var(--lp-grad);\n    transform: scaleX(0);\n    transform-origin: left;\n    transition: transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1);\n}\n\n.lp-card--bar:hover::before {\n    transform: scaleX(1);\n}\n\n.lp-card--glass {\n    background: rgba(255, 255, 255, 0.06);\n    border-color: rgba(255, 255, 255, 0.12);\n    box-shadow: none;\n    backdrop-filter: blur(10px);\n    -webkit-backdrop-filter: blur(10px);\n}\n\n.lp-card--glass:hover {\n    background: rgba(255, 255, 255, 0.09);\n    border-color: rgba(156, 200, 255, 0.45);\n    box-shadow: 0 30px 60px -30px rgba(92, 169, 251, 0.45);\n}\n\n.lp-card__title {\n    font-size: 20px;\n    font-weight: 800;\n    line-height: 1.3;\n}\n\n.lp-card__text {\n    color: var(--lp-text-soft);\n    font-size: 15.5px;\n    line-height: 1.7;\n}\n\n.lp-section--dark .lp-card__text {\n    color: rgba(255, 255, 255, 0.72);\n}\n\n.lp-icon-badge {\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    width: 56px;\n    height: 56px;\n    border-radius: 16px;\n    background: var(--lp-grad);\n    color: #fff;\n    font-size: 22px;\n    box-shadow: 0 12px 24px -10px rgba(99, 114, 255, 0.7);\n    flex-shrink: 0;\n    transition: transform 0.35s cubic-bezier(0.2, 0.7, 0.2, 1);\n}\n\n.lp-card:hover .lp-icon-badge {\n    transform: rotate(-6deg) scale(1.06);\n}\n\n.lp-icon-badge--soft {\n    background: var(--lp-brand-soft);\n    color: var(--lp-brand-2);\n    box-shadow: none;\n}\n\n.lp-chip {\n    display: inline-flex;\n    align-items: center;\n    gap: 8px;\n    padding: 8px 14px;\n    border-radius: 999px;\n    background: var(--lp-brand-soft);\n    border: 1px solid rgba(99, 114, 255, 0.18);\n    color: var(--lp-brand-2);\n    font-weight: 700;\n    font-size: 14px;\n    transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;\n}\n\na.lp-chip:hover {\n    background: var(--lp-brand-2);\n    color: #fff;\n    transform: translateY(-2px);\n}\n\n.lp-tag {\n    display: inline-block;\n    padding: 4px 10px;\n    border-radius: 8px;\n    background: var(--lp-brand-soft);\n    color: var(--lp-brand-2);\n    font-size: 12px;\n    font-weight: 800;\n    letter-spacing: 0.06em;\n    text-transform: uppercase;\n}\n\n/* --------------------------------------------------------------------------\n   Buttons\n   -------------------------------------------------------------------------- */\n\n.lp-btn {\n    position: relative;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    gap: 10px;\n    padding: 15px 28px;\n    border-radius: 999px;\n    font-family: var(--lp-font);\n    font-size: 16px;\n    font-weight: 800;\n    line-height: 1.2;\n    text-align: center;\n    text-decoration: none;\n    border: 1px solid transparent;\n    cursor: pointer;\n    transition: transform 0.25s cubic-bezier(0.2, 0.7, 0.2, 1), box-shadow 0.25s ease, background 0.25s ease, color 0.25s ease;\n    overflow: hidden;\n    max-width: 100%;\n}\n\n.lp-btn:focus-visible {\n    outline: none;\n    box-shadow: var(--lp-ring);\n}\n\n.lp-btn svg {\n    transition: transform 0.25s ease;\n}\n\n.lp-btn:hover svg {\n    transform: translateX(3px);\n}\n\n.lp-btn--primary {\n    background: var(--lp-grad);\n    color: #fff;\n    box-shadow: 0 14px 30px -10px rgba(99, 114, 255, 0.75);\n}\n\n.lp-btn--primary::after {\n    content: \"\";\n    position: absolute;\n    top: 0;\n    left: -80%;\n    width: 50%;\n    height: 100%;\n    background: linear-gradient(110deg, transparent 0%, rgba(255, 255, 255, 0.35) 50%, transparent 100%);\n    transform: skewX(-20deg);\n    transition: left 0.6s ease;\n}\n\n.lp-btn--primary:hover {\n    color: #fff;\n    transform: translateY(-2px);\n    box-shadow: 0 20px 40px -12px rgba(99, 114, 255, 0.85);\n}\n\n.lp-btn--primary:hover::after {\n    left: 130%;\n}\n\n.lp-btn--ghost {\n    background: rgba(255, 255, 255, 0.08);\n    color: #fff;\n    border-color: rgba(255, 255, 255, 0.25);\n    backdrop-filter: blur(8px);\n    -webkit-backdrop-filter: blur(8px);\n}\n\n.lp-btn--ghost:hover {\n    background: rgba(255, 255, 255, 0.16);\n    border-color: rgba(255, 255, 255, 0.5);\n    color: #fff;\n    transform: translateY(-2px);\n}\n\n.lp-btn--outline {\n    background: #fff;\n    color: var(--lp-brand-2);\n    border-color: rgba(99, 114, 255, 0.4);\n}\n\n.lp-btn--outline:hover {\n    background: var(--lp-brand-soft);\n    color: var(--lp-brand-2);\n    transform: translateY(-2px);\n}\n\n.lp-btn--lg {\n    padding: 19px 36px;\n    font-size: 18px;\n}\n\n.lp-btn--block {\n    width: 100%;\n}\n\n.lp-btn[disabled] {\n    opacity: 0.6;\n    cursor: wait;\n}\n\n/* --------------------------------------------------------------------------\n   Scroll reveal\n   -------------------------------------------------------------------------- */\n\n.landing-page [data-reveal] {\n    opacity: 0;\n    transform: translateY(26px);\n    transition: opacity 0.8s cubic-bezier(0.2, 0.7, 0.2, 1), transform 0.8s cubic-bezier(0.2, 0.7, 0.2, 1);\n    transition-delay: var(--reveal-delay, 0ms);\n    will-change: opacity, transform;\n}\n\n.landing-page [data-reveal=\"left\"] { transform: translateX(-32px); }\n.landing-page [data-reveal=\"right\"] { transform: translateX(32px); }\n.landing-page [data-reveal=\"scale\"] { transform: scale(0.94); }\n\n.landing-page [data-reveal].is-visible {\n    opacity: 1;\n    transform: none;\n}\n\n/* --------------------------------------------------------------------------\n   Navigation\n   -------------------------------------------------------------------------- */\n\n.lp-nav {\n    position: fixed;\n    top: 0;\n    left: 0;\n    width: 100%;\n    z-index: 2000;\n    height: 76px;\n    display: flex;\n    align-items: center;\n    background: transparent;\n    border-bottom: 1px solid transparent;\n    transition: background 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease, height 0.35s ease;\n}\n\n.lp-nav--solid {\n    height: 68px;\n    background: rgba(255, 255, 255, 0.86);\n    backdrop-filter: blur(16px) saturate(160%);\n    -webkit-backdrop-filter: blur(16px) saturate(160%);\n    border-bottom-color: rgba(15, 23, 42, 0.06);\n    box-shadow: 0 10px 30px -20px rgba(15, 23, 42, 0.35);\n}\n\n.lp-nav__inner {\n    width: 100%;\n    max-width: 1280px;\n    margin: 0 auto;\n    padding: 0 24px;\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    gap: 16px;\n    height: 100%;\n}\n\n.lp-nav__brand {\n    display: flex;\n    align-items: center;\n    gap: 10px;\n    cursor: pointer;\n    flex-shrink: 0;\n    color: #fff;\n    transition: color 0.35s ease;\n}\n\n.lp-nav--solid .lp-nav__brand {\n    color: var(--lp-ink);\n}\n\n.lp-nav__logo {\n    height: 38px;\n    width: 38px;\n    border-radius: 11px;\n    box-shadow: 0 6px 16px -6px rgba(15, 23, 42, 0.5);\n}\n\n.lp-nav__name {\n    font-family: var(--lp-font-display);\n    font-size: 20px;\n    font-weight: 800;\n    letter-spacing: -0.02em;\n}\n\n.lp-nav__links {\n    display: flex;\n    align-items: center;\n    list-style: none;\n    margin: 0;\n    padding: 0;\n    gap: 2px;\n}\n\n.lp-nav__link {\n    position: relative;\n    display: inline-block;\n    padding: 10px 8px;\n    font-size: 14px;\n    font-weight: 700;\n    color: rgba(255, 255, 255, 0.82);\n    border-radius: 10px;\n    white-space: nowrap;\n    transition: color 0.2s ease, background 0.2s ease;\n}\n\n.lp-nav__link::after {\n    content: \"\";\n    position: absolute;\n    left: 10px;\n    right: 10px;\n    bottom: 5px;\n    height: 2px;\n    border-radius: 2px;\n    background: var(--lp-grad);\n    transform: scaleX(0);\n    transform-origin: left;\n    transition: transform 0.25s cubic-bezier(0.2, 0.7, 0.2, 1);\n}\n\n.lp-nav__link:hover,\n.lp-nav__link.is-active {\n    color: #fff;\n}\n\n.lp-nav__link:hover::after,\n.lp-nav__link.is-active::after {\n    transform: scaleX(1);\n}\n\n.lp-nav--solid .lp-nav__link {\n    color: var(--lp-text-soft);\n}\n\n.lp-nav--solid .lp-nav__link:hover,\n.lp-nav--solid .lp-nav__link.is-active {\n    color: var(--lp-ink);\n}\n\n.lp-nav__cta {\n    padding: 11px 20px;\n    font-size: 14px;\n    margin-left: 10px;\n    white-space: nowrap;\n}\n\n/* Login: compact toggle in the bar, form opens in a popover */\n.lp-nav__login {\n    position: relative;\n    margin-left: 6px;\n}\n\n.lp-nav__login-toggle {\n    display: inline-flex;\n    align-items: center;\n    gap: 8px;\n    padding: 10px 14px;\n    border-radius: 999px;\n    border: 1px solid rgba(255, 255, 255, 0.22);\n    background: rgba(255, 255, 255, 0.08);\n    color: #fff;\n    font-family: inherit;\n    font-size: 14px;\n    font-weight: 700;\n    cursor: pointer;\n    transition: background 0.2s ease, color 0.35s ease, border-color 0.35s ease;\n}\n\n.lp-nav__login-toggle:hover,\n.lp-nav__login-toggle.is-open {\n    background: rgba(255, 255, 255, 0.18);\n}\n\n.lp-nav--solid .lp-nav__login-toggle {\n    border-color: rgba(15, 23, 42, 0.12);\n    background: #f8fafc;\n    color: var(--lp-ink);\n}\n\n.lp-nav--solid .lp-nav__login-toggle:hover,\n.lp-nav--solid .lp-nav__login-toggle.is-open {\n    background: var(--lp-brand-soft);\n    color: var(--lp-brand-2);\n}\n\n.lp-nav__popover {\n    position: absolute;\n    top: calc(100% + 12px);\n    right: 0;\n    width: 280px;\n    padding: 18px;\n    background: #fff;\n    border-radius: 18px;\n    border: 1px solid #f1f5f9;\n    box-shadow: 0 24px 50px -16px rgba(15, 23, 42, 0.35);\n    animation: lp-pop 0.2s cubic-bezier(0.2, 0.7, 0.2, 1);\n    z-index: 2100;\n}\n\n.lp-nav__popover-title {\n    font-family: var(--lp-font-display);\n    font-size: 15px;\n    font-weight: 800;\n    color: var(--lp-ink);\n    margin-bottom: 12px;\n}\n\n.lp-nav__popover .login-form-dynamic {\n    flex-direction: column;\n    gap: 10px;\n}\n\n.lp-nav__popover .login-input {\n    width: 100%;\n    padding: 12px 14px;\n    font-size: 14px;\n    border-radius: 12px;\n    transition: border-color 0.2s ease, box-shadow 0.2s ease;\n}\n\n.lp-nav__popover .login-input:focus {\n    outline: none;\n    border-color: var(--lp-brand);\n    box-shadow: var(--lp-ring);\n}\n\n.lp-nav__popover .login-submit-btn {\n    width: 100%;\n    padding: 12px;\n    font-size: 14px;\n    border-radius: 12px;\n    background: var(--lp-grad);\n}\n\n.lp-nav__popover .login-error-msg {\n    position: static;\n    font-size: 12px;\n}\n\n.lp-nav__user-btn {\n    display: inline-flex;\n    align-items: center;\n    gap: 8px;\n    padding: 10px 12px;\n    font-size: 14px;\n    font-weight: 700;\n    color: var(--lp-text-soft);\n    background: transparent;\n    border: none;\n    border-radius: 10px;\n    cursor: pointer;\n    font-family: inherit;\n    transition: background 0.2s ease, color 0.2s ease;\n}\n\n.lp-nav__user-btn:hover {\n    background: var(--lp-brand-soft);\n    color: var(--lp-brand-2);\n}\n\n.lp-nav__user-btn--accent {\n    color: var(--lp-brand);\n}\n\n.lp-nav__profile {\n    display: flex;\n    align-items: center;\n    gap: 10px;\n    padding: 6px 12px 6px 6px;\n    background: #f8fafc;\n    border-radius: 999px;\n    border: 1px solid #e2e8f0;\n    cursor: pointer;\n    user-select: none;\n    transition: box-shadow 0.2s ease;\n}\n\n.lp-nav__profile:hover {\n    box-shadow: var(--lp-ring);\n}\n\n.lp-nav__dropdown {\n    position: absolute;\n    top: calc(100% + 10px);\n    right: 0;\n    width: 250px;\n    background: #fff;\n    border-radius: 16px;\n    box-shadow: 0 20px 40px -12px rgba(15, 23, 42, 0.3);\n    border: 1px solid #f1f5f9;\n    padding: 8px;\n    z-index: 2100;\n    animation: lp-pop 0.2s cubic-bezier(0.2, 0.7, 0.2, 1);\n}\n\n.lp-nav__burger {\n    display: none;\n    align-items: center;\n    justify-content: center;\n    width: 46px;\n    height: 46px;\n    border-radius: 14px;\n    border: 1px solid rgba(255, 255, 255, 0.18);\n    background: rgba(255, 255, 255, 0.1);\n    color: #fff;\n    cursor: pointer;\n    transition: background 0.2s ease, color 0.35s ease;\n    -webkit-tap-highlight-color: transparent;\n}\n\n.lp-nav--solid .lp-nav__burger {\n    background: var(--lp-ink);\n    border-color: var(--lp-ink);\n    color: #fff;\n}\n\n@media (max-width: 1200px) {\n    .lp-nav__links { display: none; }\n    .lp-nav__burger { display: inline-flex; }\n}\n\n/* --------------------------------------------------------------------------\n   Mobile slide-in menu\n   -------------------------------------------------------------------------- */\n\n.lp-mobile__overlay {\n    position: fixed;\n    inset: 0;\n    background: rgba(11, 18, 36, 0.7);\n    backdrop-filter: blur(10px);\n    -webkit-backdrop-filter: blur(10px);\n    z-index: 2200;\n    opacity: 0;\n    visibility: hidden;\n    transition: opacity 0.35s ease, visibility 0.35s ease;\n    touch-action: none;\n}\n\n.lp-mobile__overlay.is-open {\n    opacity: 1;\n    visibility: visible;\n}\n\n.lp-mobile {\n    position: fixed;\n    top: 0;\n    right: 0;\n    width: 88%;\n    max-width: 360px;\n    height: 100dvh;\n    background: #fff;\n    z-index: 2250;\n    display: flex;\n    flex-direction: column;\n    transform: translateX(100%);\n    transition: transform 0.45s cubic-bezier(0.16, 1, 0.3, 1);\n    box-shadow: -20px 0 60px rgba(11, 18, 36, 0.3);\n    overflow-y: auto;\n    font-family: var(--lp-font);\n}\n\n.lp-mobile.is-open {\n    transform: translateX(0);\n}\n\n.lp-mobile__head {\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    padding: 16px 20px;\n    border-bottom: 1px solid var(--lp-line);\n}\n\n.lp-mobile__brand {\n    display: inline-flex;\n    align-items: center;\n    gap: 10px;\n    font-family: var(--lp-font-display);\n    font-weight: 800;\n    font-size: 18px;\n    color: var(--lp-ink);\n}\n\n.lp-mobile__brand img {\n    width: 34px;\n    height: 34px;\n    border-radius: 10px;\n}\n\n.lp-mobile__close {\n    width: 42px;\n    height: 42px;\n    border-radius: 12px;\n    border: 1px solid var(--lp-line);\n    background: #f8fafc;\n    color: var(--lp-ink);\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    cursor: pointer;\n}\n\n.lp-mobile__body {\n    padding: 20px;\n    display: flex;\n    flex-direction: column;\n    gap: 18px;\n    flex: 1;\n}\n\n.lp-mobile__label {\n    display: block;\n    font-size: 11px;\n    font-weight: 800;\n    letter-spacing: 0.18em;\n    text-transform: uppercase;\n    color: var(--lp-brand-2);\n}\n\n.lp-mobile__list {\n    list-style: none;\n    margin: 0;\n    padding: 0;\n    display: grid;\n    gap: 6px;\n}\n\n.lp-mobile__link {\n    display: flex;\n    align-items: center;\n    gap: 10px;\n    padding: 14px 16px;\n    border-radius: 14px;\n    font-size: 16px;\n    font-weight: 700;\n    color: var(--lp-ink);\n    background: #f8fafc;\n    border: 1px solid transparent;\n    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;\n}\n\n.lp-mobile__link .lp-mobile__arrow {\n    margin-left: auto;\n    opacity: 0.35;\n    transition: transform 0.2s ease, opacity 0.2s ease;\n}\n\n.lp-mobile__link:hover,\n.lp-mobile__link:focus-visible {\n    background: var(--lp-brand-soft);\n    color: var(--lp-brand-2);\n    border-color: rgba(99, 114, 255, 0.25);\n}\n\n.lp-mobile__link:hover .lp-mobile__arrow {\n    opacity: 1;\n    transform: translateX(3px);\n}\n\n.lp-mobile__link.is-active {\n    background: var(--lp-grad);\n    color: #fff;\n    box-shadow: 0 12px 24px -12px rgba(99, 114, 255, 0.8);\n}\n\n.lp-mobile__link.is-active .lp-mobile__arrow {\n    opacity: 1;\n}\n\n.lp-mobile__link--accent {\n    color: var(--lp-brand);\n}\n\n.lp-mobile__account {\n    margin-top: auto;\n}\n\n.lp-mobile__login {\n    padding: 18px;\n    border-radius: 18px;\n    background: var(--lp-bg);\n    border: 1px solid var(--lp-line);\n    display: grid;\n    gap: 12px;\n}\n\n.lp-mobile__login .login-form-dynamic {\n    flex-direction: column;\n}\n\n.lp-mobile__login .login-input {\n    width: 100%;\n    padding: 12px 14px;\n    font-size: 15px;\n    border-radius: 12px;\n}\n\n.lp-mobile__login .login-submit-btn {\n    padding: 12px;\n    border-radius: 12px;\n    background: var(--lp-ink);\n    font-size: 15px;\n}\n\n.lp-mobile__login .login-error-msg {\n    position: static;\n    font-size: 12px;\n}\n\n.lp-mobile__user {\n    padding: 20px;\n    border-radius: 18px;\n    background: var(--lp-bg);\n    border: 1px solid var(--lp-line);\n    text-align: center;\n}\n\n.lp-mobile__user--admin {\n    background: #fffbeb;\n    border-color: #fde68a;\n}\n\n.lp-mobile__user-role {\n    display: inline-flex;\n    align-items: center;\n    gap: 6px;\n    font-size: 11px;\n    font-weight: 800;\n    letter-spacing: 0.12em;\n    text-transform: uppercase;\n    color: var(--lp-muted);\n    margin-bottom: 8px;\n}\n\n.lp-mobile__user--admin .lp-mobile__user-role {\n    color: #d97706;\n}\n\n.lp-mobile__user-name {\n    font-weight: 800;\n    color: var(--lp-ink);\n    word-break: break-all;\n    margin: 0 0 16px;\n}\n\n.lp-mobile__logout {\n    width: 100%;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    gap: 8px;\n    padding: 13px;\n    border-radius: 12px;\n    border: none;\n    background: #e11d48;\n    color: #fff;\n    font-family: inherit;\n    font-weight: 800;\n    cursor: pointer;\n}\n\n/* Scroll progress bar */\n.lp-progress {\n    position: fixed;\n    top: 0;\n    left: 0;\n    height: 3px;\n    width: 100%;\n    z-index: 2001;\n    background: transparent;\n    pointer-events: none;\n}\n\n.lp-progress__bar {\n    height: 100%;\n    width: 100%;\n    background: var(--lp-grad);\n    transform-origin: left;\n    transform: scaleX(0);\n}\n\n/* Back to top */\n.lp-top {\n    position: fixed;\n    right: 22px;\n    bottom: 22px;\n    z-index: 1500;\n    width: 50px;\n    height: 50px;\n    border-radius: 50%;\n    border: none;\n    background: var(--lp-ink);\n    color: #fff;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    box-shadow: 0 16px 30px -12px rgba(15, 23, 42, 0.6);\n    cursor: pointer;\n    opacity: 0;\n    transform: translateY(16px) scale(0.9);\n    pointer-events: none;\n    transition: opacity 0.3s ease, transform 0.3s cubic-bezier(0.2, 0.7, 0.2, 1), background 0.2s ease;\n}\n\n.lp-top.is-visible {\n    opacity: 1;\n    transform: none;\n    pointer-events: auto;\n}\n\n.lp-top:hover {\n    background: var(--lp-brand-2);\n}\n\n/* --------------------------------------------------------------------------\n   Hero\n   -------------------------------------------------------------------------- */\n\n.lp-hero {\n    position: relative;\n    min-height: 100dvh;\n    display: flex;\n    align-items: center;\n    padding: 140px 0 130px;\n    color: #fff;\n    background:\n        radial-gradient(900px 500px at 15% 10%, rgba(99, 114, 255, 0.35), transparent 60%),\n        radial-gradient(800px 500px at 90% 85%, rgba(34, 211, 238, 0.22), transparent 60%),\n        linear-gradient(160deg, #0b1224 0%, #0f1a36 50%, #141f45 100%);\n    overflow: hidden;\n    isolation: isolate;\n}\n\n/* Grid pattern */\n.lp-hero__grid {\n    position: absolute;\n    inset: 0;\n    background-image:\n        linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),\n        linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);\n    background-size: 64px 64px;\n    mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.9), transparent 75%);\n    -webkit-mask-image: radial-gradient(ellipse at center, rgba(0, 0, 0, 0.9), transparent 75%);\n    z-index: -1;\n    animation: lp-grid-drift 40s linear infinite;\n}\n\n/* Aurora blobs */\n.lp-hero__blob {\n    position: absolute;\n    border-radius: 50%;\n    filter: blur(70px);\n    opacity: 0.55;\n    z-index: -1;\n    animation: lp-float 18s ease-in-out infinite;\n}\n\n.lp-hero__blob--1 {\n    width: 520px;\n    height: 520px;\n    top: -140px;\n    left: -120px;\n    background: #5ca9fb;\n}\n\n.lp-hero__blob--2 {\n    width: 460px;\n    height: 460px;\n    bottom: -160px;\n    right: -80px;\n    background: #6372ff;\n    animation-delay: -6s;\n}\n\n.lp-hero__blob--3 {\n    width: 320px;\n    height: 320px;\n    top: 40%;\n    left: 55%;\n    background: #22d3ee;\n    opacity: 0.25;\n    animation-delay: -12s;\n}\n\n.lp-hero__inner {\n    display: grid;\n    grid-template-columns: minmax(0, 1.15fr) minmax(0, 0.85fr);\n    gap: 48px;\n    align-items: center;\n}\n\n.lp-hero__eyebrow {\n    display: inline-flex;\n    align-items: center;\n    gap: 10px;\n    padding: 8px 16px 8px 10px;\n    border-radius: 999px;\n    background: rgba(255, 255, 255, 0.08);\n    border: 1px solid rgba(255, 255, 255, 0.16);\n    color: #cfe3ff;\n    font-size: 13px;\n    font-weight: 700;\n    letter-spacing: 0.04em;\n    margin-bottom: 26px;\n    backdrop-filter: blur(8px);\n    -webkit-backdrop-filter: blur(8px);\n}\n\n.lp-hero__pulse {\n    position: relative;\n    width: 10px;\n    height: 10px;\n    border-radius: 50%;\n    background: var(--lp-accent);\n    flex-shrink: 0;\n}\n\n.lp-hero__pulse::after {\n    content: \"\";\n    position: absolute;\n    inset: -4px;\n    border-radius: 50%;\n    border: 2px solid var(--lp-accent);\n    animation: lp-pulse 1.8s ease-out infinite;\n}\n\n.landing-page .lp-hero__title {\n    font-size: clamp(34px, 4.4vw, 58px);\n    font-weight: 800;\n    line-height: 1.08;\n    letter-spacing: -0.03em;\n    color: #fff;\n    margin-bottom: 24px;\n    overflow-wrap: anywhere;\n}\n\n.lp-hero__title em {\n    font-style: normal;\n    background: var(--lp-grad-text);\n    -webkit-background-clip: text;\n    background-clip: text;\n    color: transparent;\n}\n\n.lp-hero__text {\n    font-size: clamp(17px, 1.5vw, 20px);\n    line-height: 1.7;\n    color: rgba(255, 255, 255, 0.8);\n    max-width: 620px;\n    margin-bottom: 30px;\n}\n\n.lp-hero__benefits {\n    display: flex;\n    flex-wrap: wrap;\n    gap: 10px;\n    list-style: none;\n    padding: 0;\n    margin: 0 0 36px;\n}\n\n.lp-hero__benefit {\n    display: inline-flex;\n    align-items: center;\n    gap: 8px;\n    padding: 9px 14px;\n    border-radius: 999px;\n    background: rgba(255, 255, 255, 0.08);\n    border: 1px solid rgba(255, 255, 255, 0.16);\n    color: #fff;\n    font-size: 14px;\n    font-weight: 600;\n    backdrop-filter: blur(8px);\n    -webkit-backdrop-filter: blur(8px);\n}\n\n.lp-hero__benefit svg {\n    color: var(--lp-accent);\n    flex-shrink: 0;\n}\n\n.lp-hero__actions {\n    display: flex;\n    flex-wrap: wrap;\n    gap: 14px;\n}\n\n.lp-hero__visual {\n    position: relative;\n    display: flex;\n    justify-content: center;\n    animation: lp-rise 1.1s cubic-bezier(0.2, 0.7, 0.2, 1) 0.3s both;\n}\n\n.lp-hero__visual svg {\n    width: 100%;\n    max-width: 520px;\n    height: auto;\n    filter: drop-shadow(0 30px 50px rgba(0, 0, 0, 0.45));\n}\n\n.lp-hero__scroll {\n    position: absolute;\n    left: 50%;\n    bottom: 26px;\n    transform: translateX(-50%);\n    display: flex;\n    flex-direction: column;\n    align-items: center;\n    gap: 6px;\n    color: rgba(255, 255, 255, 0.55);\n    font-size: 11px;\n    letter-spacing: 0.2em;\n    text-transform: uppercase;\n    text-decoration: none;\n}\n\n.lp-hero__scroll:hover {\n    color: #fff;\n}\n\n.lp-hero__scroll span {\n    width: 1px;\n    height: 44px;\n    background: linear-gradient(to bottom, rgba(255, 255, 255, 0.6), transparent);\n    animation: lp-scroll-line 2s ease-in-out infinite;\n}\n\n/* Hero stagger animations on initial load */\n.lp-hero__copy > * {\n    animation: lp-rise 0.9s cubic-bezier(0.2, 0.7, 0.2, 1) both;\n}\n\n.lp-hero__copy > :nth-child(1) { animation-delay: 0.05s; }\n.lp-hero__copy > :nth-child(2) { animation-delay: 0.15s; }\n.lp-hero__copy > :nth-child(3) { animation-delay: 0.25s; }\n.lp-hero__copy > :nth-child(4) { animation-delay: 0.35s; }\n.lp-hero__copy > :nth-child(5) { animation-delay: 0.45s; }\n\n/* Integration diagram animation */\n.lp-net__link {\n    stroke: rgba(255, 255, 255, 0.22);\n    stroke-width: 1.5;\n    fill: none;\n}\n\n.lp-net__flow {\n    stroke: url(#lp-net-grad);\n    stroke-width: 2.5;\n    fill: none;\n    stroke-dasharray: 14 180;\n    stroke-linecap: round;\n    animation: lp-dash 3.2s linear infinite;\n}\n\n.lp-net__node {\n    animation: lp-node-float 6s ease-in-out infinite;\n}\n\n.lp-net__node:nth-of-type(2) { animation-delay: -1s; }\n.lp-net__node:nth-of-type(3) { animation-delay: -2s; }\n.lp-net__node:nth-of-type(4) { animation-delay: -3s; }\n.lp-net__node:nth-of-type(5) { animation-delay: -4s; }\n.lp-net__node:nth-of-type(6) { animation-delay: -5s; }\n\n.lp-net__ring {\n    fill: none;\n    stroke: rgba(124, 192, 255, 0.5);\n    stroke-width: 1.5;\n    transform-origin: 260px 230px;\n    animation: lp-ring 4s ease-out infinite;\n}\n\n.lp-net__ring--2 { animation-delay: -2s; }\n\n.lp-net__label {\n    font-family: var(--lp-font);\n    font-size: 12px;\n    font-weight: 700;\n    fill: #fff;\n    text-anchor: middle;\n}\n\n.lp-net__sub {\n    font-family: var(--lp-font);\n    font-size: 9.5px;\n    font-weight: 500;\n    fill: rgba(255, 255, 255, 0.6);\n    text-anchor: middle;\n}\n\n@media (max-width: 1024px) {\n    .lp-hero__inner {\n        grid-template-columns: minmax(0, 1fr);\n        text-align: center;\n    }\n\n    .lp-hero__text {\n        margin-left: auto;\n        margin-right: auto;\n    }\n\n    .lp-hero__benefits,\n    .lp-hero__actions {\n        justify-content: center;\n    }\n\n    .lp-hero__visual {\n        order: -1;\n    }\n\n    .lp-hero__visual svg {\n        max-width: 360px;\n    }\n\n    .lp-hero {\n        padding-top: 120px;\n    }\n}\n\n@media (max-width: 640px) {\n    .lp-hero__visual {\n        display: none;\n    }\n\n    .lp-hero__actions .lp-btn {\n        width: 100%;\n    }\n\n    .lp-hero__benefit {\n        white-space: normal;\n        text-align: left;\n    }\n\n    .lp-hero__blob {\n        filter: blur(50px);\n    }\n}\n\n/* --------------------------------------------------------------------------\n   Features\n   -------------------------------------------------------------------------- */\n\n.lp-feature {\n    gap: 16px;\n}\n\n.lp-feature__index {\n    position: absolute;\n    top: 18px;\n    right: 22px;\n    font-family: var(--lp-font-display);\n    font-size: 44px;\n    font-weight: 800;\n    line-height: 1;\n    color: rgba(15, 23, 42, 0.05);\n    transition: color 0.3s ease;\n}\n\n.lp-feature:hover .lp-feature__index {\n    color: rgba(99, 114, 255, 0.16);\n}\n\n/* Browser-style frame for showcase images */\n.lp-frame {\n    margin: clamp(48px, 6vw, 72px) auto 0;\n    max-width: 880px;\n    border-radius: 18px;\n    background: #0f172a;\n    box-shadow: var(--lp-shadow-lg);\n    overflow: hidden;\n    border: 1px solid rgba(15, 23, 42, 0.12);\n}\n\n.lp-frame__bar {\n    display: flex;\n    align-items: center;\n    gap: 8px;\n    padding: 12px 16px;\n    background: #111a33;\n    border-bottom: 1px solid rgba(255, 255, 255, 0.06);\n}\n\n.lp-frame__dot {\n    width: 10px;\n    height: 10px;\n    border-radius: 50%;\n    background: #ff5f57;\n}\n\n.lp-frame__dot:nth-child(2) { background: #febc2e; }\n.lp-frame__dot:nth-child(3) { background: #28c840; }\n\n.lp-frame__url {\n    margin-left: 12px;\n    flex: 1;\n    height: 22px;\n    border-radius: 6px;\n    background: rgba(255, 255, 255, 0.06);\n    color: rgba(255, 255, 255, 0.5);\n    font-size: 11px;\n    display: flex;\n    align-items: center;\n    padding: 0 10px;\n    font-family: monospace;\n}\n\n.lp-frame img {\n    display: block;\n    width: 100%;\n    height: auto;\n    transition: transform 0.6s cubic-bezier(0.2, 0.7, 0.2, 1);\n}\n\n.lp-frame a:hover img {\n    transform: scale(1.02);\n}\n\n.lp-actions-center {\n    display: flex;\n    justify-content: center;\n    flex-wrap: wrap;\n    gap: 14px;\n    margin-top: 32px;\n}\n\n/* --------------------------------------------------------------------------\n   Form controls\n   -------------------------------------------------------------------------- */\n\n.lp-field {\n    display: block;\n    margin-bottom: 16px;\n}\n\n.lp-field__label {\n    display: block;\n    font-size: 13px;\n    font-weight: 700;\n    color: var(--lp-text-soft);\n    margin-bottom: 8px;\n    letter-spacing: 0.02em;\n}\n\n.lp-input,\n.lp-textarea {\n    width: 100%;\n    padding: 13px 16px;\n    border-radius: 12px;\n    border: 1px solid #dbe2ee;\n    background: #fff;\n    font-family: var(--lp-font);\n    font-size: 15px;\n    color: var(--lp-text);\n    transition: border-color 0.2s ease, box-shadow 0.2s ease;\n}\n\n.lp-textarea {\n    resize: vertical;\n    min-height: 160px;\n    line-height: 1.6;\n}\n\n.lp-input:focus,\n.lp-textarea:focus {\n    outline: none;\n    border-color: var(--lp-brand);\n    box-shadow: var(--lp-ring);\n}\n\n.lp-input--dark {\n    background: rgba(255, 255, 255, 0.05);\n    border-color: rgba(255, 255, 255, 0.16);\n    color: #fff;\n}\n\n.lp-input--dark:focus {\n    border-color: #7cc0ff;\n    box-shadow: 0 0 0 4px rgba(124, 192, 255, 0.2);\n}\n\n.lp-input--dark::placeholder {\n    color: rgba(255, 255, 255, 0.35);\n}\n\n/* --------------------------------------------------------------------------\n   About\n   -------------------------------------------------------------------------- */\n\n.lp-about {\n    display: grid;\n    grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);\n    gap: clamp(32px, 5vw, 72px);\n    align-items: center;\n}\n\n@media (max-width: 960px) {\n    .lp-about { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-about__media {\n    position: relative;\n    padding: 20px;\n}\n\n.lp-about__media::before {\n    content: \"\";\n    position: absolute;\n    inset: 0;\n    border-radius: 32px;\n    background: var(--lp-grad);\n    opacity: 0.12;\n    transform: rotate(-3deg);\n}\n\n.lp-about__media::after {\n    content: \"\";\n    position: absolute;\n    inset: 10px;\n    border-radius: 28px;\n    border: 1px dashed rgba(99, 114, 255, 0.35);\n    transform: rotate(2deg);\n}\n\n.lp-about__img {\n    position: relative;\n    z-index: 1;\n    display: block;\n    width: 100%;\n    border-radius: 24px;\n    box-shadow: var(--lp-shadow-lg);\n    background: #fff;\n}\n\n.lp-about__badge {\n    position: absolute;\n    z-index: 2;\n    display: flex;\n    align-items: center;\n    gap: 12px;\n    padding: 12px 16px;\n    background: #fff;\n    border-radius: 16px;\n    box-shadow: var(--lp-shadow-lg);\n    border: 1px solid var(--lp-line);\n    animation: lp-float-sm 7s ease-in-out infinite;\n}\n\n.lp-about__badge--1 { bottom: 34px; left: -12px; }\n.lp-about__badge--2 { top: 30px; right: -12px; animation-delay: -3s; }\n\n.lp-about__badge strong {\n    display: block;\n    font-family: var(--lp-font-display);\n    font-size: 16px;\n    color: var(--lp-ink);\n    line-height: 1.2;\n}\n\n.lp-about__badge span {\n    font-size: 12px;\n    color: var(--lp-muted);\n}\n\n@media (max-width: 560px) {\n    .lp-about__badge { position: static; margin-top: 12px; animation: none; }\n}\n\n.lp-about__text p {\n    font-size: 17px;\n    line-height: 1.75;\n    color: var(--lp-text-soft);\n}\n\n.lp-about__sub {\n    font-size: 22px;\n    font-weight: 800;\n    margin: 32px 0 16px;\n}\n\n.lp-checks {\n    display: grid;\n    grid-template-columns: repeat(2, minmax(0, 1fr));\n    gap: 10px 22px;\n    list-style: none;\n    margin: 0;\n    padding: 0;\n}\n\n@media (max-width: 640px) {\n    .lp-checks { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-check {\n    display: flex;\n    gap: 12px;\n    align-items: flex-start;\n    font-size: 15.5px;\n    line-height: 1.6;\n    color: var(--lp-text);\n    padding: 10px 12px;\n    border-radius: 12px;\n    transition: background 0.2s ease;\n}\n\n.lp-check:hover {\n    background: var(--lp-brand-soft);\n}\n\n.lp-check__icon {\n    width: 24px;\n    height: 24px;\n    border-radius: 8px;\n    background: var(--lp-grad);\n    color: #fff;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    flex-shrink: 0;\n    margin-top: 2px;\n}\n\n/* --------------------------------------------------------------------------\n   Services\n   -------------------------------------------------------------------------- */\n\n.lp-service {\n    gap: 14px;\n}\n\n.lp-service__icon {\n    width: 58px;\n    height: 58px;\n    border-radius: 16px;\n    background: rgba(255, 255, 255, 0.08);\n    border: 1px solid rgba(255, 255, 255, 0.14);\n    color: #9cc8ff;\n    font-size: 24px;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    transition: background 0.3s ease, color 0.3s ease, transform 0.3s ease;\n}\n\n.lp-service:hover .lp-service__icon {\n    background: var(--lp-grad);\n    color: #fff;\n    transform: rotate(-6deg) scale(1.06);\n}\n\n.lp-split {\n    display: grid;\n    grid-template-columns: repeat(2, minmax(0, 1fr));\n    gap: 32px;\n    align-items: center;\n    margin-top: clamp(48px, 6vw, 80px);\n    padding: clamp(28px, 4vw, 48px);\n    border-radius: 28px;\n    background: rgba(255, 255, 255, 0.04);\n    border: 1px solid rgba(255, 255, 255, 0.1);\n}\n\n@media (max-width: 900px) {\n    .lp-split { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-split__full {\n    grid-column: 1 / -1;\n}\n\n.lp-split h3 {\n    font-size: clamp(22px, 2.4vw, 28px);\n    font-weight: 800;\n    margin-bottom: 14px;\n}\n\n.lp-split h4 {\n    font-size: 19px;\n    font-weight: 800;\n    color: #9cc8ff;\n    margin-bottom: 12px;\n}\n\n.lp-split p {\n    color: rgba(255, 255, 255, 0.74);\n    line-height: 1.75;\n    font-size: 16px;\n}\n\n.lp-zoom {\n    position: relative;\n    display: block;\n    border-radius: 16px;\n    overflow: hidden;\n    border: 1px solid rgba(255, 255, 255, 0.12);\n    box-shadow: 0 30px 60px -24px rgba(0, 0, 0, 0.7);\n    cursor: zoom-in;\n    background: none;\n    padding: 0;\n    width: 100%;\n}\n\n.lp-zoom img {\n    display: block;\n    width: 100%;\n    transition: transform 0.6s cubic-bezier(0.2, 0.7, 0.2, 1);\n}\n\n.lp-zoom:hover img {\n    transform: scale(1.04);\n}\n\n.lp-zoom::after {\n    content: \"\";\n    position: absolute;\n    inset: 0;\n    background: linear-gradient(to top, rgba(11, 18, 36, 0.55), transparent 50%);\n    opacity: 0;\n    transition: opacity 0.3s ease;\n}\n\n.lp-zoom:hover::after {\n    opacity: 1;\n}\n\n.lp-zoom__hint {\n    position: absolute;\n    right: 12px;\n    bottom: 12px;\n    z-index: 1;\n    display: inline-flex;\n    align-items: center;\n    gap: 6px;\n    padding: 6px 10px;\n    border-radius: 8px;\n    background: rgba(255, 255, 255, 0.14);\n    color: #fff;\n    font-size: 12px;\n    font-weight: 700;\n    backdrop-filter: blur(6px);\n    opacity: 0;\n    transform: translateY(6px);\n    transition: opacity 0.3s ease, transform 0.3s ease;\n}\n\n.lp-zoom:hover .lp-zoom__hint {\n    opacity: 1;\n    transform: none;\n}\n\n/* --------------------------------------------------------------------------\n   Why us\n   -------------------------------------------------------------------------- */\n\n.lp-why {\n    gap: 16px;\n    padding-top: 36px;\n}\n\n.lp-why__num {\n    position: absolute;\n    top: -6px;\n    right: 18px;\n    font-family: var(--lp-font-display);\n    font-size: 96px;\n    font-weight: 800;\n    line-height: 1;\n    color: transparent;\n    -webkit-text-stroke: 1px rgba(99, 114, 255, 0.22);\n    pointer-events: none;\n    transition: -webkit-text-stroke-color 0.3s ease;\n}\n\n.lp-why:hover .lp-why__num {\n    -webkit-text-stroke: 1px rgba(99, 114, 255, 0.55);\n}\n\n.lp-why__link {\n    margin-top: auto;\n    display: inline-flex;\n    align-items: center;\n    gap: 6px;\n    font-weight: 800;\n    color: var(--lp-brand-2);\n}\n\n.lp-why__link svg {\n    transition: transform 0.2s ease;\n}\n\n.lp-why__link:hover svg {\n    transform: translateX(4px);\n}\n\n/* --------------------------------------------------------------------------\n   Process steps & pricing\n   -------------------------------------------------------------------------- */\n\n.lp-steps {\n    position: relative;\n    display: grid;\n    grid-template-columns: repeat(4, minmax(0, 1fr));\n    gap: 24px;\n    margin-bottom: clamp(56px, 7vw, 88px);\n}\n\n.lp-steps::before {\n    content: \"\";\n    position: absolute;\n    left: 8%;\n    right: 8%;\n    top: 30px;\n    height: 2px;\n    background: linear-gradient(90deg, rgba(92, 169, 251, 0.2), rgba(99, 114, 255, 0.6), rgba(92, 169, 251, 0.2));\n}\n\n@media (max-width: 1024px) {\n    .lp-steps { grid-template-columns: repeat(2, minmax(0, 1fr)); }\n    .lp-steps::before { display: none; }\n}\n\n@media (max-width: 560px) {\n    .lp-steps { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-step {\n    position: relative;\n    text-align: left;\n}\n\n.lp-step__num {\n    position: relative;\n    z-index: 1;\n    width: 60px;\n    height: 60px;\n    border-radius: 50%;\n    background: var(--lp-grad);\n    color: #fff;\n    font-family: var(--lp-font-display);\n    font-size: 22px;\n    font-weight: 800;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    box-shadow: 0 0 0 8px #fff, 0 14px 28px -10px rgba(99, 114, 255, 0.8);\n    margin-bottom: 20px;\n}\n\n.lp-section--soft .lp-step__num {\n    box-shadow: 0 0 0 8px var(--lp-bg), 0 14px 28px -10px rgba(99, 114, 255, 0.8);\n}\n\n.lp-step h3 {\n    font-size: 19px;\n    font-weight: 800;\n    margin-bottom: 8px;\n}\n\n.lp-step p {\n    color: var(--lp-text-soft);\n    font-size: 15px;\n    line-height: 1.7;\n}\n\n.lp-price {\n    max-width: 920px;\n    margin: 0 auto;\n}\n\n.lp-price h3 {\n    text-align: center;\n    font-size: clamp(24px, 2.6vw, 30px);\n    font-weight: 800;\n    margin-bottom: 22px;\n}\n\n.lp-price__table {\n    background: #fff;\n    border-radius: var(--lp-radius);\n    border: 1px solid var(--lp-line);\n    box-shadow: var(--lp-shadow);\n    overflow: hidden;\n}\n\n.lp-price__row {\n    display: grid;\n    grid-template-columns: minmax(0, 1.3fr) minmax(0, 1fr);\n    gap: 16px;\n    align-items: center;\n    padding: 20px 26px;\n    border-bottom: 1px solid var(--lp-line);\n    font-size: 16px;\n    transition: background 0.2s ease;\n}\n\n.lp-price__row:last-child {\n    border-bottom: none;\n}\n\n.lp-price__row:hover {\n    background: var(--lp-bg);\n}\n\n.lp-price__row--highlight {\n    background: linear-gradient(90deg, rgba(92, 169, 251, 0.12), rgba(99, 114, 255, 0.08));\n}\n\n.lp-price__row--highlight:hover {\n    background: linear-gradient(90deg, rgba(92, 169, 251, 0.18), rgba(99, 114, 255, 0.12));\n}\n\n.lp-price__service {\n    display: flex;\n    align-items: center;\n    gap: 12px;\n    font-weight: 600;\n    color: var(--lp-text);\n}\n\n.lp-price__service svg {\n    color: var(--lp-brand-2);\n    flex-shrink: 0;\n}\n\n.lp-price__value {\n    font-family: var(--lp-font-display);\n    font-weight: 800;\n    color: var(--lp-ink);\n    text-align: right;\n}\n\n.lp-price__row--highlight .lp-price__value {\n    background: var(--lp-grad);\n    -webkit-background-clip: text;\n    background-clip: text;\n    color: transparent;\n    text-transform: uppercase;\n    letter-spacing: 0.06em;\n}\n\n@media (max-width: 560px) {\n    .lp-price__row { grid-template-columns: minmax(0, 1fr); gap: 6px; }\n    .lp-price__value { text-align: left; }\n}\n\n.lp-price__note {\n    margin-top: 16px;\n    font-size: 14px;\n    color: var(--lp-muted);\n    line-height: 1.7;\n}\n\n.lp-price__delivery {\n    margin-top: 40px;\n    padding: 26px 28px;\n    border-radius: var(--lp-radius);\n    background: var(--lp-bg);\n    border: 1px solid var(--lp-line);\n    display: grid;\n    grid-template-columns: auto minmax(0, 1fr);\n    gap: 18px;\n    align-items: flex-start;\n}\n\n@media (max-width: 560px) {\n    .lp-price__delivery { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-price__delivery h3 {\n    text-align: left;\n    font-size: 20px;\n    margin-bottom: 8px;\n}\n\n.lp-price__delivery p {\n    color: var(--lp-text-soft);\n    line-height: 1.75;\n    font-size: 15.5px;\n}\n\n/* --------------------------------------------------------------------------\n   Portfolio / gallery\n   -------------------------------------------------------------------------- */\n\n.lp-portfolio__intro {\n    display: grid;\n    grid-template-columns: repeat(2, minmax(0, 1fr));\n    gap: 20px 40px;\n    margin-bottom: clamp(40px, 5vw, 56px);\n}\n\n@media (max-width: 860px) {\n    .lp-portfolio__intro { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-portfolio__intro p {\n    font-size: 16.5px;\n    line-height: 1.75;\n    color: var(--lp-text-soft);\n    padding-left: 18px;\n    border-left: 3px solid rgba(99, 114, 255, 0.25);\n}\n\n.lp-portfolio__intro strong {\n    color: var(--lp-ink);\n}\n\n.lp-project {\n    gap: 14px;\n}\n\n.lp-project__mark {\n    width: 56px;\n    height: 56px;\n    border-radius: 16px;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    font-family: var(--lp-font-display);\n    font-size: 24px;\n    font-weight: 800;\n    color: #fff;\n    background: var(--lp-grad);\n    box-shadow: 0 12px 24px -10px rgba(99, 114, 255, 0.7);\n}\n\n.lp-project__label {\n    font-size: 13px;\n    color: var(--lp-muted);\n}\n\n.lp-project__link {\n    display: inline-flex;\n    align-items: center;\n    gap: 8px;\n    font-weight: 800;\n    font-size: 15px;\n    word-break: break-all;\n    margin-top: auto;\n}\n\n.lp-project__link svg {\n    flex-shrink: 0;\n    transition: transform 0.2s ease;\n}\n\n.lp-project__link:hover svg {\n    transform: translate(2px, -2px);\n}\n\n/* --------------------------------------------------------------------------\n   Testimonials\n   -------------------------------------------------------------------------- */\n\n.lp-quote {\n    gap: 18px;\n}\n\n.lp-quote__mark {\n    position: absolute;\n    top: 16px;\n    right: 22px;\n    color: rgba(99, 114, 255, 0.14);\n    transition: color 0.3s ease, transform 0.3s ease;\n}\n\n.lp-quote:hover .lp-quote__mark {\n    color: rgba(99, 114, 255, 0.35);\n    transform: rotate(-8deg);\n}\n\n.lp-quote__stars {\n    display: flex;\n    gap: 2px;\n    color: #f59e0b;\n}\n\n.lp-quote__text {\n    font-size: 16.5px;\n    line-height: 1.75;\n    color: var(--lp-text);\n    flex: 1;\n}\n\n.lp-quote__author {\n    display: flex;\n    align-items: center;\n    gap: 14px;\n    padding-top: 16px;\n    border-top: 1px solid var(--lp-line);\n}\n\n.lp-avatar {\n    width: 50px;\n    height: 50px;\n    border-radius: 50%;\n    object-fit: cover;\n    flex-shrink: 0;\n    background: var(--lp-grad);\n    color: #fff;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    font-family: var(--lp-font-display);\n    font-weight: 800;\n    font-size: 17px;\n    box-shadow: 0 0 0 3px #fff, 0 0 0 4px rgba(99, 114, 255, 0.35);\n}\n\n.lp-quote__name {\n    font-weight: 800;\n    color: var(--lp-ink);\n    font-size: 15px;\n}\n\n/* --------------------------------------------------------------------------\n   Team\n   -------------------------------------------------------------------------- */\n\n.lp-team-grid {\n    display: grid;\n    grid-template-columns: repeat(3, minmax(0, 1fr));\n    gap: 24px;\n    max-width: 1000px;\n    margin: 0 auto;\n}\n\n@media (max-width: 860px) {\n    .lp-team-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }\n}\n\n@media (max-width: 560px) {\n    .lp-team-grid { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-member {\n    align-items: center;\n    text-align: center;\n    padding: 32px 24px 28px;\n    gap: 6px;\n}\n\n.lp-member__ring {\n    position: relative;\n    width: 150px;\n    height: 150px;\n    border-radius: 50%;\n    padding: 5px;\n    background: var(--lp-grad);\n    margin-bottom: 14px;\n    transition: transform 0.4s cubic-bezier(0.2, 0.7, 0.2, 1);\n}\n\n.lp-member__ring::before {\n    content: \"\";\n    position: absolute;\n    inset: -8px;\n    border-radius: 50%;\n    border: 1px dashed rgba(99, 114, 255, 0.4);\n    animation: lp-spin 24s linear infinite;\n}\n\n.lp-member:hover .lp-member__ring {\n    transform: scale(1.05);\n}\n\n.lp-member__img {\n    width: 100%;\n    height: 100%;\n    border-radius: 50%;\n    object-fit: cover;\n    border: 4px solid #fff;\n    display: block;\n}\n\n.lp-member__name {\n    font-size: 20px;\n    font-weight: 800;\n}\n\n.lp-member__role {\n    color: var(--lp-brand-2);\n    font-weight: 700;\n    font-size: 14px;\n    letter-spacing: 0.02em;\n}\n\n/* --------------------------------------------------------------------------\n   Articles\n   -------------------------------------------------------------------------- */\n\n.lp-article {\n    gap: 12px;\n}\n\n.lp-article__meta {\n    display: flex;\n    align-items: center;\n    gap: 10px;\n    flex-wrap: wrap;\n    font-size: 13px;\n    color: var(--lp-muted);\n}\n\n.lp-article__title {\n    font-size: 19px;\n    line-height: 1.35;\n    font-weight: 800;\n}\n\n.lp-article__title a {\n    color: var(--lp-ink);\n}\n\n.lp-article__title a:hover {\n    color: var(--lp-brand-2);\n}\n\n.lp-article__read {\n    margin-top: auto;\n    display: inline-flex;\n    align-items: center;\n    gap: 6px;\n    font-weight: 800;\n    color: var(--lp-brand-2);\n}\n\n.lp-article__read svg {\n    transition: transform 0.2s ease;\n}\n\n.lp-article__read:hover svg {\n    transform: translateX(4px);\n}\n\n.lp-article__links {\n    display: flex;\n    flex-wrap: wrap;\n    justify-content: center;\n    gap: 12px;\n    margin-top: 36px;\n}\n\n/* --------------------------------------------------------------------------\n   Contact\n   -------------------------------------------------------------------------- */\n\n.lp-contact {\n    display: grid;\n    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);\n    gap: 24px;\n    align-items: stretch;\n}\n\n@media (max-width: 900px) {\n    .lp-contact { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-contact__card {\n    color: var(--lp-text);\n    gap: 0;\n}\n\n.lp-contact__card h3 {\n    font-size: 22px;\n    font-weight: 800;\n    margin-bottom: 20px;\n    color: var(--lp-ink);\n}\n\n.lp-contact__list {\n    list-style: none;\n    padding: 0;\n    margin: 0 0 20px;\n    display: grid;\n    gap: 14px;\n}\n\n.lp-contact__item {\n    display: flex;\n    gap: 14px;\n    align-items: flex-start;\n}\n\n.lp-contact__item .lp-icon-badge {\n    width: 42px;\n    height: 42px;\n    border-radius: 12px;\n    font-size: 16px;\n}\n\n.lp-contact__item strong {\n    display: block;\n    font-size: 12px;\n    letter-spacing: 0.08em;\n    text-transform: uppercase;\n    color: var(--lp-muted);\n    margin-bottom: 2px;\n}\n\n.lp-contact__item > div {\n    min-width: 0;\n}\n\n.lp-contact__item span,\n.lp-contact__item a {\n    font-size: 16px;\n    font-weight: 600;\n    color: var(--lp-text);\n    overflow-wrap: anywhere;\n}\n\n.lp-contact__item a:hover {\n    color: var(--lp-brand-2);\n}\n\n.lp-contact__chips {\n    display: flex;\n    flex-wrap: wrap;\n    gap: 10px;\n    margin-bottom: 20px;\n}\n\n.lp-contact__support {\n    margin-top: 22px;\n    padding-top: 20px;\n    border-top: 1px solid var(--lp-line);\n}\n\n.lp-contact__support h4 {\n    display: flex;\n    align-items: center;\n    gap: 8px;\n    font-size: 16px;\n    font-weight: 800;\n    margin-bottom: 12px;\n    color: var(--lp-ink);\n}\n\n.lp-contact__support dl {\n    display: grid;\n    grid-template-columns: auto minmax(0, 1fr);\n    gap: 6px 14px;\n    margin: 0;\n    font-size: 14.5px;\n}\n\n.lp-contact__support dt {\n    font-weight: 700;\n    color: var(--lp-text);\n}\n\n.lp-contact__support dd {\n    margin: 0;\n    color: var(--lp-text-soft);\n}\n\n.lp-contact__map {\n    padding: 0;\n    min-height: 420px;\n}\n\n.lp-contact__map iframe {\n    width: 100%;\n    height: 100%;\n    min-height: 420px;\n    border: 0;\n    display: block;\n    filter: saturate(0.9);\n}\n\n/* --------------------------------------------------------------------------\n   FAQ (native details accordion)\n   -------------------------------------------------------------------------- */\n\n.lp-faq {\n    max-width: 860px;\n    margin: 0 auto;\n    display: grid;\n    gap: 12px;\n}\n\n.lp-faq__item {\n    background: #fff;\n    border: 1px solid var(--lp-line);\n    border-radius: 16px;\n    box-shadow: var(--lp-shadow);\n    overflow: hidden;\n    transition: border-color 0.25s ease, box-shadow 0.25s ease;\n}\n\n.lp-faq__item[open] {\n    border-color: rgba(99, 114, 255, 0.4);\n    box-shadow: var(--lp-shadow-lg);\n}\n\n.lp-faq__q {\n    list-style: none;\n    display: flex;\n    align-items: center;\n    justify-content: space-between;\n    gap: 16px;\n    padding: 20px 24px;\n    cursor: pointer;\n    font-family: var(--lp-font-display);\n    font-size: 17px;\n    font-weight: 800;\n    color: var(--lp-ink);\n    user-select: none;\n    transition: color 0.2s ease;\n}\n\n.lp-faq__q::-webkit-details-marker {\n    display: none;\n}\n\n.lp-faq__q:hover {\n    color: var(--lp-brand-2);\n}\n\n.lp-faq__icon {\n    flex-shrink: 0;\n    width: 32px;\n    height: 32px;\n    border-radius: 10px;\n    background: var(--lp-brand-soft);\n    color: var(--lp-brand-2);\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    transition: transform 0.3s cubic-bezier(0.2, 0.7, 0.2, 1), background 0.3s ease, color 0.3s ease;\n}\n\n.lp-faq__item[open] .lp-faq__icon {\n    transform: rotate(180deg);\n    background: var(--lp-grad);\n    color: #fff;\n}\n\n.lp-faq__a {\n    padding: 0 24px 22px;\n    color: var(--lp-text-soft);\n    font-size: 15.5px;\n    line-height: 1.75;\n    animation: lp-fade-down 0.35s cubic-bezier(0.2, 0.7, 0.2, 1);\n}\n\n/* --------------------------------------------------------------------------\n   Footer\n   -------------------------------------------------------------------------- */\n\n.lp-footer {\n    background: var(--lp-ink);\n    color: rgba(255, 255, 255, 0.7);\n    padding: 64px 0 28px;\n    font-size: 14.5px;\n}\n\n.lp-footer__grid {\n    display: grid;\n    grid-template-columns: 1.4fr 1fr 1.1fr 1fr;\n    gap: 32px;\n    padding-bottom: 36px;\n    border-bottom: 1px solid rgba(255, 255, 255, 0.1);\n}\n\n@media (max-width: 960px) {\n    .lp-footer__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }\n}\n\n@media (max-width: 520px) {\n    .lp-footer__grid { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-footer__brand {\n    display: flex;\n    align-items: center;\n    gap: 10px;\n    color: #fff;\n    font-family: var(--lp-font-display);\n    font-size: 18px;\n    font-weight: 800;\n    margin-bottom: 14px;\n}\n\n.lp-footer__brand img {\n    width: 36px;\n    height: 36px;\n    border-radius: 10px;\n}\n\n.lp-footer h4 {\n    font-size: 12.5px;\n    letter-spacing: 0.12em;\n    text-transform: uppercase;\n    color: #fff;\n    margin-bottom: 14px;\n}\n\n.lp-footer p {\n    line-height: 1.8;\n}\n\n.lp-footer ul {\n    list-style: none;\n    margin: 0;\n    padding: 0;\n    display: grid;\n    gap: 8px;\n}\n\n.lp-footer a {\n    color: rgba(255, 255, 255, 0.72);\n    transition: color 0.2s ease, padding-left 0.2s ease;\n}\n\n.lp-footer a:hover {\n    color: #fff;\n}\n\n.lp-footer ul a:hover {\n    padding-left: 4px;\n}\n\n.lp-footer__bottom {\n    display: flex;\n    flex-wrap: wrap;\n    justify-content: space-between;\n    gap: 10px;\n    padding-top: 24px;\n    font-size: 13px;\n    color: rgba(255, 255, 255, 0.5);\n}\n\n/* --------------------------------------------------------------------------\n   Lightbox\n   -------------------------------------------------------------------------- */\n\n.lp-lightbox {\n    position: fixed;\n    inset: 0;\n    z-index: 9999;\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    padding: 24px;\n    background: rgba(5, 10, 24, 0.88);\n    backdrop-filter: blur(10px);\n    -webkit-backdrop-filter: blur(10px);\n    cursor: zoom-out;\n    animation: lp-fade 0.25s ease;\n}\n\n.lp-lightbox img {\n    max-width: 92vw;\n    max-height: 90vh;\n    border-radius: 16px;\n    box-shadow: 0 40px 80px rgba(0, 0, 0, 0.6);\n    object-fit: contain;\n    animation: lp-pop 0.35s cubic-bezier(0.2, 0.7, 0.2, 1);\n}\n\n.lp-lightbox__close {\n    position: absolute;\n    top: 20px;\n    right: 20px;\n    width: 46px;\n    height: 46px;\n    border-radius: 50%;\n    border: 1px solid rgba(255, 255, 255, 0.2);\n    background: rgba(255, 255, 255, 0.1);\n    color: #fff;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    cursor: pointer;\n}\n\n/* --------------------------------------------------------------------------\n   Products (automation ecosystem)\n   -------------------------------------------------------------------------- */\n\n.lp-product {\n    gap: 14px;\n}\n\n.lp-product--partner {\n    border-style: dashed;\n}\n\n.lp-product__head {\n    display: flex;\n    align-items: flex-start;\n    gap: 14px;\n}\n\n.lp-product__head .lp-icon-badge {\n    flex: 0 0 auto;\n}\n\n.lp-product__role {\n    color: var(--lp-muted);\n    font-size: 14px;\n    font-weight: 600;\n    margin-top: 4px;\n    line-height: 1.4;\n}\n\n.lp-product__tags {\n    display: flex;\n    flex-wrap: wrap;\n    gap: 8px;\n}\n\n.lp-tag--partner {\n    background: #fff4e5;\n    color: #c2410c;\n}\n\n.lp-product__list {\n    list-style: disc;\n    margin: 0;\n    padding-left: 18px;\n    display: grid;\n    gap: 6px;\n    color: var(--lp-text-soft);\n    font-size: 14.5px;\n    line-height: 1.6;\n}\n\n.lp-product__toggle {\n    align-self: flex-start;\n    display: inline-flex;\n    align-items: center;\n    gap: 6px;\n    padding: 0;\n    border: none;\n    background: none;\n    color: var(--lp-brand-2);\n    font-family: inherit;\n    font-size: 14px;\n    font-weight: 800;\n    cursor: pointer;\n}\n\n.lp-product__toggle svg {\n    transition: transform 0.2s ease;\n}\n\n.lp-product__toggle.is-open svg {\n    transform: rotate(180deg);\n}\n\n.lp-product__usecase {\n    background: var(--lp-bg);\n    border: 1px solid var(--lp-line);\n    border-radius: var(--lp-radius-sm);\n    padding: 16px;\n    animation: lp-fade 0.3s ease;\n}\n\n.lp-product__usecase-dept {\n    display: block;\n    font-size: 11.5px;\n    letter-spacing: 0.1em;\n    text-transform: uppercase;\n    font-weight: 800;\n    color: var(--lp-muted);\n    margin-bottom: 6px;\n}\n\n.lp-product__usecase h4 {\n    font-size: 16px;\n    font-weight: 800;\n    margin-bottom: 8px;\n}\n\n.lp-product__usecase p {\n    font-size: 14px;\n    color: var(--lp-text-soft);\n    line-height: 1.65;\n}\n\n.lp-product__metrics {\n    display: grid;\n    grid-template-columns: repeat(3, minmax(0, 1fr));\n    gap: 10px;\n    margin: 14px 0 0;\n}\n\n.lp-product__metrics div {\n    background: #fff;\n    border: 1px solid var(--lp-line);\n    border-radius: 10px;\n    padding: 10px 8px;\n    text-align: center;\n}\n\n.lp-product__metrics dt {\n    font-family: var(--lp-font-display);\n    font-weight: 800;\n    font-size: 14px;\n    color: var(--lp-ink);\n}\n\n.lp-product__metrics dd {\n    margin: 2px 0 0;\n    font-size: 11.5px;\n    color: var(--lp-muted);\n    line-height: 1.4;\n}\n\n@media (max-width: 480px) {\n    .lp-product__metrics { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-product__links {\n    margin-top: auto;\n    padding-top: 12px;\n    border-top: 1px solid var(--lp-line);\n    display: flex;\n    flex-direction: column;\n    gap: 6px;\n    font-size: 13.5px;\n    font-weight: 700;\n}\n\n.lp-product__links a {\n    display: inline-flex;\n    align-items: center;\n    gap: 6px;\n}\n\n.lp-product__flow {\n    margin-top: clamp(40px, 5vw, 64px);\n    background: var(--lp-surface);\n    border: 1px solid var(--lp-line);\n    border-radius: var(--lp-radius);\n    padding: clamp(20px, 3vw, 32px);\n    box-shadow: var(--lp-shadow);\n}\n\n.lp-product__flow ol {\n    margin: 14px 0 0;\n    padding-left: 20px;\n    display: grid;\n    gap: 8px;\n    color: var(--lp-text-soft);\n    font-size: 15px;\n    line-height: 1.6;\n}\n\n.lp-product__flow strong {\n    color: var(--lp-ink);\n}\n\n/* --------------------------------------------------------------------------\n   Inquiry wizard (deterministic inquiry assistant)\n   -------------------------------------------------------------------------- */\n\n.lp-wizard {\n    max-width: 1040px;\n    margin: 0 auto;\n    border-radius: 28px;\n    background: rgba(5, 10, 24, 0.55);\n    border: 1px solid rgba(255, 255, 255, 0.1);\n    padding: clamp(24px, 4vw, 44px);\n}\n\n.lp-wizard__steps {\n    list-style: none;\n    margin: 0 0 28px;\n    padding: 0;\n    display: flex;\n    flex-wrap: wrap;\n    gap: 10px;\n}\n\n.lp-wizard__step {\n    display: inline-flex;\n    align-items: center;\n    gap: 10px;\n    padding: 6px 14px 6px 6px;\n    border-radius: 999px;\n    border: 1px solid rgba(255, 255, 255, 0.12);\n    color: rgba(255, 255, 255, 0.55);\n    font-size: 13.5px;\n    font-weight: 700;\n}\n\n.lp-wizard__step-num {\n    width: 26px;\n    height: 26px;\n    border-radius: 50%;\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    background: rgba(255, 255, 255, 0.08);\n    font-size: 12px;\n    font-weight: 800;\n}\n\n.lp-wizard__step.is-active {\n    color: #fff;\n    border-color: rgba(124, 192, 255, 0.5);\n    background: rgba(124, 192, 255, 0.08);\n}\n\n.lp-wizard__step.is-active .lp-wizard__step-num {\n    background: var(--lp-grad);\n    color: #fff;\n}\n\n.lp-wizard__step.is-done {\n    color: rgba(255, 255, 255, 0.8);\n}\n\n.lp-wizard__step.is-done .lp-wizard__step-num {\n    background: var(--lp-success);\n    color: #fff;\n}\n\n.lp-wizard__panel h3 {\n    display: flex;\n    align-items: center;\n    gap: 10px;\n    font-size: clamp(20px, 2.2vw, 26px);\n    font-weight: 800;\n    margin-bottom: 8px;\n}\n\n.lp-wizard__hint {\n    color: rgba(255, 255, 255, 0.6);\n    font-size: 14.5px;\n    line-height: 1.6;\n    margin-bottom: 20px;\n}\n\n.lp-wizard__intents {\n    display: grid;\n    grid-template-columns: repeat(2, minmax(0, 1fr));\n    gap: 14px;\n}\n\n@media (max-width: 760px) {\n    .lp-wizard__intents { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-intent {\n    position: relative;\n    display: flex;\n    flex-direction: column;\n    align-items: flex-start;\n    gap: 6px;\n    padding: 18px 18px 18px 52px;\n    border-radius: 16px;\n    border: 1px solid rgba(255, 255, 255, 0.12);\n    background: rgba(255, 255, 255, 0.04);\n    color: #fff;\n    text-align: left;\n    font-family: inherit;\n    cursor: pointer;\n    transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;\n}\n\n.lp-intent:hover {\n    border-color: rgba(124, 192, 255, 0.5);\n    transform: translateY(-2px);\n}\n\n.lp-intent.is-checked {\n    border-color: #7cc0ff;\n    background: rgba(124, 192, 255, 0.12);\n    box-shadow: 0 0 0 4px rgba(124, 192, 255, 0.15);\n}\n\n.lp-intent__check {\n    position: absolute;\n    left: 18px;\n    top: 20px;\n    width: 22px;\n    height: 22px;\n    border-radius: 7px;\n    border: 1px solid rgba(255, 255, 255, 0.3);\n    display: inline-flex;\n    align-items: center;\n    justify-content: center;\n    color: transparent;\n    transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease;\n}\n\n.lp-intent.is-checked .lp-intent__check {\n    background: var(--lp-grad);\n    border-color: transparent;\n    color: #fff;\n}\n\n.lp-intent__title {\n    font-family: var(--lp-font-display);\n    font-weight: 800;\n    font-size: 16px;\n}\n\n.lp-intent__text {\n    font-size: 13.5px;\n    color: rgba(255, 255, 255, 0.65);\n    line-height: 1.55;\n}\n\n.lp-intent__examples {\n    display: flex;\n    flex-wrap: wrap;\n    gap: 6px;\n    margin-top: 4px;\n}\n\n.lp-intent__examples span {\n    font-size: 11.5px;\n    font-weight: 700;\n    padding: 3px 8px;\n    border-radius: 6px;\n    background: rgba(255, 255, 255, 0.08);\n    color: rgba(255, 255, 255, 0.7);\n}\n\n.lp-wizard__question {\n    margin-bottom: 22px;\n}\n\n.lp-wizard__label {\n    display: block;\n    font-size: 12px;\n    letter-spacing: 0.1em;\n    text-transform: uppercase;\n    color: rgba(255, 255, 255, 0.55);\n    font-weight: 800;\n    margin-bottom: 10px;\n}\n\n.lp-wizard__choices {\n    display: grid;\n    grid-template-columns: repeat(3, minmax(0, 1fr));\n    gap: 12px;\n}\n\n@media (max-width: 760px) {\n    .lp-wizard__choices { grid-template-columns: minmax(0, 1fr); }\n}\n\n.lp-choice {\n    display: flex;\n    flex-direction: column;\n    align-items: flex-start;\n    gap: 4px;\n    padding: 14px 16px;\n    border-radius: 14px;\n    border: 1px solid rgba(255, 255, 255, 0.12);\n    background: rgba(255, 255, 255, 0.04);\n    color: #fff;\n    text-align: left;\n    font-family: inherit;\n    cursor: pointer;\n    transition: border-color 0.2s ease, background 0.2s ease;\n}\n\n.lp-choice:hover {\n    border-color: rgba(124, 192, 255, 0.5);\n}\n\n.lp-choice.is-checked {\n    border-color: #7cc0ff;\n    background: rgba(124, 192, 255, 0.12);\n}\n\n.lp-choice__icon {\n    color: #9cc8ff;\n}\n\n.lp-choice__label {\n    font-weight: 800;\n    font-size: 15px;\n}\n\n.lp-choice__hint {\n    font-size: 12.5px;\n    color: rgba(255, 255, 255, 0.55);\n}\n\n.lp-wizard .lp-field {\n    margin-bottom: 0;\n}\n\n.lp-wizard textarea.lp-input--dark {\n    min-height: 0;\n    background: #0a1022;\n    border-color: rgba(255, 255, 255, 0.16);\n    color: #fff;\n    color-scheme: dark;\n}\n\n.lp-wizard__results {\n    display: grid;\n    gap: 14px;\n    margin-bottom: 28px;\n}\n\n.lp-reco {\n    border-radius: 16px;\n    border: 1px solid rgba(255, 255, 255, 0.12);\n    background: rgba(255, 255, 255, 0.04);\n    padding: 20px 22px;\n}\n\n.lp-reco h4 {\n    font-size: 17px;\n    font-weight: 800;\n    margin-bottom: 8px;\n}\n\n.lp-reco p {\n    color: rgba(255, 255, 255, 0.72);\n    font-size: 14.5px;\n    line-height: 1.65;\n}\n\n.lp-reco--ai {\n    border-color: rgba(103, 232, 249, 0.35);\n    background: rgba(103, 232, 249, 0.06);\n}\n\n.lp-reco__products {\n    display: flex;\n    flex-wrap: wrap;\n    gap: 8px;\n    margin-top: 12px;\n}\n\n.lp-chip--dark {\n    background: rgba(255, 255, 255, 0.08);\n    border-color: rgba(255, 255, 255, 0.14);\n    color: #fff;\n    font-size: 13px;\n    padding: 6px 12px;\n}\n\na.lp-chip--dark:hover {\n    background: var(--lp-brand);\n    color: #fff;\n}\n\n.lp-reco__links {\n    display: flex;\n    flex-wrap: wrap;\n    gap: 14px;\n    margin-top: 12px;\n    font-size: 13.5px;\n    font-weight: 700;\n}\n\n.lp-reco__links a {\n    display: inline-flex;\n    align-items: center;\n    gap: 6px;\n}\n\n.lp-wizard__summary {\n    border-top: 1px solid rgba(255, 255, 255, 0.1);\n    padding-top: 24px;\n}\n\n.lp-wizard__summary .lp-wizard__hint {\n    margin: 12px 0 16px;\n}\n\n.lp-wizard__actions {\n    display: flex;\n    flex-wrap: wrap;\n    gap: 12px;\n}\n\n.lp-wizard__error {\n    color: #ff9b9b;\n    font-weight: 700;\n    font-size: 14px;\n    margin-top: 16px;\n}\n\n.lp-wizard__nav {\n    display: flex;\n    justify-content: space-between;\n    align-items: center;\n    gap: 12px;\n    margin-top: 28px;\n    padding-top: 20px;\n    border-top: 1px solid rgba(255, 255, 255, 0.1);\n}\n\n\n/* --------------------------------------------------------------------------\n   Keyframes\n   -------------------------------------------------------------------------- */\n\n@keyframes lp-rise {\n    from { opacity: 0; transform: translateY(28px); }\n    to { opacity: 1; transform: none; }\n}\n\n@keyframes lp-pop {\n    from { opacity: 0; transform: translateY(8px) scale(0.98); }\n    to { opacity: 1; transform: none; }\n}\n\n@keyframes lp-fade {\n    from { opacity: 0; }\n    to { opacity: 1; }\n}\n\n@keyframes lp-fade-down {\n    from { opacity: 0; transform: translateY(-6px); }\n    to { opacity: 1; transform: none; }\n}\n\n@keyframes lp-float {\n    0%, 100% { transform: translate(0, 0) scale(1); }\n    33% { transform: translate(40px, -30px) scale(1.08); }\n    66% { transform: translate(-30px, 30px) scale(0.95); }\n}\n\n@keyframes lp-float-sm {\n    0%, 100% { transform: translateY(0); }\n    50% { transform: translateY(-8px); }\n}\n\n@keyframes lp-grid-drift {\n    from { background-position: 0 0, 0 0; }\n    to { background-position: 0 64px, 64px 0; }\n}\n\n@keyframes lp-pulse {\n    0% { transform: scale(0.6); opacity: 1; }\n    100% { transform: scale(2.2); opacity: 0; }\n}\n\n@keyframes lp-scroll-line {\n    0% { transform: scaleY(0); transform-origin: top; }\n    50% { transform: scaleY(1); transform-origin: top; }\n    51% { transform-origin: bottom; }\n    100% { transform: scaleY(0); transform-origin: bottom; }\n}\n\n@keyframes lp-dash {\n    from { stroke-dashoffset: 194; }\n    to { stroke-dashoffset: 0; }\n}\n\n@keyframes lp-node-float {\n    0%, 100% { transform: translateY(0); }\n    50% { transform: translateY(-6px); }\n}\n\n@keyframes lp-ring {\n    0% { transform: scale(0.4); opacity: 0.9; }\n    100% { transform: scale(2.4); opacity: 0; }\n}\n\n@keyframes lp-spin {\n    to { transform: rotate(360deg); }\n}\n\n@keyframes lp-blink {\n    to { visibility: hidden; }\n}\n\n/* --------------------------------------------------------------------------\n   Reduced motion\n   -------------------------------------------------------------------------- */\n\n@media (prefers-reduced-motion: reduce) {\n    .landing-page *,\n    .landing-page *::before,\n    .landing-page *::after {\n        animation-duration: 0.01ms !important;\n        animation-iteration-count: 1 !important;\n        transition-duration: 0.01ms !important;\n    }\n\n    .landing-page [data-reveal] {\n        opacity: 1;\n        transform: none;\n    }\n}\n"],"sourceRoot":""}]);
// Exports
___CSS_LOADER_EXPORT___.locals = {};
export default ___CSS_LOADER_EXPORT___;
